/**
 * Procedural Background Renderers
 *
 * Canvas-drawn animated backgrounds selectable from the asset library.
 * URL convention: "procedural:<rendererName>" e.g. "procedural:skyClouds"
 *
 * Each renderer signature: (ctx, width, height, time, state, direction) => void
 *   - ctx:       CanvasRenderingContext2D
 *   - width:     canvas CSS width (already DPR-scaled by caller)
 *   - height:    canvas CSS height
 *   - time:      elapsed seconds (monotonic)
 *   - state:     mutable object for caching (cloud arrays, star positions, etc.)
 *   - direction: 'right' | 'left' | 'down' | 'up' (default 'right')
 */

// ─── Helpers ──────────────────────────────────────────────

export function isProceduralBackground(url) {
  return typeof url === 'string' && url.startsWith('procedural:');
}

export function getProceduralKey(url) {
  return url?.replace('procedural:', '') || '';
}

export function getProceduralRenderer(url) {
  const key = getProceduralKey(url);
  return renderers[key] || null;
}

/** Direct key lookup — avoids needing to re-add the 'procedural:' prefix */
export function getRendererByKey(key) {
  return renderers[key] || null;
}

/**
 * All available procedural backgrounds (for asset library registration).
 *
 * Some `key` values look unrelated to their display `name` (e.g. `neonGrid`
 * shows as "Bokeh", `lavaFlow` as "Magma Flow", `sunsetGlow` as "Aurora
 * Borealis"). The renderer keys are stable identifiers used by Firestore
 * documents (`procedural:<key>` URLs on options.backgroundImage) and were
 * frozen early; the display names have been rebranded over time. Renaming
 * the keys would break every existing campaign — instead we keep both,
 * and ensure the same display name is used everywhere a user sees it
 * (assetLibrary.js, this list, functions/index.js BACKGROUND_ASSETS).
 */
export const proceduralBackgroundList = [
  { key: 'skyClouds',    name: 'Sky & Clouds' },
  { key: 'sunsetGlow',   name: 'Aurora Borealis' },
  { key: 'nightStars',   name: 'Deep Space' },
  { key: 'neonGrid',     name: 'Bokeh' },
  { key: 'oceanWaves',   name: 'Underwater' },
  { key: 'rollingHills', name: 'Rolling Hills' },
  { key: 'sunsetHorizon', name: 'Sunset Horizon' },
  { key: 'mountainRange', name: 'Mountain Range' },
  { key: 'snowfall',      name: 'Snowfall' },
  { key: 'lavaFlow',      name: 'Magma Flow' },
  { key: 'forestCanopy',  name: 'Forest Canopy' },
  // coralDepths, industrialSmog, crystalCavern, enchantedTwilight,
  // steampunkSkyline removed — too dark / too similar to existing entries.
  { key: 'candySky',           name: 'Candy Sky' },
  { key: 'bambooGrove',        name: 'Bamboo Grove' },
  { key: 'terracottaDesert',   name: 'Terracotta Desert' },
  { key: 'pixelRain',          name: 'Pixel Rain' },
  { key: 'goldenPalace',       name: 'Golden Palace' },
  { key: 'neonDistrict',       name: 'Neon District' },
  { key: 'rooftopSunset',      name: 'Rooftop Sunset' },
  { key: 'downtownNight',      name: 'Downtown Night' },
  { key: 'harbourFront',       name: 'Harbour Front' },
  { key: 'miamiVice',          name: 'Miami Vice' },
  // Top-down designs for vertical-scrolling games (Lane Dash).
  { key: 'softDrift',          name: 'Soft Drift' },
  { key: 'waterChannel',       name: 'Water Channel' },
  { key: 'tileFlow',           name: 'Tile Flow' },
  { key: 'skyCloudsTopDown',   name: 'Sky & Clouds' },
  { key: 'skyJourney',         name: 'Sky Journey' },
  { key: 'magmaRise',          name: 'Magma Rise' },
];

// ─── Direction helpers ────────────────────────────────────

function getDirSign(direction) {
  if (!direction) return 0; // null direction = no drift
  return (direction === 'left' || direction === 'up') ? -1 : 1;
}

// Triangle wave: returns 0→1→0 with period of 1 (pointed peaks)
function triangleWave(t) {
  const p = ((t % 1) + 1) % 1;
  return p < 0.5 ? 2 * p : 2 * (1 - p);
}

function isVertical(direction) {
  return direction === 'down' || direction === 'up';
}

function wrap(value, span) {
  return ((value % span) + span) % span;
}

// Cap effective area for particle/element density calculations.
// Prevents desktop viewports (1440×900 = 1.3M px²) from spawning 3-5x more
// particles than mobile (360×640 = 230k px²). 500k keeps desktop looking full
// while avoiding hundreds of unnecessary draw calls per frame.
const MAX_DENSITY_AREA = 500000;
function cappedArea(w, h) {
  return Math.min(w * h, MAX_DENSITY_AREA);
}

// Cache a vertical linear gradient on state to avoid recreating it every frame.
// Only rebuilds when canvas height changes.
function cachedVGradient(ctx, h, state, cacheKey, stops) {
  if (!state._gradCache) state._gradCache = {};
  const cached = state._gradCache[cacheKey];
  if (cached && cached._h === h) return cached.grad;
  const grad = ctx.createLinearGradient(0, 0, 0, h);
  for (const [offset, color] of stops) grad.addColorStop(offset, color);
  state._gradCache[cacheKey] = { grad, _h: h };
  return grad;
}

// ─── Cloud system (used by skyClouds) ─────────────────────
//
// Crisp vector-style clouds: overlapping solid-fill circles.
// Matches the original minigolf cloud look.

function generateClouds(w, h, count = 10, frontCount = 3) {
  const clouds = [];
  for (let i = 0; i < count; i++) {
    const sc = 0.5 + Math.random() * 1.5;
    const blobCount = 3 + Math.floor(Math.random() * 4);
    const blobs = [];
    for (let j = 0; j < blobCount; j++) {
      blobs.push({
        ox: (Math.random() - 0.3) * 50 * sc,
        oy: (Math.random() - 0.5) * 20 * sc,
        r: (15 + Math.random() * 25) * sc,
      });
    }
    clouds.push({
      x: Math.random() * w * 1.4 - w * 0.2,
      y: Math.random() * h,
      scale: sc,
      opacity: 0.35 + Math.random() * 0.4,
      blobs,
      speed: 0.08 + Math.random() * 0.12,
      layer: i < frontCount ? 'front' : 'back',
    });
  }
  clouds._w = w;
  clouds._h = h;
  return clouds;
}

/**
 * Draw clouds as crisp solid-fill circles.
 * @param {string} color       - fill color (e.g. '#fff' or 'rgba(40,10,50,0.3)')
 * @param {number} radiusScale - multiply blob radii (1.3 for foreground wisps)
 * @param {number} opacityScale - multiply cloud opacity (0.2 for foreground wisps)
 */
function drawClouds(ctx, w, h, time, clouds, color = '#fff', speedScale = 1, direction, layerFilter = null, radiusScale = 1, opacityScale = 1) {
  const vert = isVertical(direction);
  const sign = getDirSign(direction);

  for (const cloud of clouds) {
    if (layerFilter && cloud.layer !== layerFilter) continue;

    // Per-cloud incremental drift — no jump when direction changes
    if (cloud._driftX == null) { cloud._driftX = 0; cloud._driftY = 0; cloud._lastTime = time; }
    const cdt = Math.min(time - cloud._lastTime, 2.0);
    cloud._lastTime = time;

    const cloudSpeed = 15 * speedScale * cloud.speed;
    if (sign === 0) {
      cloud._driftX += cdt * cloudSpeed * 1.5; // ambient rightward drift
    } else if (vert) {
      cloud._driftY += cdt * cloudSpeed * sign;
    } else {
      cloud._driftX += cdt * cloudSpeed * sign;
    }

    ctx.globalAlpha = cloud.opacity * opacityScale;
    ctx.fillStyle = color;

    const cx = wrap(cloud.x + cloud._driftX + w * 0.2, w + 200) - 100;
    const cy = vert
      ? wrap(cloud.y + cloud._driftY + h * 0.2, h + 200) - 100
      : cloud.y;

    ctx.beginPath();
    for (const blob of cloud.blobs) {
      const r = blob.r * radiusScale;
      ctx.moveTo(cx + blob.ox + r, cy + blob.oy);
      ctx.arc(cx + blob.ox, cy + blob.oy, r, 0, Math.PI * 2);
    }
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

// ── Kenney-style flat-bottom clouds ──────────────────────
// Reference: Kenney background_clouds.svg — flat base, 2-3 rounded bumps on top,
// center bump largest, solid white fill, subtle shadow underneath.

function generateKenneyClouds(w, h, count = 7, yMin = 0.05, yMax = 0.55) {
  const clouds = [];
  const uniformSpeed = 0.07;
  const pad = 20;
  const usableW = w - pad * 2;
  const yBase = h * yMin;
  const yRange = h * (yMax - yMin);

  // Distribute across a grid: cols × rows, one cloud per cell
  const cols = Math.ceil(Math.sqrt(count * 1.5));
  const rows = Math.ceil(count / cols);
  const cellW = usableW / cols;
  const cellH = yRange / rows;

  let placed = 0;
  for (let r = 0; r < rows && placed < count; r++) {
    for (let c = 0; c < cols && placed < count; c++) {
      const sc = 0.7 + Math.random() * 0.5;
      const baseW = (55 + Math.random() * 30) * sc;
      const baseH = (10 + Math.random() * 5) * sc;
      const centerR = baseW * 0.28 + Math.random() * baseW * 0.06;
      const leftR = centerR * (0.55 + Math.random() * 0.15);
      const rightR = centerR * (0.5 + Math.random() * 0.15);
      const bumps = [
        { xRatio: 0.22, r: leftR },
        { xRatio: 0.5, r: centerR },
        { xRatio: 0.78, r: rightR },
      ];

      const x = pad + cellW * c + Math.random() * (cellW - baseW);
      const y = yBase + cellH * r + Math.random() * (cellH - centerR * 2);

      clouds.push({ x, y, baseW, baseH, bumps, speed: uniformSpeed, layer: 'back' });
      placed++;
    }
  }
  clouds._w = w;
  clouds._h = h;
  return clouds;
}

function drawKenneyClouds(ctx, w, h, time, clouds, color = '#fff', shadowColor = null, speedScale = 1, direction, layerFilter = null) {
  const sign = getDirSign(direction);

  for (const c of clouds) {
    if (layerFilter && c.layer !== layerFilter) continue;
    if (c._drift == null) { c._drift = 0; c._lastTime = time; }
    const cdt = Math.min(time - c._lastTime, 2.0);
    c._lastTime = time;
    const spd = 15 * speedScale * c.speed;
    if (sign === 0) c._drift += cdt * spd * 1.5;
    else c._drift += cdt * spd * sign;

    const totalW = c.baseW + 40;
    const cx = wrap(c.x + c._drift + w * 0.2, w + totalW + 60) - totalW - 30;
    const cy = c.y;
    const bw = c.baseW;
    const bh = c.baseH;

    // Very subtle shadow underneath (slightly offset down)
    if (shadowColor) {
      ctx.globalAlpha = 0.12;
      ctx.fillStyle = shadowColor;
      _drawCloudShape(ctx, cx, cy + bh * 0.15, bw, bh, c.bumps);
    }

    // Main cloud fill
    ctx.globalAlpha = 1;
    ctx.fillStyle = color;
    _drawCloudShape(ctx, cx, cy, bw, bh, c.bumps);
  }
  ctx.globalAlpha = 1;
}

function _drawCloudShape(ctx, cx, cy, bw, bh, bumps) {
  const sorted = [...bumps].sort((a, b) => a.xRatio - b.xRatio);
  const bottomY = cy + bh;
  const baseR = Math.min(bh, bw * 0.06);

  ctx.beginPath();

  // ── Bottom edge (flat with rounded corners) ──
  ctx.moveTo(cx + baseR, bottomY);
  ctx.lineTo(cx + bw - baseR, bottomY);
  ctx.arcTo(cx + bw, bottomY, cx + bw, cy, baseR);

  // ── Right side up to baseline ──
  ctx.lineTo(cx + bw, cy);

  // ── Top profile: one continuous cubic-bezier path ──
  // Trace right-to-left through each bump peak with smooth valleys between
  let curX = cx + bw;
  let curY = cy;

  for (let i = sorted.length - 1; i >= 0; i--) {
    const bump = sorted[i];
    const peakX = cx + bw * bump.xRatio;
    const peakY = cy - bump.r;

    // End of this bump segment: valley midpoint to next bump, or left edge
    let endX, endY;
    if (i > 0) {
      const next = sorted[i - 1];
      const nextPeakX = cx + bw * next.xRatio;
      endX = (peakX + nextPeakX) / 2;
      endY = cy - Math.min(bump.r, next.r) * 0.12; // shallow valley
    } else {
      endX = cx;
      endY = cy;
    }

    // Cubic bezier with control points pulling up through the peak
    const cp1x = (curX + peakX) / 2;
    const cp1y = peakY;
    const cp2x = (peakX + endX) / 2;
    const cp2y = peakY;
    ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, endX, endY);

    curX = endX;
    curY = endY;
  }

  // If we didn't end exactly at left edge, close to it
  if (curX > cx + 1) ctx.lineTo(cx, cy);

  // ── Left side down with rounded corner ──
  ctx.lineTo(cx, bottomY - baseR);
  ctx.arcTo(cx, bottomY, cx + baseR, bottomY, baseR);
  ctx.closePath();
  ctx.fill();
}

// ── 1. Sky & Clouds ─────────────────────────────────────

function skyClouds(ctx, w, h, time, state, direction, wallTime = time) {
  // Kenney-style: white top fading to very pale blue
  ctx.fillStyle = cachedVGradient(ctx, h, state, 'bg', [
    [0, '#FFFFFF'], [0.4, '#EEF6FC'], [0.7, '#DDEFFF'], [1, '#C3E3FF'],
  ]);
  ctx.fillRect(0, 0, w, h);

  if (!state.clouds || state.clouds._w !== w || state.clouds._h !== h) {
    state.clouds = generateKenneyClouds(w, h, 14, 0.03, 0.92);
  }
  drawKenneyClouds(ctx, w, h, time, state.clouds, '#fff', '#C8DFF0', 1.5, direction, 'back');
}

// ── 2. Aurora Borealis (key: sunsetGlow) ────────────────

function sunsetGlow(ctx, w, h, time, state, direction, wallTime = time) {
  // Dark night sky
  ctx.fillStyle = cachedVGradient(ctx, h, state, 'bg', [
    [0, '#020111'], [0.3, '#0a0e2a'], [0.6, '#0d1840'], [1, '#071028'],
  ]);
  ctx.fillRect(0, 0, w, h);

  // Background stars
  if (!state.auroraStars || state.auroraStars._w !== w || state.auroraStars._h !== h) {
    const stars = [];
    const count = Math.min(Math.floor(cappedArea(w, h) / 1200), 400);
    for (let i = 0; i < count; i++) {
      stars.push({
        x: Math.random() * w,
        y: Math.random() * h,
        r: 0.3 + Math.random() * 1.0,
        baseOpacity: 0.2 + Math.random() * 0.5,
        twinkleSpeed: 0.4 + Math.random() * 1.5,
        twinklePhase: Math.random() * Math.PI * 2,
      });
    }
    stars._w = w;
    stars._h = h;
    state.auroraStars = stars;
  }

  // Draw stars (incremental drift — no jump on time reset)
  const sign = getDirSign(direction);
  const vert = isVertical(direction);
  if (state._auroraStarDrift == null) { state._auroraStarDrift = 0; state._auroraStarLastTime = time; }
  const starDt = Math.min(time - state._auroraStarLastTime, 2.0);
  state._auroraStarLastTime = time;
  state._auroraStarDrift += starDt * 1.5 * sign;
  for (const star of state.auroraStars) {
    const twinkle = 0.5 + 0.5 * Math.sin(wallTime * star.twinkleSpeed + star.twinklePhase);
    ctx.globalAlpha = star.baseOpacity * (0.3 + twinkle * 0.7);
    ctx.fillStyle = '#c8d8ff';
    const sx = vert ? star.x : ((star.x + state._auroraStarDrift) % (w + 4) + w + 4) % (w + 4) - 2;
    const sy = vert ? ((star.y + state._auroraStarDrift) % (h + 4) + h + 4) % (h + 4) - 2 : star.y;
    ctx.beginPath();
    ctx.arc(sx, sy, star.r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;

  // Aurora ribbons
  const ribbonColors = [
    { r: 80, g: 255, b: 180, a: 0.12 },  // green
    { r: 100, g: 220, b: 255, a: 0.08 },  // cyan
    { r: 180, g: 120, b: 255, a: 0.06 },  // purple
    { r: 60, g: 255, b: 140, a: 0.10 },   // bright green
  ];

  if (state._auroraRibbonDrift == null) { state._auroraRibbonDrift = 0; state._auroraRibbonLastTime = time; }
  const ribbonDt = Math.min(time - state._auroraRibbonLastTime, 2.0);
  state._auroraRibbonLastTime = time;
  state._auroraRibbonDrift += ribbonDt * 1.5 * sign;
  const driftOffset = state._auroraRibbonDrift;

  for (let r = 0; r < ribbonColors.length; r++) {
    const ribbon = ribbonColors[r];
    const baseY = h * (0.15 + r * 0.12);
    const ribbonWidth = h * (0.08 + r * 0.02);

    ctx.beginPath();
    const step = 4;
    for (let x = -step; x <= w + step; x += step) {
      const xOff = vert ? x : x + driftOffset;
      const wave1 = Math.sin(xOff * 0.004 + time * 0.04 + r * 1.5) * h * 0.06;
      const wave2 = Math.sin(xOff * 0.008 + time * 0.07 + r * 2.3) * h * 0.03;
      const wave3 = Math.sin(xOff * 0.002 + time * 0.02 + r * 0.7) * h * 0.04;
      const y = baseY + wave1 + wave2 + wave3;
      if (x === -step) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    // Close ribbon shape
    for (let x = w + step; x >= -step; x -= step) {
      const xOff = vert ? x : x + driftOffset;
      const wave1 = Math.sin(xOff * 0.004 + time * 0.04 + r * 1.5) * h * 0.06;
      const wave2 = Math.sin(xOff * 0.008 + time * 0.07 + r * 2.3) * h * 0.03;
      const wave3 = Math.sin(xOff * 0.002 + time * 0.02 + r * 0.7) * h * 0.04;
      const y = baseY + wave1 + wave2 + wave3 + ribbonWidth;
      ctx.lineTo(x, y);
    }
    ctx.closePath();

    // Gradient fill for ribbon
    const grd = ctx.createLinearGradient(0, baseY - h * 0.05, 0, baseY + ribbonWidth + h * 0.05);
    grd.addColorStop(0, `rgba(${ribbon.r},${ribbon.g},${ribbon.b},0)`);
    grd.addColorStop(0.3, `rgba(${ribbon.r},${ribbon.g},${ribbon.b},${ribbon.a})`);
    grd.addColorStop(0.5, `rgba(${ribbon.r},${ribbon.g},${ribbon.b},${ribbon.a * 1.5})`);
    grd.addColorStop(0.7, `rgba(${ribbon.r},${ribbon.g},${ribbon.b},${ribbon.a})`);
    grd.addColorStop(1, `rgba(${ribbon.r},${ribbon.g},${ribbon.b},0)`);
    ctx.fillStyle = grd;
    ctx.fill();
  }

  // Bright aurora glow at peak
  const glowX = w * 0.5 + Math.sin(wallTime * 0.03) * w * 0.15;
  const glowY = h * 0.25;
  const glow = ctx.createRadialGradient(glowX, glowY, 0, glowX, glowY, Math.min(w, h) * 0.3);
  glow.addColorStop(0, 'rgba(100,255,180,0.06)');
  glow.addColorStop(0.5, 'rgba(80,200,255,0.03)');
  glow.addColorStop(1, 'rgba(80,200,255,0)');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, w, h);
}

// ── 3. Deep Space (key: nightStars) ─────────────────────

function nightStars(ctx, w, h, time, state, direction, wallTime = time) {
  // Rich nebula gradient
  const grad = ctx.createRadialGradient(w * 0.35, h * 0.4, 0, w * 0.5, h * 0.5, Math.max(w, h) * 0.9);
  grad.addColorStop(0, '#1a0f3a');
  grad.addColorStop(0.3, '#120a30');
  grad.addColorStop(0.6, '#0a0820');
  grad.addColorStop(1, '#050510');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  const sign = getDirSign(direction);
  const vert = isVertical(direction);

  // Nebula gas clouds — overlapping radial gradients
  if (!state.nebulaClouds || state.nebulaClouds._w !== w || state.nebulaClouds._h !== h) {
    const clouds = [];
    const count = 5 + Math.floor(Math.random() * 3);
    const colors = [
      [120, 60, 200], [80, 40, 180], [60, 100, 200],
      [150, 50, 150], [40, 80, 160], [100, 30, 180],
    ];
    for (let i = 0; i < count; i++) {
      clouds.push({
        x: Math.random() * w,
        y: Math.random() * h,
        r: Math.min(w, h) * (0.15 + Math.random() * 0.25),
        color: colors[i % colors.length],
        opacity: 0.04 + Math.random() * 0.06,
        driftSpeed: 0.5 + Math.random() * 1.0,
        pulseSpeed: 0.2 + Math.random() * 0.3,
        pulsePhase: Math.random() * Math.PI * 2,
      });
    }
    clouds._w = w;
    clouds._h = h;
    state.nebulaClouds = clouds;
  }

  // Draw nebula clouds (incremental drift)
  for (const cloud of state.nebulaClouds) {
    if (cloud._drift == null) { cloud._drift = 0; cloud._lastTime = time; }
    const cdt = Math.min(time - cloud._lastTime, 2.0);
    cloud._lastTime = time;
    cloud._drift += cdt * cloud.driftSpeed * sign * 0.8;
    const pulse = 1 + 0.15 * Math.sin(wallTime * cloud.pulseSpeed + cloud.pulsePhase);
    const cx = vert ? cloud.x : ((cloud.x + cloud._drift) % (w + cloud.r * 2) + w + cloud.r * 2) % (w + cloud.r * 2) - cloud.r;
    const cy = vert ? ((cloud.y + cloud._drift) % (h + cloud.r * 2) + h + cloud.r * 2) % (h + cloud.r * 2) - cloud.r : cloud.y;
    const r = cloud.r * pulse;
    const [cr, cg, cb] = cloud.color;

    const grd = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
    grd.addColorStop(0, `rgba(${cr},${cg},${cb},${cloud.opacity * 1.5})`);
    grd.addColorStop(0.4, `rgba(${cr},${cg},${cb},${cloud.opacity})`);
    grd.addColorStop(1, `rgba(${cr},${cg},${cb},0)`);
    ctx.fillStyle = grd;
    ctx.fillRect(cx - r, cy - r, r * 2, r * 2);
  }

  // Stars
  if (!state.deepStars || state.deepStars._w !== w || state.deepStars._h !== h) {
    const stars = [];
    const count = Math.min(Math.floor(cappedArea(w, h) / 700), 500);
    for (let i = 0; i < count; i++) {
      const colorRnd = Math.random();
      let color;
      if (colorRnd > 0.9) color = '#ffeedd';       // warm
      else if (colorRnd > 0.8) color = '#ccddff';   // cool blue
      else if (colorRnd > 0.7) color = '#eeddff';   // lavender
      else color = '#ffffff';
      stars.push({
        x: Math.random() * w,
        y: Math.random() * h,
        r: 0.3 + Math.random() * 1.2,
        baseOpacity: 0.25 + Math.random() * 0.6,
        twinkleSpeed: 0.4 + Math.random() * 2.0,
        twinklePhase: Math.random() * Math.PI * 2,
        color,
        driftSpeed: 0.8 + Math.random() * 0.2, // tight range — near-uniform drift
      });
    }
    // Add a few bright feature stars with cross-glints
    const featureCount = 3 + Math.floor(Math.random() * 3);
    for (let i = 0; i < featureCount; i++) {
      const colorRnd = Math.random();
      let color = colorRnd > 0.5 ? '#ffeedd' : '#ccddff';
      stars.push({
        x: Math.random() * w,
        y: Math.random() * h,
        r: 1.8 + Math.random() * 1.0,
        baseOpacity: 0.7 + Math.random() * 0.3,
        twinkleSpeed: 0.3 + Math.random() * 0.5,
        twinklePhase: Math.random() * Math.PI * 2,
        color,
        driftSpeed: 0.85,
        feature: true,
      });
    }
    stars._w = w;
    stars._h = h;
    state.deepStars = stars;
  }

  for (const star of state.deepStars) {
    if (star._drift == null) { star._drift = 0; star._lastTime = time; }
    const sdt = Math.min(time - star._lastTime, 2.0);
    star._lastTime = time;
    star._drift += sdt * star.driftSpeed * sign * 1.2;
    const twinkle = 0.5 + 0.5 * Math.sin(wallTime * star.twinkleSpeed + star.twinklePhase);
    ctx.globalAlpha = star.baseOpacity * (0.3 + twinkle * 0.7);
    ctx.fillStyle = star.color;
    const sx = vert ? star.x : ((star.x + star._drift) % (w + 4) + w + 4) % (w + 4) - 2;
    const sy = vert ? ((star.y + star._drift) % (h + 4) + h + 4) % (h + 4) - 2 : star.y;
    ctx.beginPath();
    ctx.arc(sx, sy, star.r, 0, Math.PI * 2);
    ctx.fill();

    // Cross-glint on feature stars
    if (star.feature) {
      const glintLen = star.r * 3;
      ctx.strokeStyle = star.color;
      ctx.lineWidth = 0.5;
      ctx.globalAlpha *= 0.5;
      ctx.beginPath();
      ctx.moveTo(sx - glintLen, sy);
      ctx.lineTo(sx + glintLen, sy);
      ctx.moveTo(sx, sy - glintLen);
      ctx.lineTo(sx, sy + glintLen);
      ctx.stroke();
    }
  }
  ctx.globalAlpha = 1;

  // Shooting stars — more frequent
  if (!state.shootingStar) state.shootingStar = { active: false, nextAt: 2 + Math.random() * 3 };
  const ss = state.shootingStar;
  if (!ss.active && time > ss.nextAt) {
    ss.active = true;
    ss.startTime = time;
    ss.duration = 1.0 + Math.random() * 0.8;
    ss.x0 = Math.random() * w * 0.8 + w * 0.1;
    ss.y0 = Math.random() * h * 0.4;
    const angle = Math.PI * 0.15 + Math.random() * Math.PI * 0.2;
    ss.dx = Math.cos(angle) * w * 0.25;
    ss.dy = Math.sin(angle) * w * 0.25;
  }
  if (ss.active) {
    const t = (time - ss.startTime) / ss.duration;
    if (t > 1) {
      ss.active = false;
      ss.nextAt = time + 2 + Math.random() * 4;
    } else {
      const fade = t < 0.3 ? t / 0.3 : 1 - (t - 0.3) / 0.7;
      ctx.globalAlpha = fade * 0.7;
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      const headX = ss.x0 + ss.dx * t;
      const headY = ss.y0 + ss.dy * t;
      const tailT = Math.max(0, t - 0.15);
      const tailX = ss.x0 + ss.dx * tailT;
      const tailY = ss.y0 + ss.dy * tailT;
      ctx.moveTo(tailX, tailY);
      ctx.lineTo(headX, headY);
      ctx.stroke();
      ctx.globalAlpha = 1;
    }
  }
}

// ── 4. Bokeh (key: neonGrid) ─────────────────────────────
// Clean, Kenney-inspired abstract bokeh — solid rings and filled circles,
// high enough opacity to read on mobile, no ambient drift when static.

function neonGrid(ctx, w, h, time, state, direction, wallTime = time) {
  // Rich dark gradient — deep navy with subtle purple warmth
  ctx.fillStyle = cachedVGradient(ctx, h, state, 'bg', [
    [0, '#0a0a18'], [0.4, '#0e0e20'], [0.7, '#141228'], [1, '#1a1630'],
  ]);
  ctx.fillRect(0, 0, w, h);

  const sign = getDirSign(direction);
  const vert = isVertical(direction);

  if (!state.bokeh || state.bokeh._w !== w || state.bokeh._h !== h) {
    const circles = [];
    const colors = [
      [255, 180, 80],   // warm amber
      [100, 170, 255],  // cool blue
      [255, 130, 170],  // soft pink
      [180, 130, 255],  // lavender
      [255, 220, 100],  // golden
      [120, 255, 200],  // mint
    ];

    // Kenney-inspired: fewer circles, more visible, clean defined shapes
    // Back layer — small solid dots
    const backCount = Math.min(Math.floor(cappedArea(w, h) / 4000), 120);
    for (let i = 0; i < backCount; i++) {
      circles.push({
        x: Math.random() * w, y: Math.random() * h,
        r: 2 + Math.random() * 5,
        color: colors[Math.floor(Math.random() * colors.length)],
        opacity: 0.15 + Math.random() * 0.25,
        pulseSpeed: 0.2 + Math.random() * 0.4,
        pulsePhase: Math.random() * Math.PI * 2,
        driftSpeed: 2 + Math.random() * 2,
        layer: 'back', ring: false,
      });
    }
    // Mid layer — mix of filled circles and rings
    const midCount = Math.min(Math.floor(cappedArea(w, h) / 10000), 40);
    for (let i = 0; i < midCount; i++) {
      const isRing = Math.random() > 0.4;
      circles.push({
        x: Math.random() * w, y: Math.random() * h,
        r: 12 + Math.random() * 22,
        color: colors[Math.floor(Math.random() * colors.length)],
        opacity: isRing ? 0.08 + Math.random() * 0.12 : 0.04 + Math.random() * 0.06,
        pulseSpeed: 0.15 + Math.random() * 0.3,
        pulsePhase: Math.random() * Math.PI * 2,
        driftSpeed: 3 + Math.random() * 3,
        layer: 'mid', ring: isRing,
      });
    }
    // Front layer — large rings, drawn by foreground renderer
    const frontCount = Math.min(6, Math.floor(cappedArea(w, h) / 30000));
    for (let i = 0; i < frontCount; i++) {
      circles.push({
        x: Math.random() * w, y: Math.random() * h,
        r: 35 + Math.random() * 45,
        color: colors[Math.floor(Math.random() * colors.length)],
        opacity: 0.06 + Math.random() * 0.06,
        pulseSpeed: 0.1 + Math.random() * 0.2,
        pulsePhase: Math.random() * Math.PI * 2,
        driftSpeed: 5 + Math.random() * 4,
        layer: 'front', ring: true,
      });
    }
    circles._w = w;
    circles._h = h;
    state.bokeh = circles;
  }

  // Draw back + mid layers
  for (const circle of state.bokeh) {
    if (circle.layer === 'front') continue;

    if (circle._drift == null) { circle._drift = 0; circle._lastTime = time; }
    const bdt = Math.min(time - circle._lastTime, 2.0);
    circle._lastTime = time;
    circle._drift += bdt * circle.driftSpeed * sign;
    const pulse = 0.7 + 0.3 * Math.sin(wallTime * circle.pulseSpeed + circle.pulsePhase);
    // Gentle ambient float — slow sine bob even when static
    const floatX = Math.sin(wallTime * 0.15 + circle.pulsePhase) * circle.r * 0.4;
    const floatY = Math.cos(wallTime * 0.1 + circle.pulsePhase * 1.3) * circle.r * 0.3;
    const wrapW = w + circle.r * 2;
    const wrapH = h + circle.r * 2;
    const cx = (vert ? circle.x : ((circle.x + circle._drift) % wrapW + wrapW) % wrapW - circle.r) + floatX;
    const cy = (vert ? ((circle.y + circle._drift) % wrapH + wrapH) % wrapH - circle.r : circle.y) + floatY;
    const [cr, cg, cb] = circle.color;

    ctx.globalAlpha = circle.opacity * pulse;
    if (circle.ring) {
      // Clean ring — Kenney-style defined stroke, no gradient blur
      ctx.strokeStyle = `rgb(${cr},${cg},${cb})`;
      ctx.lineWidth = Math.max(1.5, circle.r * 0.08);
      ctx.beginPath();
      ctx.arc(cx, cy, circle.r, 0, Math.PI * 2);
      ctx.stroke();
    } else {
      // Solid filled circle — clean and visible
      ctx.fillStyle = `rgb(${cr},${cg},${cb})`;
      ctx.beginPath();
      ctx.arc(cx, cy, circle.r, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  ctx.globalAlpha = 1;
}

// ── 5. Underwater (key: oceanWaves) ─────────────────────

function oceanWaves(ctx, w, h, time, state, direction, wallTime = time) {
  // Depth gradient — brighter surface zone at top, deep dark at bottom
  ctx.fillStyle = cachedVGradient(ctx, h, state, 'bg', [
    [0, '#1A90A8'], [0.15, '#0E6878'], [0.4, '#085060'], [0.7, '#043848'], [1, '#022830'],
  ]);
  ctx.fillRect(0, 0, w, h);

  const sign = getDirSign(direction);
  const vert = isVertical(direction);

  // Caustic light rays from above (incremental drift)
  if (state._oceanRayDrift == null) { state._oceanRayDrift = 0; state._oceanRayLastTime = time; }
  const rayDt = Math.min(time - state._oceanRayLastTime, 2.0);
  state._oceanRayLastTime = time;
  if (!vert) state._oceanRayDrift += rayDt * 5 * sign;
  const rayCount = 8;
  for (let i = 0; i < rayCount; i++) {
    const baseX = (w / (rayCount - 1)) * i;
    const sway = Math.sin(wallTime * 0.12 + i * 1.3) * w * 0.04;
    const drift = vert ? 0 : state._oceanRayDrift;
    const x = ((baseX + sway + drift) % (w + 100) + w + 100) % (w + 100) - 50;
    const rayWidth = w * 0.06 + Math.sin(wallTime * 0.08 + i) * w * 0.02;
    const intensity = 0.03 + 0.015 * Math.sin(wallTime * 0.15 + i * 0.8);

    ctx.globalAlpha = intensity;
    ctx.fillStyle = '#80d8e8';
    ctx.beginPath();
    ctx.moveTo(x - rayWidth * 0.3, 0);
    ctx.lineTo(x + rayWidth * 0.3, 0);
    ctx.lineTo(x + rayWidth * 1.5, h);
    ctx.lineTo(x - rayWidth * 1.5, h);
    ctx.closePath();
    ctx.fill();
  }
  ctx.globalAlpha = 1;

  // Subtle light caustic pattern overlay — pre-rendered sprite
  const causticSize = 120;
  if (!state._causticSprite) {
    const spriteW = Math.round(causticSize * 0.8);
    const oc = document.createElement('canvas');
    oc.width = spriteW;
    oc.height = spriteW;
    const sctx = oc.getContext('2d');
    const half = spriteW / 2;
    const grd = sctx.createRadialGradient(half, half, 0, half, half, half);
    grd.addColorStop(0, '#90e8f0');
    grd.addColorStop(1, 'rgba(144,232,240,0)');
    sctx.fillStyle = grd;
    sctx.fillRect(0, 0, spriteW, spriteW);
    state._causticSprite = oc;
  }
  const causticCount = Math.ceil(w / causticSize) + 2;
  const causticRows = Math.ceil(h / causticSize) + 2;
  const causticDraw = causticSize * 0.4;
  for (let row = 0; row < causticRows; row++) {
    for (let col = 0; col < causticCount; col++) {
      const bx = col * causticSize + Math.sin(wallTime * 0.08 + row * 2.1) * 15;
      const by = row * causticSize + Math.cos(wallTime * 0.06 + col * 1.7) * 15;
      const brightness = 0.012 + 0.008 * Math.sin(wallTime * 0.15 + col * 1.3 + row * 0.9);
      ctx.globalAlpha = brightness;
      ctx.drawImage(state._causticSprite, bx - causticDraw, by - causticDraw);
    }
  }
  ctx.globalAlpha = 1;

  // Background bubbles (small, distant)
  if (!state.bubbles || state.bubbles._w !== w || state.bubbles._h !== h) {
    const bubbles = [];
    const count = Math.min(Math.floor(cappedArea(w, h) / 6000), 120);
    for (let i = 0; i < count; i++) {
      bubbles.push({
        x: Math.random() * w,
        y: Math.random() * h,
        r: 1.5 + Math.random() * 4,
        riseSpeed: 8 + Math.random() * 15,
        wobbleSpeed: 0.5 + Math.random() * 1.5,
        wobbleAmp: 3 + Math.random() * 8,
        wobblePhase: Math.random() * Math.PI * 2,
        opacity: 0.08 + Math.random() * 0.12,
        layer: 'back',
      });
    }
    // Foreground bubbles — larger, closer
    const fgCount = Math.min(12, Math.floor(cappedArea(w, h) / 20000));
    for (let i = 0; i < fgCount; i++) {
      bubbles.push({
        x: Math.random() * w,
        y: Math.random() * h,
        r: 6 + Math.random() * 14,
        riseSpeed: 12 + Math.random() * 20,
        wobbleSpeed: 0.3 + Math.random() * 0.8,
        wobbleAmp: 5 + Math.random() * 12,
        wobblePhase: Math.random() * Math.PI * 2,
        opacity: 0.06 + Math.random() * 0.08,
        layer: 'front',
      });
    }
    bubbles._w = w;
    bubbles._h = h;
    state.bubbles = bubbles;
  }

  // Draw background bubbles only (incremental rise)
  for (const bubble of state.bubbles) {
    if (bubble.layer === 'front') continue;
    if (bubble._rise == null) { bubble._rise = 0; bubble._lastTime = time; }
    const bdt = Math.min(time - bubble._lastTime, 2.0);
    bubble._lastTime = time;
    bubble._rise += bdt * bubble.riseSpeed;
    const wobble = Math.sin(wallTime * bubble.wobbleSpeed + bubble.wobblePhase) * bubble.wobbleAmp;
    const rise = bubble._rise % (h + bubble.r * 4);
    const bx = bubble.x + wobble;
    const by = ((bubble.y - rise) % (h + bubble.r * 4) + h + bubble.r * 4) % (h + bubble.r * 4) - bubble.r * 2;

    ctx.globalAlpha = bubble.opacity;
    ctx.strokeStyle = 'rgba(180,230,255,0.4)';
    ctx.lineWidth = 0.8;
    ctx.beginPath();
    ctx.arc(bx, by, bubble.r, 0, Math.PI * 2);
    ctx.stroke();

    // Highlight
    ctx.globalAlpha = bubble.opacity * 0.5;
    ctx.fillStyle = '#fff';
    ctx.beginPath();
    ctx.arc(bx - bubble.r * 0.25, by - bubble.r * 0.25, bubble.r * 0.2, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

// ── 6. Rolling Hills ────────────────────────────────────
// Pointed triangle-wave terrain with crisp drifting clouds
// (matching the skyClouds style) and foreground wisps.

function rollingHills(ctx, w, h, time, state, direction, wallTime = time) {
  // Kenney-style sky — pale blue top (enough contrast for white clouds), lighter bottom
  const grad = ctx.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, '#C3E3FF');
  grad.addColorStop(0.5, '#D6EDFF');
  grad.addColorStop(0.8, '#E4F2F8');
  grad.addColorStop(1, '#EDF6FF');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  const sign = getDirSign(direction);

  // Incremental drift — accumulates only while direction is set, no jump on transition
  if (state._rhLastTime == null) state._rhLastTime = time;
  const dt = Math.min(time - state._rhLastTime, 2.0);
  state._rhLastTime = time;

  // Kenney-style clouds — more clouds, spread across sky
  if (!state.kenneyCloudsList || state.kenneyCloudsList._w !== w || state.kenneyCloudsList._h !== h) {
    state.kenneyCloudsList = generateKenneyClouds(w, h, 6, 0.03, 0.38);
  }
  drawKenneyClouds(ctx, w, h, time, state.kenneyCloudsList, '#fff', '#D8E8F4', 1.5, direction, 'back');

  // Scenery — Kenney-style single smooth sine wave per layer
  if (!state.landscape || state.landscape._w !== w || state.landscape._h !== h) {
    state.landscape = {
      _w: w, _h: h,
      // Single sine wave per layer — clean, uniform rolling bumps like Kenney
      mountain: { baseY: h * 0.55, amp: h * 0.06, freq: w * 0.8, phase: Math.random() * Math.PI * 2, color: '#D4E8F2', speed: 5, _drift: 0 },
      hill:     { baseY: h * 0.70, amp: h * 0.04, freq: w * 0.65, phase: Math.random() * Math.PI * 2, color: '#5AC96F', speed: 12, _drift: 0 },
    };
  }

  // Draw each layer as a single clean sine wave
  for (const key of ['mountain', 'hill']) {
    const layer = state.landscape[key];
    layer._drift -= sign * dt * layer.speed;
    ctx.fillStyle = layer.color;
    ctx.beginPath();
    ctx.moveTo(0, h);
    for (let x = 0; x <= w; x += 3) {
      const y = layer.baseY - layer.amp * (Math.cos(((x + layer._drift) / layer.freq) * Math.PI * 2 + layer.phase) * 0.5 + 0.5);
      ctx.lineTo(x, y);
    }
    ctx.lineTo(w, h);
    ctx.closePath();
    ctx.fill();
  }
}

// ── Fairway ───────────────────────────────────────────────
//
// Bright daytime golf course scene: blue sky, soft clouds, distant rolling
// hills, a flagstick on a far hill, and a vivid green fairway in the
// foreground. Built for golf-prospect demos but reads as generic "fresh
// outdoor sport" for any hospitality / outdoor brand.

function fairway(ctx, w, h, time, state, direction, wallTime = time) {
  // Sky — bright clear daytime
  const sky = cachedVGradient(ctx, h, state, 'fairway_sky', [
    [0,    '#7FC7FF'],
    [0.55, '#B8E0FF'],
    [0.85, '#E8F5FF'],
    [1,    '#F4FAFF'],
  ]);
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, w, h);

  const sign = getDirSign(direction);
  if (state._fwLastTime == null) state._fwLastTime = time;
  const dt = Math.min(time - state._fwLastTime, 2.0);
  state._fwLastTime = time;

  // Soft scattered clouds (lazy gen)
  if (!state.fwClouds || state.fwClouds._w !== w || state.fwClouds._h !== h) {
    state.fwClouds = generateKenneyClouds(w, h, 5, 0.04, 0.32);
  }
  drawKenneyClouds(ctx, w, h, time, state.fwClouds, '#FFFFFF', '#E0EEFA', 1.4, direction, 'back');

  // Landscape layers — three depths of soft rolling hills, deepest greens
  // up front. Single sine wave per layer (Kenney-style) for that clean,
  // friendly arcade look that matches Sky & Clouds + Rolling Hills.
  if (!state.fwLandscape || state.fwLandscape._w !== w || state.fwLandscape._h !== h) {
    state.fwLandscape = {
      _w: w, _h: h,
      far:   { baseY: h * 0.58, amp: h * 0.04, freq: w * 0.95, phase: Math.random() * Math.PI * 2, color: '#B8D8B5', speed: 4,  _drift: 0 },
      mid:   { baseY: h * 0.68, amp: h * 0.05, freq: w * 0.75, phase: Math.random() * Math.PI * 2, color: '#7CC07A', speed: 8,  _drift: 0 },
      near:  { baseY: h * 0.80, amp: h * 0.045, freq: w * 0.6,  phase: Math.random() * Math.PI * 2, color: '#56AC55', speed: 14, _drift: 0 },
      // Flagstick lives on the mid hill — positioned by sampling that
      // layer's wave so the flag always sits on a peak.
      flagAnchorX: w * (0.55 + Math.random() * 0.2),
    };
  }
  const land = state.fwLandscape;

  for (const key of ['far', 'mid', 'near']) {
    const layer = land[key];
    layer._drift -= sign * dt * layer.speed;
    ctx.fillStyle = layer.color;
    ctx.beginPath();
    ctx.moveTo(0, h);
    for (let x = 0; x <= w; x += 3) {
      const y = layer.baseY - layer.amp * (Math.cos(((x + layer._drift) / layer.freq) * Math.PI * 2 + layer.phase) * 0.5 + 0.5);
      ctx.lineTo(x, y);
    }
    ctx.lineTo(w, h);
    ctx.closePath();
    ctx.fill();
  }

  // Flagstick — drifts horizontally with the mid layer so it parallaxes
  // correctly. Sample the mid layer's wave at the flag's screen X to plant
  // the base on the hill surface.
  const mid = land.mid;
  const flagX = ((land.flagAnchorX + mid._drift) % w + w) % w;
  const groundY = mid.baseY - mid.amp * (Math.cos(((flagX + mid._drift) / mid.freq) * Math.PI * 2 + mid.phase) * 0.5 + 0.5);
  const poleH = Math.min(h * 0.12, 70);
  const poleTop = groundY - poleH;

  // Pole
  ctx.strokeStyle = '#FFFFFF';
  ctx.lineWidth = 2;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(flagX, groundY);
  ctx.lineTo(flagX, poleTop);
  ctx.stroke();

  // Flag — triangle with a tiny gentle flutter
  const flutter = Math.sin(wallTime * 1.8) * 1.5;
  const flagW = 14;
  const flagH = 10;
  ctx.fillStyle = '#E84747';
  ctx.beginPath();
  ctx.moveTo(flagX + 1, poleTop);
  ctx.lineTo(flagX + 1 + flagW, poleTop + flagH / 2 + flutter);
  ctx.lineTo(flagX + 1, poleTop + flagH);
  ctx.closePath();
  ctx.fill();

  // Foreground fairway — saturated green band at the very bottom with
  // faint horizontal mowing stripes, drifting with the near layer.
  const fwTop = h * 0.86;
  const fairwayGrad = cachedVGradient(ctx, h, state, 'fairway_grass', [
    [0,    '#4FA34D'],
    [0.6,  '#3F933E'],
    [1,    '#2F7E31'],
  ]);
  ctx.save();
  ctx.beginPath();
  ctx.rect(0, fwTop, w, h - fwTop);
  ctx.clip();
  ctx.fillStyle = fairwayGrad;
  ctx.fillRect(0, fwTop, w, h - fwTop);

  // Mowing stripes — soft alternating bands. Subtle so it reads as texture
  // not stripes-as-decoration.
  const stripeH = Math.max(6, (h - fwTop) / 6);
  const offset = ((land.near._drift * 0.3) % (stripeH * 2) + stripeH * 2) % (stripeH * 2);
  ctx.fillStyle = 'rgba(255,255,255,0.05)';
  for (let y = fwTop - offset; y < h; y += stripeH * 2) {
    ctx.fillRect(0, y, w, stripeH);
  }
  ctx.restore();
}

function fairway_fg(ctx, w, h, time, state, direction, wallTime = time) {
  ctx.clearRect(0, 0, w, h);
}

function sunsetHorizon(ctx, w, h, time, state, direction, wallTime = time) {
  // Warm sunset gradient
  const grad = ctx.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, '#1B1033');
  grad.addColorStop(0.15, '#3D1F5C');
  grad.addColorStop(0.35, '#C44B3F');
  grad.addColorStop(0.5, '#E8804A');
  grad.addColorStop(0.65, '#F4B860');
  grad.addColorStop(0.8, '#FCE38A');
  grad.addColorStop(1, '#FFF5D0');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  const sign = getDirSign(direction);

  // Dusk stars in the upper dark zone
  if (!state.sunsetStars || state.sunsetStars._w !== w || state.sunsetStars._h !== h) {
    const stars = [];
    const count = Math.min(Math.floor(cappedArea(w, h) / 3000), 80);
    for (let i = 0; i < count; i++) {
      stars.push({
        x: Math.random() * w,
        y: Math.random() * h * 0.25,
        r: 0.3 + Math.random() * 0.8,
        opacity: 0.15 + Math.random() * 0.35,
        twinkleSpeed: 0.3 + Math.random() * 0.8,
        twinklePhase: Math.random() * Math.PI * 2,
      });
    }
    stars._w = w;
    stars._h = h;
    state.sunsetStars = stars;
  }
  for (const star of state.sunsetStars) {
    const twinkle = 0.5 + 0.5 * Math.sin(wallTime * star.twinkleSpeed + star.twinklePhase);
    ctx.globalAlpha = star.opacity * twinkle;
    ctx.fillStyle = '#e8d0ff';
    ctx.beginPath();
    ctx.arc(star.x, star.y, star.r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;

  // Sun glow — soft radial gradient near the horizon
  const sunX = w * 0.5 + Math.sin(wallTime * 0.02) * w * 0.05;
  const sunY = h * 0.72;
  const sunR = Math.min(w, h) * 0.25;
  const sunGlow = ctx.createRadialGradient(sunX, sunY, 0, sunX, sunY, sunR);
  sunGlow.addColorStop(0, 'rgba(255,240,200,0.35)');
  sunGlow.addColorStop(0.3, 'rgba(255,200,120,0.15)');
  sunGlow.addColorStop(0.7, 'rgba(255,160,80,0.05)');
  sunGlow.addColorStop(1, 'rgba(255,120,60,0)');
  ctx.fillStyle = sunGlow;
  ctx.fillRect(0, 0, w, h);

  // Cloud bands — crisp layered strips across the sky
  if (!state.sunsetBands || state.sunsetBands._w !== w) {
    const bands = [];
    const bandCount = 10 + Math.floor(Math.random() * 4);
    const minGap = h * 0.025;
    const usedYs = [];
    for (let i = 0; i < bandCount; i++) {
      const bandHeight = 6 + Math.random() * 8;
      let y, attempts = 0;
      do {
        y = h * 0.08 + Math.random() * h * 0.55;
        attempts++;
      } while (attempts < 30 && usedYs.some(([uy, uh]) => Math.abs(y - uy) < uh + minGap));
      usedYs.push([y, bandHeight]);
      bands.push({
        x: Math.random() * w * 1.5 - w * 0.25,
        y,
        width: 80 + Math.random() * 160,
        height: bandHeight,
        opacity: 0.15 + Math.random() * 0.25,
        speed: 4,
        layer: i < 3 ? 'front' : 'back',
      });
    }
    bands._w = w;
    state.sunsetBands = bands;
  }

  // Draw back cloud bands — colour tints warmer near horizon
  for (const band of state.sunsetBands) {
    if (band.layer === 'front') continue;
    if (band._drift == null) { band._drift = 0; band._lastTime = time; }
    const dt = Math.min(time - band._lastTime, 2.0);
    band._lastTime = time;
    band._drift += dt * band.speed * (sign || 0.5);
    const bx = ((band.x + band._drift) % (w + band.width + 100) + w + band.width + 100) % (w + band.width + 100) - band.width - 50;
    ctx.globalAlpha = band.opacity;
    // Warmer colour near horizon, darker higher up
    const yRatio = band.y / (h * 0.6);
    const r = Math.floor(60 + yRatio * 140);
    const g = Math.floor(20 + yRatio * 60);
    const b = Math.floor(40 + yRatio * 20);
    ctx.fillStyle = `rgba(${r},${g},${b},0.6)`;
    // Rounded cloud band
    const hr = band.height / 2;
    ctx.beginPath();
    ctx.moveTo(bx + hr, band.y);
    ctx.lineTo(bx + band.width - hr, band.y);
    ctx.arc(bx + band.width - hr, band.y + hr, hr, -Math.PI / 2, Math.PI / 2);
    ctx.lineTo(bx + hr, band.y + band.height);
    ctx.arc(bx + hr, band.y + hr, hr, Math.PI / 2, -Math.PI / 2);
    ctx.closePath();
    ctx.fill();
  }
  ctx.globalAlpha = 1;

  // Silhouette ground strip
  const groundY = h * 0.88;
  ctx.fillStyle = '#1a0a20';
  ctx.beginPath();
  ctx.moveTo(-2, h + 2);
  for (let x = -2; x <= w + 2; x += 6) {
    const y = groundY + Math.sin(x * 0.008 + 1.5) * h * 0.015 + Math.sin(x * 0.02 + 0.7) * h * 0.006;
    ctx.lineTo(x, y);
  }
  ctx.lineTo(w + 2, h + 2);
  ctx.closePath();
  ctx.fill();
}

// ── 8. Mountain Range ───────────────────────────────────
// Pointed mountain peaks with drifting clouds and foreground wisps.

function mountainRange(ctx, w, h, time, state, direction, wallTime = time) {
  // Dusk sky
  const grad = ctx.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, '#0F1B3D');
  grad.addColorStop(0.5, '#1E2D5A');
  grad.addColorStop(1, '#3A5070');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  // Twinkling stars
  if (!state.mtStars || state.mtStars._w !== w || state.mtStars._h !== h) {
    const stars = [];
    const count = Math.min(Math.floor((cappedArea(w, h) * 0.35) / 1200), 200);
    for (let i = 0; i < count; i++) {
      stars.push({
        x: Math.random() * w,
        y: Math.random() * h * 0.4,
        r: 0.4 + Math.random() * 0.8,
        opacity: 0.3 + Math.random() * 0.4,
        twinkleSpeed: 0.5 + Math.random() * 1.5,
        twinklePhase: Math.random() * Math.PI * 2,
      });
    }
    stars._w = w;
    stars._h = h;
    state.mtStars = stars;
  }

  for (const star of state.mtStars) {
    const twinkle = 0.5 + 0.5 * Math.sin(wallTime * star.twinkleSpeed + star.twinklePhase);
    ctx.globalAlpha = star.opacity * twinkle;
    ctx.fillStyle = '#fff';
    ctx.beginPath();
    ctx.arc(star.x, star.y, star.r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;

  // Very subtle clouds — nearly invisible against the night sky
  if (!state.mtClouds || state.mtClouds._w !== w || state.mtClouds._h !== h * 0.45) {
    state.mtClouds = generateKenneyClouds(w, h * 0.45, 2, 0.05, 0.50);
  }
  drawKenneyClouds(ctx, w, h * 0.45, time, state.mtClouds, '#1e2d4a', '#162540', 1.5, direction, 'back');

  const sign = getDirSign(direction);

  // Incremental drift — no jump on transition
  if (state._mrLastTime == null) state._mrLastTime = time;
  const dt = Math.min(time - state._mrLastTime, 2.0);
  state._mrLastTime = time;

  // Spiky mountain layers — sharp triangular peaks with clean zigzag ridgelines
  if (!state.mountains || state.mountains._w !== w || state.mountains._h !== h) {
    // Generate zigzag peaks for a layer
    function genSpikes(count, baseY, minAmp, maxAmp) {
      const points = [];
      const segW = (w * 1.5) / count;
      for (let i = 0; i < count + 2; i++) {
        const isPeak = i % 2 === 0;
        const x = segW * i - w * 0.25;
        const amp = isPeak ? (minAmp + Math.random() * (maxAmp - minAmp)) : 0;
        // Valleys sit slightly above base for natural look
        const valleyLift = isPeak ? 0 : (minAmp * (0.1 + Math.random() * 0.2));
        points.push({ x, y: baseY - amp - valleyLift });
      }
      return points;
    }

    state.mountains = [
      // Back — tall peaks, base pushed down
      { points: genSpikes(6, h * 0.72, h * 0.10, h * 0.28), color: '#2a3f5e', snowColor: null, snowLine: 0, speed: 2, _drift: 0, baseY: h * 0.72 },
      // Front — medium peaks, base near bottom
      { points: genSpikes(8, h * 0.88, h * 0.05, h * 0.14), color: '#1a2b4a', snowColor: null, snowLine: 0, speed: 5, _drift: 0, baseY: h * 0.88 },
    ];
    state.mountains._w = w;
    state.mountains._h = h;
  }

  for (const layer of state.mountains) {
    layer._drift += sign * dt * layer.speed;

    // Draw mountain silhouette — straight lines between peaks and valleys
    ctx.fillStyle = layer.color;
    ctx.beginPath();
    ctx.moveTo(-20, h + 2);
    for (const pt of layer.points) {
      ctx.lineTo(pt.x + layer._drift, pt.y);
    }
    ctx.lineTo(w + 20, h + 2);
    ctx.closePath();
    ctx.fill();

  }
}
// ── 10. Snowfall ─────────────────────────────────────────

function snowfall(ctx, w, h, time, state, direction, wallTime = time) {
  // Cold blue-white sky gradient
  const grad = ctx.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, '#3a4a6a');
  grad.addColorStop(0.3, '#5a6a8a');
  grad.addColorStop(0.6, '#8090a8');
  grad.addColorStop(1, '#a8b8c8');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  const sign = getDirSign(direction);
  const vert = isVertical(direction);

  // Generate snowflakes (back layer only — front drawn by foreground renderer)
  if (!state.snowflakes || state.snowflakes._w !== w || state.snowflakes._h !== h) {
    const flakes = [];
    const backCount = Math.min(Math.floor(cappedArea(w, h) / 4000), 150);
    for (let i = 0; i < backCount; i++) {
      flakes.push({
        x: Math.random() * w,
        y: Math.random() * h,
        r: 0.8 + Math.random() * 2.0,
        fallSpeed: 8 + Math.random() * 15,
        wobbleSpeed: 0.3 + Math.random() * 0.8,
        wobbleAmp: 3 + Math.random() * 8,
        wobblePhase: Math.random() * Math.PI * 2,
        opacity: 0.2 + Math.random() * 0.4,
        layer: 'back',
      });
    }
    // Front snowflakes — larger, closer
    const frontCount = Math.min(15, Math.floor(cappedArea(w, h) / 18000));
    for (let i = 0; i < frontCount; i++) {
      flakes.push({
        x: Math.random() * w,
        y: Math.random() * h,
        r: 3 + Math.random() * 5,
        fallSpeed: 15 + Math.random() * 20,
        wobbleSpeed: 0.2 + Math.random() * 0.5,
        wobbleAmp: 5 + Math.random() * 12,
        wobblePhase: Math.random() * Math.PI * 2,
        opacity: 0.08 + Math.random() * 0.12,
        layer: 'front',
      });
    }
    flakes._w = w;
    flakes._h = h;
    state.snowflakes = flakes;
  }

  // Draw back snowflakes (incremental drift — no jump on time reset)
  ctx.fillStyle = '#fff';
  for (const flake of state.snowflakes) {
    if (flake.layer === 'front') continue;
    if (flake._driftX == null) { flake._driftX = 0; flake._driftY = 0; flake._lastTime = time; }
    const fdt = Math.min(time - flake._lastTime, 2.0);
    flake._lastTime = time;
    flake._driftY += fdt * flake.fallSpeed;
    if (sign !== 0) flake._driftX += fdt * 5 * sign;
    const wobble = Math.sin(wallTime * flake.wobbleSpeed + flake.wobblePhase) * flake.wobbleAmp;
    const fx = ((flake.x + wobble + flake._driftX) % (w + flake.r * 4) + w + flake.r * 4) % (w + flake.r * 4) - flake.r * 2;
    const fy = ((flake.y + flake._driftY) % (h + flake.r * 4) + h + flake.r * 4) % (h + flake.r * 4) - flake.r * 2;

    ctx.globalAlpha = flake.opacity;
    ctx.beginPath();
    ctx.arc(fx, fy, flake.r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;

  // Snow-covered hill silhouettes — multiple smooth layers for depth
  const hillData = [
    { baseY: 0.78, color: 'rgba(140,155,175,0.35)', waves: [{ freq: 0.004, amp: 0.03 }, { freq: 0.009, amp: 0.015 }, { freq: 0.018, amp: 0.008 }] },
    { baseY: 0.85, color: 'rgba(170,185,200,0.45)', waves: [{ freq: 0.005, amp: 0.025 }, { freq: 0.012, amp: 0.012 }, { freq: 0.022, amp: 0.006 }] },
    { baseY: 0.92, color: 'rgba(200,210,220,0.55)', waves: [{ freq: 0.006, amp: 0.018 }, { freq: 0.015, amp: 0.01 }] },
  ];
  for (const hill of hillData) {
    ctx.fillStyle = hill.color;
    ctx.beginPath();
    ctx.moveTo(0, h);
    for (let x = 0; x <= w; x += 4) {
      let y = h * hill.baseY;
      for (const wv of hill.waves) {
        y -= Math.cos(x * wv.freq + wv.freq * 500) * h * wv.amp;
      }
      ctx.lineTo(x, y);
    }
    ctx.lineTo(w, h);
    ctx.closePath();
    ctx.fill();
  }
}

// ── 11. Dark Energy ──────────────────────────────────────

function lavaFlow(ctx, w, h, time, state, direction, wallTime = time) {
  // Deep dark gradient — cooler, more void-like
  const grad = ctx.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, '#04000a');
  grad.addColorStop(0.3, '#08001a');
  grad.addColorStop(0.5, '#0a0020');
  grad.addColorStop(0.7, '#0c0018');
  grad.addColorStop(1, '#06000f');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  // Subtle ambient pulse — the void breathes
  const pulse = 0.03 * Math.sin(wallTime * 0.15);
  ctx.fillStyle = `rgba(80,20,160,${Math.max(0, pulse)})`;
  ctx.fillRect(0, 0, w, h);

  const sign = getDirSign(direction);
  const vert = isVertical(direction);

  // Generate energy stream bands
  if (!state.lavaBands || state.lavaBands._w !== w || state.lavaBands._h !== h) {
    const bands = [];
    const bandCount = 3;
    const colors = [
      { r: 120, g: 40, b: 255 },  // deep purple
      { r: 40, g: 180, b: 255 },  // cyan
      { r: 200, g: 50, b: 180 },  // magenta
    ];
    for (let i = 0; i < bandCount; i++) {
      bands.push({
        baseY: h * (0.3 + i * 0.22),
        thickness: h * (0.04 + Math.random() * 0.03),
        color: colors[i],
        waveFreq1: 0.003 + Math.random() * 0.002,
        waveFreq2: 0.007 + Math.random() * 0.003,
        waveAmp1: h * (0.03 + Math.random() * 0.02),
        waveAmp2: h * (0.01 + Math.random() * 0.01),
        speed1: 0.06 + Math.random() * 0.04,
        speed2: 0.10 + Math.random() * 0.06,
        opacity: 0.5 + Math.random() * 0.3,
      });
    }
    bands._w = w;
    bands._h = h;
    state.lavaBands = bands;
  }

  // Draw lava bands (incremental drift — no jump on time reset)
  if (state._lavaDrift == null) { state._lavaDrift = 0; state._lavaLastTime = time; }
  const lavaDt = Math.min(time - state._lavaLastTime, 2.0);
  state._lavaLastTime = time;
  state._lavaDrift += lavaDt * 4 * (sign || 0.15);
  const driftOffset = state._lavaDrift;
  for (const band of state.lavaBands) {
    const { r: cr, g: cg, b: cb } = band.color;
    // Outer glow
    ctx.globalAlpha = band.opacity * 0.15;
    ctx.fillStyle = `rgb(${cr},${cg},${cb})`;
    ctx.beginPath();
    const step = 4;
    for (let x = -step; x <= w + step; x += step) {
      const xOff = vert ? x : x + driftOffset;
      const wave1 = Math.sin(xOff * band.waveFreq1 + time * band.speed1) * band.waveAmp1;
      const wave2 = Math.sin(xOff * band.waveFreq2 + time * band.speed2) * band.waveAmp2;
      const y = band.baseY + wave1 + wave2 - band.thickness;
      if (x === -step) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    for (let x = w + step; x >= -step; x -= step) {
      const xOff = vert ? x : x + driftOffset;
      const wave1 = Math.sin(xOff * band.waveFreq1 + time * band.speed1) * band.waveAmp1;
      const wave2 = Math.sin(xOff * band.waveFreq2 + time * band.speed2) * band.waveAmp2;
      const y = band.baseY + wave1 + wave2 + band.thickness * 2;
      ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.fill();

    // Core bright band
    ctx.globalAlpha = band.opacity * 0.6;
    ctx.beginPath();
    for (let x = -step; x <= w + step; x += step) {
      const xOff = vert ? x : x + driftOffset;
      const wave1 = Math.sin(xOff * band.waveFreq1 + time * band.speed1) * band.waveAmp1;
      const wave2 = Math.sin(xOff * band.waveFreq2 + time * band.speed2) * band.waveAmp2;
      const y = band.baseY + wave1 + wave2;
      if (x === -step) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    for (let x = w + step; x >= -step; x -= step) {
      const xOff = vert ? x : x + driftOffset;
      const wave1 = Math.sin(xOff * band.waveFreq1 + time * band.speed1) * band.waveAmp1;
      const wave2 = Math.sin(xOff * band.waveFreq2 + time * band.speed2) * band.waveAmp2;
      const y = band.baseY + wave1 + wave2 + band.thickness;
      ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.fill();

    // Bright centre line
    ctx.globalAlpha = band.opacity * 0.4;
    ctx.strokeStyle = `rgba(200,220,255,0.6)`;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    for (let x = -step; x <= w + step; x += step) {
      const xOff = vert ? x : x + driftOffset;
      const wave1 = Math.sin(xOff * band.waveFreq1 + time * band.speed1) * band.waveAmp1;
      const wave2 = Math.sin(xOff * band.waveFreq2 + time * band.speed2) * band.waveAmp2;
      const y = band.baseY + wave1 + wave2 + band.thickness * 0.5;
      if (x === -step) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
  }
  ctx.globalAlpha = 1;

  // Ember particles (back layer — small, sparse)
  if (!state.embers || state.embers._w !== w || state.embers._h !== h) {
    const embers = [];
    const backCount = Math.min(Math.floor(cappedArea(w, h) / 10000), 80);
    for (let i = 0; i < backCount; i++) {
      embers.push({
        x: Math.random() * w,
        y: Math.random() * h,
        r: 0.8 + Math.random() * 1.5,
        riseSpeed: 5 + Math.random() * 12,
        wobbleSpeed: 0.3 + Math.random() * 0.8,
        wobbleAmp: 2 + Math.random() * 6,
        wobblePhase: Math.random() * Math.PI * 2,
        opacity: 0.15 + Math.random() * 0.3,
        pulseSpeed: 0.5 + Math.random() * 1.5,
        pulsePhase: Math.random() * Math.PI * 2,
        layer: 'back',
      });
    }
    // Front embers
    const frontCount = Math.min(10, Math.floor(cappedArea(w, h) / 25000));
    for (let i = 0; i < frontCount; i++) {
      embers.push({
        x: Math.random() * w,
        y: Math.random() * h,
        r: 2 + Math.random() * 3,
        riseSpeed: 10 + Math.random() * 18,
        wobbleSpeed: 0.2 + Math.random() * 0.5,
        wobbleAmp: 4 + Math.random() * 10,
        wobblePhase: Math.random() * Math.PI * 2,
        opacity: 0.08 + Math.random() * 0.12,
        pulseSpeed: 0.3 + Math.random() * 1.0,
        pulsePhase: Math.random() * Math.PI * 2,
        layer: 'front',
      });
    }
    embers._w = w;
    embers._h = h;
    state.embers = embers;
  }

  // Draw back embers only (incremental rise — no jump on time reset)
  for (const ember of state.embers) {
    if (ember.layer === 'front') continue;
    if (ember._rise == null) { ember._rise = 0; ember._lastTime = time; }
    const edt = Math.min(time - ember._lastTime, 2.0);
    ember._lastTime = time;
    ember._rise += edt * ember.riseSpeed;
    const wobble = Math.sin(wallTime * ember.wobbleSpeed + ember.wobblePhase) * ember.wobbleAmp;
    const pulse = 0.5 + 0.5 * Math.sin(wallTime * ember.pulseSpeed + ember.pulsePhase);
    const ex = ember.x + wobble;
    const ey = ((ember.y - ember._rise) % (h + ember.r * 4) + h + ember.r * 4) % (h + ember.r * 4) - ember.r * 2;

    ctx.globalAlpha = ember.opacity * pulse;
    // Alternate ember colours between purple, cyan, and magenta
    const emberColors = ['#8040ff', '#30c0ff', '#d040c0'];
    ctx.fillStyle = emberColors[Math.floor(ember.wobblePhase * 10) % 3];
    ctx.beginPath();
    ctx.arc(ex, ey, ember.r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

// ── 12. Forest Canopy ────────────────────────────────────

function forestCanopy(ctx, w, h, time, state, direction, wallTime = time) {
  // Misty sky gradient — light teal at top fading to muted green
  const grad = ctx.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, '#b8d8c8');
  grad.addColorStop(0.3, '#8abca0');
  grad.addColorStop(0.6, '#5a9878');
  grad.addColorStop(1, '#3a7858');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  const sign = getDirSign(direction);

  // Generate pine tree layers — back to front, lighter to darker
  if (!state.treeLayers || state.treeLayers._w !== w || state.treeLayers._h !== h) {
    const layers = [];
    const layerDefs = [
      { baseY: h * 0.55, minH: h * 0.15, maxH: h * 0.25, density: 0.035, color: '#7aaa90', speed: 1 },
      { baseY: h * 0.65, minH: h * 0.18, maxH: h * 0.30, density: 0.030, color: '#5a9070', speed: 2 },
      { baseY: h * 0.75, minH: h * 0.20, maxH: h * 0.35, density: 0.035, color: '#3a7555', speed: 3 },
      { baseY: h * 0.85, minH: h * 0.25, maxH: h * 0.40, density: 0.040, color: '#255840', speed: 4 },
      { baseY: h * 1.08, minH: h * 0.30, maxH: h * 0.50, density: 0.045, color: '#153828', speed: 5 },
    ];

    for (const def of layerDefs) {
      const trees = [];
      const count = Math.max(6, Math.floor(w * def.density));
      const spacing = (w * 1.4) / count;
      for (let i = 0; i < count; i++) {
        const treeH = def.minH + Math.random() * (def.maxH - def.minH);
        const treeW = treeH * (0.4 + Math.random() * 0.2);
        trees.push({
          x: spacing * i - w * 0.2 + (Math.random() - 0.5) * spacing * 0.6,
          h: treeH,
          w: treeW,
          // Pine trees have 3 triangle tiers
          tiers: 3,
        });
      }
      layers.push({ trees, baseY: def.baseY, color: def.color, speed: def.speed, _drift: 0 });
    }
    layers._w = w;
    layers._h = h;
    state.treeLayers = layers;
  }

  // Draw each layer back to front
  for (const layer of state.treeLayers) {
    if (layer._lastTime == null) layer._lastTime = time;
    const ldt = Math.min(time - layer._lastTime, 2.0);
    layer._lastTime = time;
    layer._drift += ldt * layer.speed * sign;

    ctx.fillStyle = layer.color;

    // Fill ground beneath trees
    ctx.fillRect(0, layer.baseY, w, h - layer.baseY + 2);

    // Draw each pine tree
    for (const tree of layer.trees) {
      const tx = ((tree.x + layer._drift) % (w * 1.4) + w * 1.4) % (w * 1.4) - w * 0.2;
      const treeTop = layer.baseY - tree.h;
      const tierH = tree.h / tree.tiers;

      ctx.beginPath();
      // Build pine shape: stacked triangles, each wider than the one above
      for (let t = 0; t < tree.tiers; t++) {
        const tierTop = treeTop + t * tierH * 0.65;
        const tierBottom = tierTop + tierH;
        const tierWidth = tree.w * (0.3 + (t / tree.tiers) * 0.7);

        ctx.moveTo(tx, tierTop);
        ctx.lineTo(tx + tierWidth / 2, tierBottom);
        ctx.lineTo(tx - tierWidth / 2, tierBottom);
        ctx.closePath();
      }

      // Small trunk
      const trunkW = tree.w * 0.08;
      ctx.moveTo(tx - trunkW, layer.baseY);
      ctx.lineTo(tx - trunkW, layer.baseY - tierH * 0.3);
      ctx.lineTo(tx + trunkW, layer.baseY - tierH * 0.3);
      ctx.lineTo(tx + trunkW, layer.baseY);

      ctx.fill();
    }
  }

  // Pollen/dust motes
  if (!state.pollen || state.pollen._w !== w || state.pollen._h !== h) {
    const motes = [];
    const backCount = Math.min(Math.floor(cappedArea(w, h) / 12000), 60);
    for (let i = 0; i < backCount; i++) {
      motes.push({
        x: Math.random() * w,
        y: Math.random() * h * 0.7,
        r: 0.5 + Math.random() * 1.5,
        driftSpeed: 2 + Math.random() * 5,
        floatSpeed: 0.2 + Math.random() * 0.5,
        floatAmp: 3 + Math.random() * 8,
        floatPhase: Math.random() * Math.PI * 2,
        opacity: 0.15 + Math.random() * 0.25,
        layer: 'back',
      });
    }
    const frontCount = Math.min(10, Math.floor(cappedArea(w, h) / 25000));
    for (let i = 0; i < frontCount; i++) {
      motes.push({
        x: Math.random() * w,
        y: Math.random() * h * 0.6,
        r: 1.5 + Math.random() * 3,
        driftSpeed: 4 + Math.random() * 7,
        floatSpeed: 0.15 + Math.random() * 0.4,
        floatAmp: 5 + Math.random() * 12,
        floatPhase: Math.random() * Math.PI * 2,
        opacity: 0.06 + Math.random() * 0.1,
        layer: 'front',
      });
    }
    motes._w = w;
    motes._h = h;
    state.pollen = motes;
  }

  ctx.fillStyle = '#d0e080';
  for (const mote of state.pollen) {
    if (mote.layer === 'front') continue;
    if (mote._drift == null) { mote._drift = 0; mote._lastTime = time; }
    const mdt = Math.min(time - mote._lastTime, 2.0);
    mote._lastTime = time;
    mote._drift += mdt * mote.driftSpeed * (sign || 0.3);
    const float = Math.sin(wallTime * mote.floatSpeed + mote.floatPhase) * mote.floatAmp;
    const wrapW = w + mote.r * 4;
    const mx = ((mote.x + mote._drift) % wrapW + wrapW) % wrapW - mote.r * 2;
    const my = mote.y + float;

    ctx.globalAlpha = mote.opacity;
    ctx.beginPath();
    ctx.arc(mx, my, mote.r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

// ─── Foreground renderers ──────────────────────────────────
// Draw ONLY the front wisp layer on a transparent canvas.
// Shares the same state object as the background renderer.

// 1. Sky & Clouds — Kenney style has no foreground wisps; just clear
function skyClouds_fg(ctx, w, h, time, state, direction, wallTime = time) {
  ctx.clearRect(0, 0, w, h);
}

// 2. Aurora Borealis — crisp floating ice crystal dots (faster than background)
function sunsetGlow_fg(ctx, w, h, time, state, direction, wallTime = time) {
  if (!state.auroraParticles || state.auroraParticles._w !== w || state.auroraParticles._h !== h) {
    const particles = [];
    const count = Math.min(Math.min(Math.floor(cappedArea(w, h) / 8000), 80), 100);
    for (let i = 0; i < count; i++) {
      particles.push({
        x: Math.random() * w,
        y: Math.random() * h,
        r: 1 + Math.random() * 2.5,
        opacity: 0.06 + Math.random() * 0.1,
        driftSpeed: 5 + Math.random() * 8,
        floatSpeed: 0.3 + Math.random() * 0.6,
        floatAmp: 5 + Math.random() * 10,
        floatPhase: Math.random() * Math.PI * 2,
        pulseSpeed: 0.5 + Math.random() * 1.5,
        pulsePhase: Math.random() * Math.PI * 2,
        color: Math.random() > 0.5 ? '#8cffc8' : '#78c8ff',
      });
    }
    particles._w = w;
    particles._h = h;
    state.auroraParticles = particles;
  }

  ctx.clearRect(0, 0, w, h);
  const sign = getDirSign(direction);
  const vert = isVertical(direction);

  for (const p of state.auroraParticles) {
    if (p._drift == null) { p._drift = 0; p._lastTime = time; }
    const pdt = Math.min(time - p._lastTime, 2.0);
    p._lastTime = time;
    p._drift += pdt * p.driftSpeed * sign;
    const float = Math.sin(wallTime * p.floatSpeed + p.floatPhase) * p.floatAmp;
    const pulse = 0.5 + 0.5 * Math.sin(wallTime * p.pulseSpeed + p.pulsePhase);
    const wrapW = w + p.r * 4;
    const wrapH = h + p.r * 4;
    const px = vert ? p.x + float : ((p.x + p._drift) % wrapW + wrapW) % wrapW - p.r * 2;
    const py = vert ? ((p.y + p._drift) % wrapH + wrapH) % wrapH - p.r * 2 : p.y + float;

    ctx.globalAlpha = p.opacity * pulse;
    ctx.fillStyle = p.color;
    ctx.beginPath();
    ctx.arc(px, py, p.r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

// 3. Deep Space — crisp dust dots (faster than background)
function nightStars_fg(ctx, w, h, time, state, direction, wallTime = time) {
  if (!state.spaceDust || state.spaceDust._w !== w || state.spaceDust._h !== h) {
    const dust = [];
    const count = Math.min(Math.min(Math.floor(cappedArea(w, h) / 12000), 60), 80);
    const colors = ['#b48cff', '#8ca0ff', '#ffdcff'];
    for (let i = 0; i < count; i++) {
      dust.push({
        x: Math.random() * w,
        y: Math.random() * h,
        r: 1.5 + Math.random() * 3,
        opacity: 0.05 + Math.random() * 0.08,
        driftSpeed: 4 + Math.random() * 8,
        pulseSpeed: 0.3 + Math.random() * 0.8,
        pulsePhase: Math.random() * Math.PI * 2,
        color: colors[Math.floor(Math.random() * colors.length)],
      });
    }
    dust._w = w;
    dust._h = h;
    state.spaceDust = dust;
  }

  ctx.clearRect(0, 0, w, h);
  const sign = getDirSign(direction);
  const vert = isVertical(direction);

  for (const d of state.spaceDust) {
    if (d._drift == null) { d._drift = 0; d._lastTime = time; }
    const ddt = Math.min(time - d._lastTime, 2.0);
    d._lastTime = time;
    d._drift += ddt * d.driftSpeed * sign;
    const pulse = 0.5 + 0.5 * Math.sin(wallTime * d.pulseSpeed + d.pulsePhase);
    const wrapW = w + d.r * 4;
    const wrapH = h + d.r * 4;
    const dx = vert ? d.x : ((d.x + d._drift) % wrapW + wrapW) % wrapW - d.r * 2;
    const dy = vert ? ((d.y + d._drift) % wrapH + wrapH) % wrapH - d.r * 2 : d.y;

    ctx.globalAlpha = d.opacity * pulse;
    ctx.fillStyle = d.color;
    ctx.beginPath();
    ctx.arc(dx, dy, d.r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

// 4. Bokeh — large foreground rings
function neonGrid_fg(ctx, w, h, time, state, direction, wallTime = time) {
  ctx.clearRect(0, 0, w, h);
  if (!state.bokeh) return;

  const sign = getDirSign(direction);
  const vert = isVertical(direction);

  for (const circle of state.bokeh) {
    if (circle.layer !== 'front') continue;

    if (circle._fgDrift == null) { circle._fgDrift = 0; circle._fgLastTime = time; }
    const fdt = Math.min(time - circle._fgLastTime, 2.0);
    circle._fgLastTime = time;
    circle._fgDrift += fdt * circle.driftSpeed * sign;
    const pulse = 0.7 + 0.3 * Math.sin(wallTime * circle.pulseSpeed + circle.pulsePhase);
    const floatX = Math.sin(wallTime * 0.12 + circle.pulsePhase) * circle.r * 0.3;
    const floatY = Math.cos(wallTime * 0.08 + circle.pulsePhase * 1.3) * circle.r * 0.25;
    const wrapW = w + circle.r * 2;
    const wrapH = h + circle.r * 2;
    const cx = (vert ? circle.x : ((circle.x + circle._fgDrift) % wrapW + wrapW) % wrapW - circle.r) + floatX;
    const cy = (vert ? ((circle.y + circle._fgDrift) % wrapH + wrapH) % wrapH - circle.r : circle.y) + floatY;
    const [cr, cg, cb] = circle.color;

    // Large foreground rings — clean Kenney-style strokes
    ctx.globalAlpha = circle.opacity * pulse;
    ctx.strokeStyle = `rgb(${cr},${cg},${cb})`;
    ctx.lineWidth = Math.max(2, circle.r * 0.06);
    ctx.beginPath();
    ctx.arc(cx, cy, circle.r, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
}

// 5. Underwater — crisp foreground bubbles
function oceanWaves_fg(ctx, w, h, time, state, direction, wallTime = time) {
  ctx.clearRect(0, 0, w, h);
  if (!state.bubbles) return;

  for (const bubble of state.bubbles) {
    if (bubble.layer !== 'front') continue;
    if (bubble._fgRise == null) { bubble._fgRise = 0; bubble._fgLastTime = time; }
    const bdt = Math.min(time - bubble._fgLastTime, 2.0);
    bubble._fgLastTime = time;
    bubble._fgRise += bdt * bubble.riseSpeed;
    const wobble = Math.sin(wallTime * bubble.wobbleSpeed + bubble.wobblePhase) * bubble.wobbleAmp;
    const rise = bubble._fgRise % (h + bubble.r * 4);
    const bx = bubble.x + wobble;
    const by = ((bubble.y - rise) % (h + bubble.r * 4) + h + bubble.r * 4) % (h + bubble.r * 4) - bubble.r * 2;

    // Crisp bubble outline
    ctx.globalAlpha = bubble.opacity;
    ctx.strokeStyle = '#b4f0ff';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(bx, by, bubble.r, 0, Math.PI * 2);
    ctx.stroke();

    // Solid highlight dot
    ctx.globalAlpha = bubble.opacity * 0.6;
    ctx.fillStyle = '#fff';
    ctx.beginPath();
    ctx.arc(bx - bubble.r * 0.3, by - bubble.r * 0.3, bubble.r * 0.15, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

// 6b. Rolling Hills foreground — Kenney style has no foreground wisps; just clear
function rollingHills_fg(ctx, w, h, time, state, direction, wallTime = time) {
  ctx.clearRect(0, 0, w, h);
}

// 8b. Mountain Range foreground — Kenney style has no foreground wisps; just clear
function mountainRange_fg(ctx, w, h, time, state, direction, wallTime = time) {
  ctx.clearRect(0, 0, w, h);
}

// 7. Sunset Horizon — front cloud bands
function sunsetHorizon_fg(ctx, w, h, time, state, direction, wallTime = time) {
  ctx.clearRect(0, 0, w, h);
  if (!state.sunsetBands) return;

  const sign = getDirSign(direction);

  for (const band of state.sunsetBands) {
    if (band.layer !== 'front') continue;
    if (band._drift == null) { band._drift = 0; band._lastTime = time; }
    const dt = Math.min(time - band._lastTime, 2.0);
    band._lastTime = time;
    band._drift += dt * band.speed * (sign || 0.7) * 1.4;
    const bx = ((band.x + band._drift) % (w + band.width + 100) + w + band.width + 100) % (w + band.width + 100) - band.width - 50;
    ctx.globalAlpha = band.opacity * 0.3;
    const yRatio = band.y / (h * 0.6);
    const cr = Math.floor(60 + yRatio * 140);
    const cg = Math.floor(20 + yRatio * 60);
    const cb = Math.floor(40 + yRatio * 20);
    ctx.fillStyle = `rgba(${cr},${cg},${cb},0.6)`;
    const r = band.height / 2;
    ctx.beginPath();
    ctx.moveTo(bx + r, band.y);
    ctx.lineTo(bx + band.width - r, band.y);
    ctx.arc(bx + band.width - r, band.y + r, r, -Math.PI / 2, Math.PI / 2);
    ctx.lineTo(bx + r, band.y + band.height);
    ctx.arc(bx + r, band.y + r, r, Math.PI / 2, -Math.PI / 2);
    ctx.closePath();
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}
// 10. Snowfall — front snowflakes
function snowfall_fg(ctx, w, h, time, state, direction, wallTime = time) {
  ctx.clearRect(0, 0, w, h);
  if (!state.snowflakes) return;

  const sign = getDirSign(direction);

  ctx.fillStyle = '#fff';
  for (const flake of state.snowflakes) {
    if (flake.layer !== 'front') continue;
    // Front flakes use separate drift keys to avoid colliding with back layer
    if (flake._fgDriftX == null) { flake._fgDriftX = 0; flake._fgDriftY = 0; flake._fgLastTime = time; }
    const fdt = Math.min(time - flake._fgLastTime, 2.0);
    flake._fgLastTime = time;
    flake._fgDriftY += fdt * flake.fallSpeed;
    if (sign !== 0) flake._fgDriftX += fdt * 8 * sign;
    const wobble = Math.sin(wallTime * flake.wobbleSpeed + flake.wobblePhase) * flake.wobbleAmp;
    const fx = ((flake.x + wobble + flake._fgDriftX) % (w + flake.r * 4) + w + flake.r * 4) % (w + flake.r * 4) - flake.r * 2;
    const fy = ((flake.y + flake._fgDriftY) % (h + flake.r * 4) + h + flake.r * 4) % (h + flake.r * 4) - flake.r * 2;

    ctx.globalAlpha = flake.opacity;
    ctx.beginPath();
    ctx.arc(fx, fy, flake.r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

// 11. Dark Energy — front embers
function lavaFlow_fg(ctx, w, h, time, state, direction, wallTime = time) {
  ctx.clearRect(0, 0, w, h);
  if (!state.embers) return;

  for (const ember of state.embers) {
    if (ember.layer !== 'front') continue;
    if (ember._fgRise == null) { ember._fgRise = 0; ember._fgLastTime = time; }
    const edt = Math.min(time - ember._fgLastTime, 2.0);
    ember._fgLastTime = time;
    ember._fgRise += edt * ember.riseSpeed;
    const wobble = Math.sin(wallTime * ember.wobbleSpeed + ember.wobblePhase) * ember.wobbleAmp;
    const pulse = 0.5 + 0.5 * Math.sin(wallTime * ember.pulseSpeed + ember.pulsePhase);
    const ex = ember.x + wobble;
    const ey = ((ember.y - ember._fgRise) % (h + ember.r * 4) + h + ember.r * 4) % (h + ember.r * 4) - ember.r * 2;

    ctx.globalAlpha = ember.opacity * pulse;
    const fgColors = ['#a060ff', '#40d0ff', '#e060d0'];
    ctx.fillStyle = fgColors[Math.floor(ember.wobblePhase * 10) % 3];
    ctx.beginPath();
    ctx.arc(ex, ey, ember.r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

// 12. Forest Canopy — front pollen motes + front leaf clusters
function forestCanopy_fg(ctx, w, h, time, state, direction, wallTime = time) {
  ctx.clearRect(0, 0, w, h);
  const sign = getDirSign(direction);

  // Drifting mist banks — wide, flat, semi-transparent bands that float through the trees
  if (!state.fgMist || state.fgMist._w !== w) {
    const banks = [];
    const count = 4 + Math.floor(Math.random() * 3);
    for (let i = 0; i < count; i++) {
      banks.push({
        x: Math.random() * w * 1.5 - w * 0.25,
        y: h * (0.25 + Math.random() * 0.55),
        w: w * (0.4 + Math.random() * 0.5),
        h: h * (0.04 + Math.random() * 0.06),
        opacity: 0.03 + Math.random() * 0.04,
        speed: 1 + Math.random() * 2,
        floatSpeed: 0.05 + Math.random() * 0.08,
        floatAmp: 8 + Math.random() * 12,
        floatPhase: Math.random() * Math.PI * 2,
      });
    }
    banks._w = w;
    state.fgMist = banks;
  }

  for (const bank of state.fgMist) {
    if (bank._drift == null) { bank._drift = 0; bank._lastTime = time; }
    const bdt = Math.min(time - bank._lastTime, 2.0);
    bank._lastTime = time;
    bank._drift += bdt * bank.speed * (sign || 0.3);
    const floatY = Math.sin(wallTime * bank.floatSpeed + bank.floatPhase) * bank.floatAmp;
    const wrapW = w + bank.w + 60;
    const bx = ((bank.x + bank._drift) % wrapW + wrapW) % wrapW - bank.w * 0.3;
    const by = bank.y + floatY;

    ctx.globalAlpha = bank.opacity;
    // Wide soft gradient mist band
    const grad = ctx.createRadialGradient(bx + bank.w / 2, by, 0, bx + bank.w / 2, by, bank.w / 2);
    grad.addColorStop(0, 'rgba(180,210,180,0.5)');
    grad.addColorStop(0.4, 'rgba(170,200,170,0.3)');
    grad.addColorStop(0.7, 'rgba(160,195,165,0.1)');
    grad.addColorStop(1, 'rgba(150,190,160,0)');
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.ellipse(bx + bank.w / 2, by, bank.w / 2, bank.h, 0, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

// ── 13. Coral Depths ──────────────────────────────────────

function coralDepths(ctx, w, h, time, state, direction, wallTime = time) {
  // Mid-ocean reef gradient — warmer and brighter than Underwater
  const grad = ctx.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, '#002030');
  grad.addColorStop(0.3, '#004050');
  grad.addColorStop(0.6, '#005858');
  grad.addColorStop(1, '#006858');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  const sign = getDirSign(direction);
  const vert = isVertical(direction);

  // Warm caustic rays — wider and warmer than Underwater
  if (state._coralRayDrift == null) { state._coralRayDrift = 0; state._coralRayLastTime = time; }
  const rayDt = Math.min(time - state._coralRayLastTime, 2.0);
  state._coralRayLastTime = time;
  if (!vert) state._coralRayDrift += rayDt * 3 * sign;
  const rayCount = 6;
  for (let i = 0; i < rayCount; i++) {
    const baseX = (w / (rayCount - 1)) * i;
    const sway = Math.sin(wallTime * 0.3 + i * 1.5) * w * 0.05;
    const drift = vert ? 0 : state._coralRayDrift;
    const x = ((baseX + sway + drift) % (w + 120) + w + 120) % (w + 120) - 60;
    const rayWidth = w * 0.08 + Math.sin(wallTime * 0.25 + i) * w * 0.02;
    const intensity = 0.025 + 0.012 * Math.sin(wallTime * 0.4 + i * 0.9);
    ctx.globalAlpha = intensity;
    ctx.fillStyle = '#60c8a8';
    ctx.beginPath();
    ctx.moveTo(x - rayWidth * 0.3, 0);
    ctx.lineTo(x + rayWidth * 0.3, 0);
    ctx.lineTo(x + rayWidth * 1.8, h);
    ctx.lineTo(x - rayWidth * 1.8, h);
    ctx.closePath();
    ctx.fill();
  }
  ctx.globalAlpha = 1;

  // Coral silhouettes — overlapping circles forming branching shapes
  if (!state.coralForms || state.coralForms._w !== w || state.coralForms._h !== h) {
    const forms = [];
    // Far corals — bottom 30%
    const farCount = Math.floor(w / 80);
    for (let i = 0; i < farCount; i++) {
      const baseX = (w / farCount) * i + Math.random() * 40 - 20;
      const baseY = h * 0.8 + Math.random() * h * 0.1;
      const blobs = [];
      const blobCount = 3 + Math.floor(Math.random() * 4);
      for (let j = 0; j < blobCount; j++) {
        blobs.push({
          ox: (Math.random() - 0.5) * 25,
          oy: -j * (8 + Math.random() * 6),
          r: 10 + Math.random() * 8,
        });
      }
      forms.push({ x: baseX, y: baseY, blobs, layer: 'far', speed: 2 + Math.random() * 2 });
    }
    // Near corals — bottom 20%
    const nearCount = Math.floor(w / 120);
    for (let i = 0; i < nearCount; i++) {
      const baseX = (w / nearCount) * i + Math.random() * 60 - 30;
      const baseY = h * 0.85 + Math.random() * h * 0.08;
      const blobs = [];
      const blobCount = 4 + Math.floor(Math.random() * 4);
      for (let j = 0; j < blobCount; j++) {
        blobs.push({
          ox: (Math.random() - 0.5) * 30,
          oy: -j * (10 + Math.random() * 8),
          r: 14 + Math.random() * 12,
        });
      }
      forms.push({ x: baseX, y: baseY, blobs, layer: 'near', speed: 4 + Math.random() * 3 });
    }
    forms._w = w;
    forms._h = h;
    state.coralForms = forms;
  }

  // Draw coral silhouettes
  const coralColors = { far: 'rgba(180,70,55,0.2)', near: 'rgba(220,90,70,0.25)' };
  for (const form of state.coralForms) {
    if (form._drift == null) { form._drift = 0; form._lastTime = time; }
    const cdt = Math.min(time - form._lastTime, 2.0);
    form._lastTime = time;
    form._drift += cdt * form.speed * (sign === 0 ? 0.3 : sign);
    const fx = vert ? form.x : ((form.x + form._drift) % (w + 80) + w + 80) % (w + 80) - 40;
    ctx.fillStyle = coralColors[form.layer];
    ctx.beginPath();
    for (const blob of form.blobs) {
      ctx.moveTo(fx + blob.ox + blob.r, form.y + blob.oy);
      ctx.arc(fx + blob.ox, form.y + blob.oy, blob.r, 0, Math.PI * 2);
    }
    ctx.fill();
  }

  // Rising bubbles
  if (!state.bubbles || state.bubbles._w !== w || state.bubbles._h !== h) {
    const bubbles = [];
    const backCount = Math.min(Math.floor(cappedArea(w, h) / 12000), 60);
    const frontCount = Math.min(10, Math.floor(cappedArea(w, h) / 25000));
    for (let i = 0; i < backCount + frontCount; i++) {
      bubbles.push({
        x: Math.random() * w,
        y: Math.random() * h,
        r: 1 + Math.random() * 2.5,
        riseSpeed: 6 + Math.random() * 12,
        wobbleSpeed: 0.5 + Math.random() * 1,
        wobbleAmp: 3 + Math.random() * 5,
        wobblePhase: Math.random() * Math.PI * 2,
        opacity: i < backCount ? 0.08 + Math.random() * 0.1 : 0.06 + Math.random() * 0.08,
        layer: i < backCount ? 'back' : 'front',
      });
    }
    bubbles._w = w;
    bubbles._h = h;
    state.bubbles = bubbles;
  }

  ctx.strokeStyle = '#80D0B0';
  ctx.lineWidth = 0.6;
  for (const b of state.bubbles) {
    if (b.layer === 'front') continue;
    if (b._rise == null) { b._rise = 0; b._lastTime = time; }
    const bdt = Math.min(time - b._lastTime, 2.0);
    b._lastTime = time;
    b._rise += bdt * b.riseSpeed;
    const wobble = Math.sin(wallTime * b.wobbleSpeed + b.wobblePhase) * b.wobbleAmp;
    const bx = b.x + wobble;
    const by = ((b.y - b._rise) % (h + b.r * 4) + h + b.r * 4) % (h + b.r * 4) - b.r * 2;
    ctx.globalAlpha = b.opacity;
    ctx.beginPath();
    ctx.arc(bx, by, b.r, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;

  // Fish particles — elongated ovals darting with sine wobble
  if (!state.fish || state.fish._w !== w || state.fish._h !== h) {
    const fish = [];
    const count = Math.min(12, Math.floor(cappedArea(w, h) / 20000));
    const colors = ['#f0a060', '#e08050', '#f0c070', '#d09060', '#e0b060'];
    for (let i = 0; i < count; i++) {
      fish.push({
        x: Math.random() * w,
        y: h * 0.2 + Math.random() * h * 0.5,
        speed: 8 + Math.random() * 12,
        wobbleSpeed: 0.5 + Math.random() * 1.0,
        wobbleAmp: 3 + Math.random() * 6,
        wobblePhase: Math.random() * Math.PI * 2,
        size: 3 + Math.random() * 4,
        opacity: 0.3 + Math.random() * 0.3,
        color: colors[Math.floor(Math.random() * colors.length)],
        layer: i < 4 ? 'front' : 'back',
      });
    }
    fish._w = w;
    fish._h = h;
    state.fish = fish;
  }

  for (const f of state.fish) {
    if (f.layer === 'front') continue;
    if (f._drift == null) { f._drift = 0; f._lastTime = time; }
    const fdt = Math.min(time - f._lastTime, 2.0);
    f._lastTime = time;
    f._drift += fdt * f.speed * (sign === 0 ? 1 : sign);
    const wobble = Math.sin(wallTime * f.wobbleSpeed + f.wobblePhase) * f.wobbleAmp;
    const wrapW = w + f.size * 8;
    const fx = vert ? f.x : ((f.x + f._drift) % wrapW + wrapW) % wrapW - f.size * 4;
    const fy = f.y + wobble;
    ctx.globalAlpha = f.opacity;
    ctx.fillStyle = f.color;
    ctx.beginPath();
    ctx.ellipse(fx, fy, f.size * 2, f.size, 0, 0, Math.PI * 2);
    ctx.fill();
    // Tiny tail triangle
    const tailDir = (sign === 0 ? 1 : sign) > 0 ? -1 : 1;
    ctx.beginPath();
    ctx.moveTo(fx + tailDir * f.size * 2, fy);
    ctx.lineTo(fx + tailDir * f.size * 3.5, fy - f.size * 0.6);
    ctx.lineTo(fx + tailDir * f.size * 3.5, fy + f.size * 0.6);
    ctx.closePath();
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

// ── 14. Candy Sky ─────────────────────────────────────────

function candySky(ctx, w, h, time, state, direction, wallTime = time) {
  // Warm pastel gradient — cotton candy pink to lavender to baby blue
  const grad = ctx.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, '#FFB0C8');
  grad.addColorStop(0.25, '#FFC8D8');
  grad.addColorStop(0.5, '#E8D0F0');
  grad.addColorStop(0.75, '#D0E0FF');
  grad.addColorStop(1, '#C0E8FF');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  const sign = getDirSign(direction);

  // Kenney-style candy clouds — clean shapes in pink, mint, lilac
  if (!state.candyClouds || state.candyClouds._w !== w || state.candyClouds._h !== h) {
    const colors = ['#F0C0D0', '#C0E8DC', '#D8C8E8', '#F0DCC0'];
    const shadows = ['#E8B0C0', '#B0D8CC', '#C8B8D8', '#E8D0B0'];
    const clouds = [];
    const count = 8;
    for (let i = 0; i < count; i++) {
      const col = i % colors.length;
      clouds.push({
        x: (w / count) * i + Math.random() * (w / count) * 0.5,
        y: h * 0.05 + Math.random() * h * 0.45,
        baseW: 50 + Math.random() * 50,
        scale: 0.7 + Math.random() * 0.6,
        color: colors[col],
        shadow: shadows[col],
        speed: 4,
        layer: 'back',
      });
    }
    clouds._w = w;
    clouds._h = h;
    state.candyClouds = clouds;
  }

  // Draw candy clouds using Kenney bezier shape
  for (const cloud of state.candyClouds) {
    if (cloud._drift == null) { cloud._drift = 0; cloud._lastTime = time; }
    const cdt = Math.min(time - cloud._lastTime, 2.0);
    cloud._lastTime = time;
    cloud._drift += cdt * cloud.speed * (sign || 0.5);
    const wrapW = w + cloud.baseW * cloud.scale + 60;
    const cx = ((cloud.x + cloud._drift) % wrapW + wrapW) % wrapW - cloud.baseW * cloud.scale * 0.3;
    const cy = cloud.y;
    const bw = cloud.baseW * cloud.scale;
    const bh = bw * 0.25;

    const bumps = [{ xRatio: 0.2, r: bw * 0.18 }, { xRatio: 0.5, r: bw * 0.25 }, { xRatio: 0.78, r: bw * 0.16 }];
    // Shadow — very subtle
    ctx.globalAlpha = 0.15;
    ctx.fillStyle = cloud.shadow;
    _drawCloudShape(ctx, cx + 2, cy + 2, bw, bh, bumps);
    // Main cloud — soft and airy
    ctx.globalAlpha = 0.3;
    ctx.fillStyle = cloud.color;
    _drawCloudShape(ctx, cx, cy, bw, bh, bumps);
  }
  ctx.globalAlpha = 1;

  // Candy hills — soft rolling pastel hills at the bottom
  if (!state.candyHills || state.candyHills._w !== w) {
    state.candyHills = [
      { baseY: h * 0.82, amp: h * 0.025, freq: 0.004 + Math.random() * 0.001, phase: Math.random() * Math.PI * 2, color: '#E8C8D8' },
      { baseY: h * 0.90, amp: h * 0.02, freq: 0.006 + Math.random() * 0.001, phase: Math.random() * Math.PI * 2, color: '#E0B8CC' },
      { baseY: h * 0.96, amp: h * 0.015, freq: 0.008 + Math.random() * 0.002, phase: Math.random() * Math.PI * 2, color: '#D8B0C4' },
    ];
    state.candyHills._w = w;
  }

  for (const hill of state.candyHills) {
    ctx.fillStyle = hill.color;
    ctx.beginPath();
    ctx.moveTo(-2, h + 2);
    for (let x = -2; x <= w + 2; x += 4) {
      const y = hill.baseY - hill.amp * (0.5 + 0.5 * Math.sin(x * hill.freq * Math.PI * 2 + hill.phase));
      ctx.lineTo(x, y);
    }
    ctx.lineTo(w + 2, h + 2);
    ctx.closePath();
    ctx.fill();
  }

  // Lollipop decorations on the hills
  if (!state.lollipops || state.lollipops._w !== w) {
    const lollipops = [];
    const colors = ['#F0A0B8', '#A0D8C0', '#C0A8D8', '#F0C8A0', '#A0C0E0'];
    const count = 4 + Math.floor(Math.random() * 3);
    for (let i = 0; i < count; i++) {
      lollipops.push({
        x: (w / count) * i + Math.random() * (w / count) * 0.7,
        stickH: h * (0.02 + Math.random() * 0.015),
        headR: 3 + Math.random() * 3,
        color: colors[Math.floor(Math.random() * colors.length)],
      });
    }
    lollipops._w = w;
    state.lollipops = lollipops;
  }

  for (const lp of state.lollipops) {
    const hillY = state.candyHills[1].baseY - state.candyHills[1].amp *
      (0.5 + 0.5 * Math.sin(lp.x * state.candyHills[1].freq * Math.PI * 2 + state.candyHills[1].phase));
    // Stick
    ctx.strokeStyle = '#e0e0e0';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(lp.x, hillY);
    ctx.lineTo(lp.x, hillY - lp.stickH);
    ctx.stroke();
    // Head — circle with swirl
    ctx.fillStyle = lp.color;
    ctx.beginPath();
    ctx.arc(lp.x, hillY - lp.stickH - lp.headR, lp.headR, 0, Math.PI * 2);
    ctx.fill();
    // White highlight
    ctx.fillStyle = 'rgba(255,255,255,0.4)';
    ctx.beginPath();
    ctx.arc(lp.x - lp.headR * 0.25, hillY - lp.stickH - lp.headR - lp.headR * 0.2, lp.headR * 0.35, 0, Math.PI * 2);
    ctx.fill();
  }

  // Sprinkle particles (back layer) — tiny coloured rectangles falling slowly
  if (!state.sprinkles || state.sprinkles._w !== w || state.sprinkles._h !== h) {
    const sprinkles = [];
    const colors = ['#F0D8A0', '#C0E0F0', '#F0C0D0', '#C8E8C8', '#D8C0E8'];
    const backCount = Math.min(Math.floor(cappedArea(w, h) / 15000), 40);
    const frontCount = Math.min(10, Math.floor(cappedArea(w, h) / 25000));
    for (let i = 0; i < backCount + frontCount; i++) {
      sprinkles.push({
        x: Math.random() * w,
        y: Math.random() * h,
        rotation: Math.random() * Math.PI,
        rotSpeed: 0.2 + Math.random() * 0.3,
        driftSpeed: 2 + Math.random() * 3,
        fallSpeed: 2 + Math.random() * 4,
        wobbleSpeed: 0.3 + Math.random() * 0.6,
        wobbleAmp: 2 + Math.random() * 4,
        wobblePhase: Math.random() * Math.PI * 2,
        opacity: i < backCount ? 0.08 + Math.random() * 0.12 : 0.06 + Math.random() * 0.08,
        color: colors[Math.floor(Math.random() * colors.length)],
        layer: i < backCount ? 'back' : 'front',
      });
    }
    sprinkles._w = w;
    sprinkles._h = h;
    state.sprinkles = sprinkles;
  }

  for (const s of state.sprinkles) {
    if (s.layer === 'front') continue;
    if (s._driftX == null) { s._driftX = 0; s._driftY = 0; s._lastTime = time; }
    const sdt = Math.min(time - s._lastTime, 2.0);
    s._lastTime = time;
    s._driftX += sdt * s.driftSpeed * sign;
    s._driftY += sdt * s.fallSpeed;
    const wobble = Math.sin(wallTime * s.wobbleSpeed + s.wobblePhase) * s.wobbleAmp;
    const wrapW = w + 20;
    const wrapH = h + 20;
    const sx = ((s.x + wobble + s._driftX) % wrapW + wrapW) % wrapW - 10;
    const sy = ((s.y + s._driftY) % wrapH + wrapH) % wrapH - 10;
    const rot = s.rotation + time * s.rotSpeed;
    ctx.globalAlpha = s.opacity;
    ctx.fillStyle = s.color;
    ctx.save();
    ctx.translate(sx, sy);
    ctx.rotate(rot);
    ctx.beginPath();
    ctx.roundRect(-2, -1, 4, 2, 0.8);
    ctx.fill();
    ctx.restore();
  }
  ctx.globalAlpha = 1;
}

// ── 15. Stage Spotlights (was Industrial Smog) ───────────


// ── 16. Zen Garden (was Bamboo Grove) ────────────────────

function bambooGrove(ctx, w, h, time, state, direction, wallTime = time) {
  // Warm sand/stone gradient — peaceful beige tones
  const grad = ctx.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, '#D8D0C0');
  grad.addColorStop(0.3, '#CCC4B4');
  grad.addColorStop(0.6, '#C4BCA8');
  grad.addColorStop(1, '#B8B0A0');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  const sign = getDirSign(direction);

  // Raked sand lines — horizontal wavy lines across the full canvas
  if (!state.sandLines || state.sandLines._w !== w || state.sandLines._h !== h) {
    const lines = [];
    const lineSpacing = 12;
    const lineCount = Math.ceil(h / lineSpacing) + 2;
    for (let i = 0; i < lineCount; i++) {
      lines.push({
        y: i * lineSpacing,
        freq: 0.008 + Math.random() * 0.004,
        amp: 2 + Math.random() * 3,
        phase: Math.random() * Math.PI * 2,
      });
    }
    lines._w = w;
    lines._h = h;
    state.sandLines = lines;
  }

  // Animate sand lines — very slow phase drift for gentle movement
  ctx.strokeStyle = 'rgba(180,172,156,0.5)';
  ctx.lineWidth = 1;
  for (const line of state.sandLines) {
    const animPhase = line.phase + time * 0.08;
    ctx.beginPath();
    for (let x = -2; x <= w + 2; x += 4) {
      const y = line.y + Math.sin(x * line.freq + animPhase) * line.amp;
      if (x === -2) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
  }

  // Floating dust motes — very subtle warm particles
  if (!state.zenDust || state.zenDust._w !== w || state.zenDust._h !== h) {
    const motes = [];
    const count = Math.min(Math.floor(cappedArea(w, h) / 15000), 30);
    for (let i = 0; i < count; i++) {
      motes.push({
        x: Math.random() * w,
        y: Math.random() * h,
        r: 0.5 + Math.random() * 1,
        floatSpeedX: 0.2 + Math.random() * 0.4,
        floatSpeedY: 0.15 + Math.random() * 0.3,
        floatPhaseX: Math.random() * Math.PI * 2,
        floatPhaseY: Math.random() * Math.PI * 2,
        floatAmpX: 8 + Math.random() * 15,
        floatAmpY: 5 + Math.random() * 10,
        opacity: 0.08 + Math.random() * 0.12,
        layer: i < count * 0.7 ? 'back' : 'front',
      });
    }
    motes._w = w;
    motes._h = h;
    state.zenDust = motes;
  }

  ctx.fillStyle = '#A09880';
  for (const mote of state.zenDust) {
    if (mote.layer === 'front') continue;
    const mx = mote.x + Math.sin(wallTime * mote.floatSpeedX + mote.floatPhaseX) * mote.floatAmpX;
    const my = mote.y + Math.sin(wallTime * mote.floatSpeedY + mote.floatPhaseY) * mote.floatAmpY;
    ctx.globalAlpha = mote.opacity;
    ctx.beginPath();
    ctx.arc(mx, my, mote.r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

// ── 17. Terracotta Desert ─────────────────────────────────

function terracottaDesert(ctx, w, h, time, state, direction, wallTime = time) {
  // Kenney-style warm sky gradient — bright and warm
  const grad = ctx.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, '#FFE8D0');
  grad.addColorStop(0.3, '#FFD8B8');
  grad.addColorStop(0.5, '#FFCCA0');
  grad.addColorStop(0.7, '#F0B888');
  grad.addColorStop(1, '#E8A878');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  const sign = getDirSign(direction);

  // Sun glow near horizon
  const sunX = w * 0.6 + Math.sin(wallTime * 0.015) * w * 0.03;
  const sunY = h * 0.35;
  const sunR = Math.min(w, h) * 0.25;
  const sunGlow = ctx.createRadialGradient(sunX, sunY, 0, sunX, sunY, sunR);
  sunGlow.addColorStop(0, 'rgba(255,240,200,0.3)');
  sunGlow.addColorStop(0.4, 'rgba(255,220,160,0.1)');
  sunGlow.addColorStop(1, 'rgba(255,200,140,0)');
  ctx.fillStyle = sunGlow;
  ctx.fillRect(0, 0, w, h);

  // Kenney clouds — warm tinted
  if (!state.desertClouds || state.desertClouds._w !== w || state.desertClouds._h !== h) {
    state.desertClouds = generateKenneyClouds(w, h, 5, 0.05, 0.35);
    state.desertClouds._w = w;
    state.desertClouds._h = h;
  }
  drawKenneyClouds(ctx, w, h, time, state.desertClouds, 'rgba(255,255,255,0.3)', null, 1, direction);

  // Sand dune layers — Kenney-style smooth rolling hills
  if (!state.dunes || state.dunes._w !== w || state.dunes._h !== h) {
    state.dunes = [
      { baseY: h * 0.68, amp: h * 0.03, freq: 0.003 + Math.random() * 0.001, phase: Math.random() * Math.PI * 2, color: '#F0C8A0' },
      { baseY: h * 0.78, amp: h * 0.025, freq: 0.005 + Math.random() * 0.001, phase: Math.random() * Math.PI * 2, color: '#E8B890' },
      { baseY: h * 0.87, amp: h * 0.02, freq: 0.007 + Math.random() * 0.002, phase: Math.random() * Math.PI * 2, color: '#E0A878' },
      { baseY: h * 0.95, amp: h * 0.012, freq: 0.009 + Math.random() * 0.002, phase: Math.random() * Math.PI * 2, color: '#D8A070' },
    ];
    state.dunes._w = w;
    state.dunes._h = h;
  }

  for (const dune of state.dunes) {
    const animPhase = dune.phase + time * 0.03;
    ctx.fillStyle = dune.color;
    ctx.beginPath();
    ctx.moveTo(-2, h + 2);
    for (let x = -2; x <= w + 2; x += 4) {
      const y = dune.baseY + Math.sin(x * dune.freq + animPhase) * dune.amp;
      ctx.lineTo(x, y);
    }
    ctx.lineTo(w + 2, h + 2);
    ctx.closePath();
    ctx.fill();
  }

  // Subtle heat shimmer — very faint rising dust motes
  if (!state.heatShimmer || state.heatShimmer._w !== w || state.heatShimmer._h !== h) {
    const particles = [];
    const count = Math.min(Math.floor(cappedArea(w, h) / 15000), 40);
    for (let i = 0; i < count; i++) {
      particles.push({
        x: Math.random() * w,
        y: h * 0.5 + Math.random() * h * 0.4,
        r: 0.5 + Math.random() * 1,
        riseSpeed: 2 + Math.random() * 4,
        wobbleSpeed: 0.3 + Math.random() * 0.6,
        wobbleAmp: 3 + Math.random() * 5,
        wobblePhase: Math.random() * Math.PI * 2,
        opacity: 0.06 + Math.random() * 0.1,
        layer: i < count * 0.7 ? 'back' : 'front',
      });
    }
    particles._w = w;
    particles._h = h;
    state.heatShimmer = particles;
  }

  ctx.fillStyle = '#D8A070';
  for (const p of state.heatShimmer) {
    if (p.layer === 'front') continue;
    if (p._rise == null) { p._rise = 0; p._lastTime = time; }
    const pdt = Math.min(time - p._lastTime, 2.0);
    p._lastTime = time;
    p._rise += pdt * p.riseSpeed;
    const wobble = Math.sin(wallTime * p.wobbleSpeed + p.wobblePhase) * p.wobbleAmp;
    const px = p.x + wobble;
    const py = ((p.y - p._rise) % (h + p.r * 4) + h + p.r * 4) % (h + p.r * 4) - p.r * 2;
    ctx.globalAlpha = p.opacity;
    ctx.beginPath();
    ctx.arc(px, py, p.r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

// ── 18. Crystal Cavern ────────────────────────────────────


// ── 19. Enchanted Twilight ────────────────────────────────

// ── 19. Stadium Grass (was Enchanted Twilight) ───────────


// ── 20. Steampunk Skyline ─────────────────────────────────

// ── 20. Stadium Atmosphere (was Steampunk Skyline) ───────


// ── 21. Pixel Rain ────────────────────────────────────────

function pixelRain(ctx, w, h, time, state, direction, wallTime = time) {
  // Near-black gradient
  const grad = ctx.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, '#080810');
  grad.addColorStop(0.5, '#0C0C18');
  grad.addColorStop(1, '#101020');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  // Faint grid — 16px cells
  const gridSize = 16;
  ctx.strokeStyle = '#202030';
  ctx.lineWidth = 0.5;
  ctx.globalAlpha = 0.1;
  for (let x = 0; x <= w; x += gridSize) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, h);
    ctx.stroke();
  }
  for (let y = 0; y <= h; y += gridSize) {
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(w, y);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;

  // Scan lines — every 2px, very faint
  ctx.fillStyle = '#FFFFFF';
  ctx.globalAlpha = 0.02;
  for (let y = 0; y < h; y += 4) {
    ctx.fillRect(0, y, w, 1);
  }
  ctx.globalAlpha = 1;

  const sign = getDirSign(direction);
  const vert = isVertical(direction);

  // Pixel rain particles
  if (!state.pixelRain || state.pixelRain._w !== w || state.pixelRain._h !== h) {
    const drops = [];
    const colors = ['#00FFD4', '#FF00FF', '#00FF80'];
    const backCount = Math.min(Math.floor(cappedArea(w, h) / 6000), 100);
    const frontCount = Math.min(8, Math.min(Math.floor(cappedArea(w, h) / 24000), 30));
    for (let i = 0; i < backCount + frontCount; i++) {
      const isBack = i < backCount;
      drops.push({
        x: Math.random() * w,
        y: Math.random() * h,
        width: 3,
        height: isBack ? 6 + Math.random() * 4 : 8 + Math.random() * 6,
        fallSpeed: isBack ? 10 + Math.random() * 15 : 18 + Math.random() * 20,
        opacity: isBack ? 0.15 + Math.random() * 0.25 : 0.1 + Math.random() * 0.15,
        color: colors[Math.floor(Math.random() * colors.length)],
        layer: isBack ? 'back' : 'front',
      });
    }
    drops._w = w;
    drops._h = h;
    state.pixelRain = drops;
  }

  for (const drop of state.pixelRain) {
    if (drop.layer === 'front') continue;
    if (drop._driftX == null) { drop._driftX = 0; drop._driftY = 0; drop._lastTime = time; }
    const ddt = Math.min(time - drop._lastTime, 2.0);
    drop._lastTime = time;
    drop._driftY += ddt * drop.fallSpeed;
    if (sign !== 0) drop._driftX += ddt * 3 * sign;
    const dx = ((drop.x + drop._driftX) % (w + drop.width * 2) + w + drop.width * 2) % (w + drop.width * 2) - drop.width;
    const dy = ((drop.y + drop._driftY) % (h + drop.height * 2) + h + drop.height * 2) % (h + drop.height * 2) - drop.height;
    ctx.globalAlpha = drop.opacity;
    ctx.fillStyle = drop.color;
    ctx.fillRect(dx, dy, drop.width, drop.height);
  }
  ctx.globalAlpha = 1;
}

// ── 22. Golden Palace ─────────────────────────────────────

function goldenPalace(ctx, w, h, time, state, direction, wallTime = time) {
  // Golden-brown gradient (lightened for readability)
  const grad = ctx.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, '#281C10');
  grad.addColorStop(0.4, '#33251A');
  grad.addColorStop(0.7, '#3D2A18');
  grad.addColorStop(1, '#281C10');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  const sign = getDirSign(direction);
  const vert = isVertical(direction);

  // Column silhouettes with arch tops
  if (!state.columns || state.columns._w !== w || state.columns._h !== h) {
    const columns = [];
    // Far columns
    const farCount = Math.floor(w / 50);
    for (let i = 0; i < farCount; i++) {
      columns.push({
        x: (w / farCount) * i + Math.random() * 15 - 7,
        width: 6 + Math.random() * 4,
        archR: 8 + Math.random() * 5,
        layer: 'far', speed: 1.5 + Math.random() * 1.5,
      });
    }
    // Near columns
    const nearCount = Math.floor(w / 80);
    for (let i = 0; i < nearCount; i++) {
      columns.push({
        x: (w / nearCount) * i + Math.random() * 25 - 12,
        width: 10 + Math.random() * 5,
        archR: 12 + Math.random() * 6,
        layer: 'near', speed: 3 + Math.random() * 2,
      });
    }
    columns._w = w;
    columns._h = h;
    state.columns = columns;
  }

  for (const col of state.columns) {
    if (col._drift == null) { col._drift = 0; col._lastTime = time; }
    const cdt = Math.min(time - col._lastTime, 2.0);
    col._lastTime = time;
    col._drift += cdt * col.speed * (sign === 0 ? 0.15 : sign);
    const wrapW = w + col.width + 20;
    const cx = vert ? col.x : ((col.x + col._drift) % wrapW + wrapW) % wrapW - col.width - 10;
    ctx.fillStyle = col.layer === 'far' ? '#1A1410' : '#12100C';
    // Column body
    ctx.fillRect(cx, col.archR + h * 0.05, col.width, h);
    // Arch top — semicircle
    ctx.beginPath();
    ctx.arc(cx + col.width / 2, col.archR + h * 0.05, col.archR, Math.PI, 0);
    ctx.fill();
  }

  // Warm glow spots at column bases
  if (!state.candleGlows || state.candleGlows._w !== w) {
    const glows = [];
    const count = Math.min(5, Math.floor(w / 100));
    for (let i = 0; i < count; i++) {
      glows.push({
        x: w * (0.1 + i * 0.2) + Math.random() * 20 - 10,
        y: h * 0.85 + Math.random() * h * 0.05,
        r: 15 + Math.random() * 10,
        flickerPhase1: Math.random() * Math.PI * 2,
        flickerPhase2: Math.random() * Math.PI * 2,
      });
    }
    glows._w = w;
    state.candleGlows = glows;
  }

  for (const glow of state.candleGlows) {
    const flicker = 0.3 + 0.15 * Math.sin(wallTime * 8 + glow.flickerPhase1) + 0.05 * Math.sin(wallTime * 13 + glow.flickerPhase2);
    ctx.globalAlpha = 0.2 * flicker;
    ctx.fillStyle = '#FFD060';
    ctx.beginPath();
    ctx.arc(glow.x, glow.y, glow.r, 0, Math.PI * 2);
    ctx.fill();
    // Smaller bright core
    ctx.globalAlpha = 0.35 * flicker;
    ctx.beginPath();
    ctx.arc(glow.x, glow.y, glow.r * 0.4, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;

  // Gold dust particles (back layer)
  if (!state.goldDust || state.goldDust._w !== w || state.goldDust._h !== h) {
    const dust = [];
    const backCount = Math.min(Math.floor(cappedArea(w, h) / 10000), 80);
    const frontCount = Math.min(12, Math.floor(cappedArea(w, h) / 20000));
    for (let i = 0; i < backCount + frontCount; i++) {
      dust.push({
        x: Math.random() * w,
        y: Math.random() * h,
        r: 0.8 + Math.random() * 1.5,
        driftSpeed: 1.5 + Math.random() * 3,
        floatSpeed: 0.15 + Math.random() * 0.35,
        floatAmp: 3 + Math.random() * 6,
        floatPhase: Math.random() * Math.PI * 2,
        opacity: 0.1 + Math.random() * 0.2,
        pulseSpeed: 0.5 + Math.random() * 1.0,
        pulsePhase: Math.random() * Math.PI * 2,
        layer: i < backCount ? 'back' : 'front',
      });
    }
    dust._w = w;
    dust._h = h;
    state.goldDust = dust;
  }

  ctx.fillStyle = '#FFE080';
  for (const d of state.goldDust) {
    if (d.layer === 'front') continue;
    if (d._drift == null) { d._drift = 0; d._lastTime = time; }
    const ddt = Math.min(time - d._lastTime, 2.0);
    d._lastTime = time;
    d._drift += ddt * d.driftSpeed * (sign === 0 ? 0.3 : sign);
    const float = Math.sin(wallTime * d.floatSpeed + d.floatPhase) * d.floatAmp;
    const pulse = 0.5 + 0.5 * Math.sin(wallTime * d.pulseSpeed + d.pulsePhase);
    const wrapW = w + d.r * 4;
    const dx = vert ? d.x + float : ((d.x + d._drift) % wrapW + wrapW) % wrapW - d.r * 2;
    const dy = vert ? ((d.y + d._drift) % (h + d.r * 4) + h + d.r * 4) % (h + d.r * 4) - d.r * 2 : d.y + float;
    ctx.globalAlpha = d.opacity * pulse;
    ctx.beginPath();
    ctx.arc(dx, dy, d.r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

// ─── Foreground renderers for new backgrounds ──────────────

// 13. Coral Depths — front fish + sea sparkle
function coralDepths_fg(ctx, w, h, time, state, direction, wallTime = time) {
  ctx.clearRect(0, 0, w, h);

  const sign = getDirSign(direction);
  const vert = isVertical(direction);

  // Front fish
  if (state.fish) {
    for (const f of state.fish) {
      if (f.layer !== 'front') continue;
      if (f._fgDrift == null) { f._fgDrift = 0; f._fgLastTime = time; }
      const fdt = Math.min(time - f._fgLastTime, 2.0);
      f._fgLastTime = time;
      f._fgDrift += fdt * f.speed * 1.5 * (sign === 0 ? 1 : sign);
      const wobble = Math.sin(wallTime * f.wobbleSpeed + f.wobblePhase) * f.wobbleAmp;
      const wrapW = w + f.size * 8;
      const fx = vert ? f.x : ((f.x + f._fgDrift) % wrapW + wrapW) % wrapW - f.size * 4;
      const fy = f.y + wobble;
      ctx.globalAlpha = f.opacity * 0.6;
      ctx.fillStyle = f.color;
      ctx.beginPath();
      ctx.ellipse(fx, fy, f.size * 2, f.size, 0, 0, Math.PI * 2);
      ctx.fill();
      const tailDir = (sign === 0 ? 1 : sign) > 0 ? -1 : 1;
      ctx.beginPath();
      ctx.moveTo(fx + tailDir * f.size * 2, fy);
      ctx.lineTo(fx + tailDir * f.size * 3.5, fy - f.size * 0.6);
      ctx.lineTo(fx + tailDir * f.size * 3.5, fy + f.size * 0.6);
      ctx.closePath();
      ctx.fill();
    }
  }

  // Front bubbles — larger, rising
  if (state.bubbles) {
    ctx.strokeStyle = '#80D0B0';
    ctx.lineWidth = 0.8;
    for (const b of state.bubbles) {
      if (b.layer !== 'front') continue;
      if (b._fgRise == null) { b._fgRise = 0; b._fgLastTime = time; }
      const bdt = Math.min(time - b._fgLastTime, 2.0);
      b._fgLastTime = time;
      b._fgRise += bdt * b.riseSpeed * 1.3;
      const wobble = Math.sin(wallTime * b.wobbleSpeed + b.wobblePhase) * b.wobbleAmp;
      const bx = b.x + wobble;
      const by = ((b.y - b._fgRise) % (h + b.r * 4) + h + b.r * 4) % (h + b.r * 4) - b.r * 2;
      ctx.globalAlpha = b.opacity;
      ctx.beginPath();
      ctx.arc(bx, by, b.r * 1.4, 0, Math.PI * 2);
      ctx.stroke();
    }
  }
  ctx.globalAlpha = 1;
}

// 14. Candy Sky — front clouds + sprinkles
function candySky_fg(ctx, w, h, time, state, direction, wallTime = time) {
  ctx.clearRect(0, 0, w, h);
  if (!state.candyClouds && !state.sprinkles) return;

  const vert = isVertical(direction);
  const dsign = getDirSign(direction);

  // Front sprinkles only — falling + wobbling (no foreground clouds)
  if (state.sprinkles) {
    for (const s of state.sprinkles) {
      if (s.layer !== 'front') continue;
      if (s._fgDriftX == null) { s._fgDriftX = 0; s._fgDriftY = 0; s._fgLastTime = time; }
      const sdt = Math.min(time - s._fgLastTime, 2.0);
      s._fgLastTime = time;
      s._fgDriftX += sdt * s.driftSpeed * 1.5 * dsign;
      s._fgDriftY += sdt * s.fallSpeed * 1.2;
      const wobble = Math.sin(wallTime * s.wobbleSpeed + s.wobblePhase) * s.wobbleAmp;
      const wrapW = w + 20;
      const wrapH = h + 20;
      const sx = ((s.x + wobble + s._fgDriftX) % wrapW + wrapW) % wrapW - 10;
      const sy = ((s.y + s._fgDriftY) % wrapH + wrapH) % wrapH - 10;
      const rot = s.rotation + time * s.rotSpeed;
      ctx.globalAlpha = s.opacity;
      ctx.fillStyle = s.color;
      ctx.save();
      ctx.translate(sx, sy);
      ctx.rotate(rot);
      ctx.beginPath();
      ctx.roundRect(-2.5, -1.2, 5, 2.4, 1);
      ctx.fill();
      ctx.restore();
    }
  }
  ctx.globalAlpha = 1;
}

// 15. Industrial Smog — front sparks

// 16. Zen Garden — front dust motes
function bambooGrove_fg(ctx, w, h, time, state, direction, wallTime = time) {
  ctx.clearRect(0, 0, w, h);
  if (!state.zenDust) return;

  // Front dust motes — larger, softer
  ctx.fillStyle = '#B0A890';
  for (const mote of state.zenDust) {
    if (mote.layer !== 'front') continue;
    const mx = mote.x + Math.sin(wallTime * mote.floatSpeedX * 0.6 + mote.floatPhaseX) * mote.floatAmpX * 1.3;
    const my = mote.y + Math.sin(wallTime * mote.floatSpeedY * 0.6 + mote.floatPhaseY) * mote.floatAmpY * 1.3;
    ctx.globalAlpha = mote.opacity * 0.6;
    ctx.beginPath();
    ctx.arc(mx, my, mote.r * 1.5, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

// 17. Terracotta Desert — front heat shimmer
function terracottaDesert_fg(ctx, w, h, time, state, direction, wallTime = time) {
  ctx.clearRect(0, 0, w, h);
  if (!state.heatShimmer) return;

  ctx.fillStyle = '#D8A070';
  for (const p of state.heatShimmer) {
    if (p.layer !== 'front') continue;
    if (p._fgRise == null) { p._fgRise = 0; p._fgLastTime = time; }
    const pdt = Math.min(time - p._fgLastTime, 2.0);
    p._fgLastTime = time;
    p._fgRise += pdt * p.riseSpeed * 0.8;
    const wobble = Math.sin(wallTime * p.wobbleSpeed + p.wobblePhase) * p.wobbleAmp * 1.3;
    const px = p.x + wobble;
    const py = ((p.y - p._fgRise) % (h + p.r * 4) + h + p.r * 4) % (h + p.r * 4) - p.r * 2;
    ctx.globalAlpha = p.opacity * 0.5;
    ctx.beginPath();
    ctx.arc(px, py, p.r * 1.3, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

// 18. Crystal Cavern — front mineral dust (floating in centre shaft)

// 19. Enchanted Twilight — front fireflies
// 19. Stadium Grass — front grass texture dots

// 20. Steampunk Skyline — front cogs
// 20. Stadium Atmosphere — foreground atmosphere haze

// 21. Pixel Rain — front bright drops
function pixelRain_fg(ctx, w, h, time, state, direction, wallTime = time) {
  ctx.clearRect(0, 0, w, h);
  if (!state.pixelRain) return;

  const sign = getDirSign(direction);

  for (const drop of state.pixelRain) {
    if (drop.layer !== 'front') continue;
    if (drop._fgDriftX == null) { drop._fgDriftX = 0; drop._fgDriftY = 0; drop._fgLastTime = time; }
    const ddt = Math.min(time - drop._fgLastTime, 2.0);
    drop._fgLastTime = time;
    drop._fgDriftY += ddt * drop.fallSpeed;
    if (sign !== 0) drop._fgDriftX += ddt * 5 * sign;
    const dx = ((drop.x + drop._fgDriftX) % (w + drop.width * 2) + w + drop.width * 2) % (w + drop.width * 2) - drop.width;
    const dy = ((drop.y + drop._fgDriftY) % (h + drop.height * 2) + h + drop.height * 2) % (h + drop.height * 2) - drop.height;
    ctx.globalAlpha = drop.opacity;
    ctx.fillStyle = drop.color;
    ctx.fillRect(dx, dy, drop.width + 1, drop.height);
  }
  ctx.globalAlpha = 1;
}

// 22. Golden Palace — front gold dust
function goldenPalace_fg(ctx, w, h, time, state, direction, wallTime = time) {
  ctx.clearRect(0, 0, w, h);
  if (!state.goldDust) return;

  const sign = getDirSign(direction);
  const vert = isVertical(direction);

  ctx.fillStyle = '#FFE080';
  for (const d of state.goldDust) {
    if (d.layer !== 'front') continue;
    if (d._fgDrift == null) { d._fgDrift = 0; d._fgLastTime = time; }
    const ddt = Math.min(time - d._fgLastTime, 2.0);
    d._fgLastTime = time;
    d._fgDrift += ddt * d.driftSpeed * 1.5 * (sign === 0 ? 0.3 : sign);
    const float = Math.sin(wallTime * d.floatSpeed + d.floatPhase) * d.floatAmp;
    const pulse = 0.5 + 0.5 * Math.sin(wallTime * d.pulseSpeed + d.pulsePhase);
    const wrapW = w + d.r * 4;
    const dx = vert ? d.x + float : ((d.x + d._fgDrift) % wrapW + wrapW) % wrapW - d.r * 2;
    const dy = vert ? ((d.y + d._fgDrift) % (h + d.r * 4) + h + d.r * 4) % (h + d.r * 4) - d.r * 2 : d.y + float;
    ctx.globalAlpha = d.opacity * pulse;
    ctx.beginPath();
    ctx.arc(dx, dy, d.r * 1.3, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

// ── 23. Neon District ─────────────────────────────────────

function neonDistrict(ctx, w, h, time, state, direction, wallTime = time) {
  // Deep dark gradient with purple undertone
  const grad = ctx.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, '#06060E');
  grad.addColorStop(0.4, '#0A0A18');
  grad.addColorStop(0.7, '#10101E');
  grad.addColorStop(1, '#0E0816');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  const sign = getDirSign(direction);
  const vert = isVertical(direction);

  // Building silhouettes with lit windows
  if (!state.neonBuildings || state.neonBuildings._w !== w || state.neonBuildings._h !== h) {
    const buildings = [];
    // Far buildings — shorter, more numerous
    const farCount = Math.floor(w / 50);
    for (let i = 0; i < farCount; i++) {
      const bw = 30 + Math.random() * 30;
      const bh = h * (0.12 + Math.random() * 0.18);
      // Generate lit windows
      const windows = [];
      const winCols = Math.floor(bw / 6);
      const winRows = Math.floor(bh / 8);
      for (let r = 0; r < winRows; r++) {
        for (let c = 0; c < winCols; c++) {
          if (Math.random() > 0.5) {
            const winColor = Math.random() > 0.7
              ? (Math.random() > 0.5 ? '#00FFD4' : '#FF00FF')
              : '#FFE8A0';
            windows.push({ xOff: 3 + c * 5, yOff: 4 + r * 7, color: winColor, flicker: Math.random() * Math.PI * 2 });
          }
        }
      }
      buildings.push({
        x: (w / farCount) * i + Math.random() * 10 - 5,
        width: bw, height: bh, windows,
        layer: 'far', speed: 4,
      });
    }
    // Near buildings — taller, wider
    const nearCount = Math.floor(w / 75);
    for (let i = 0; i < nearCount; i++) {
      const bw = 40 + Math.random() * 40;
      const bh = h * (0.2 + Math.random() * 0.25);
      const windows = [];
      const winCols = Math.floor(bw / 7);
      const winRows = Math.floor(bh / 9);
      for (let r = 0; r < winRows; r++) {
        for (let c = 0; c < winCols; c++) {
          if (Math.random() > 0.4) {
            const winColor = Math.random() > 0.6
              ? (Math.random() > 0.5 ? '#00FFD4' : '#FF00FF')
              : '#FFE8A0';
            windows.push({ xOff: 4 + c * 6, yOff: 5 + r * 8, color: winColor, flicker: Math.random() * Math.PI * 2 });
          }
        }
      }
      buildings.push({
        x: (w / nearCount) * i + Math.random() * 15 - 7,
        width: bw, height: bh, windows,
        layer: 'near', speed: 4,
      });
    }
    buildings._w = w;
    buildings._h = h;
    state.neonBuildings = buildings;
  }

  // Neon glow wash — pulsing colour at bottom
  const neonPulse = 0.5 + 0.5 * Math.sin(wallTime * 0.5);
  ctx.globalAlpha = 0.03 * neonPulse;
  ctx.fillStyle = '#FF00FF';
  ctx.fillRect(0, h * 0.6, w, h * 0.4);
  ctx.fillStyle = '#00FFD4';
  ctx.globalAlpha = 0.02 * (1 - neonPulse);
  ctx.fillRect(0, h * 0.7, w, h * 0.3);
  ctx.globalAlpha = 1;

  // Draw buildings
  for (const b of state.neonBuildings) {
    if (b._drift == null) { b._drift = 0; b._lastTime = time; }
    const bdt = Math.min(time - b._lastTime, 2.0);
    b._lastTime = time;
    b._drift += bdt * b.speed * (sign === 0 ? 0.3 : sign);
    const wrapW = w + b.width + 20;
    const bx = vert ? b.x : ((b.x + b._drift) % wrapW + wrapW) % wrapW - b.width - 10;
    const by = h - b.height;
    // Building body
    ctx.fillStyle = b.layer === 'far' ? '#0C0C14' : '#080812';
    ctx.fillRect(bx, by, b.width, b.height);
    // Windows
    for (const win of b.windows) {
      const flicker = 0.6 + 0.4 * Math.sin(wallTime * 0.8 + win.flicker);
      ctx.globalAlpha = (b.layer === 'far' ? 0.4 : 0.6) * flicker;
      ctx.fillStyle = win.color;
      ctx.fillRect(bx + win.xOff, by + win.yOff, 3, 4);
    }
    ctx.globalAlpha = 1;
  }

  // Neon sign glows — scattered coloured circles on building faces
  if (!state.neonSigns || state.neonSigns._w !== w) {
    const signs = [];
    const count = Math.floor(w / 80);
    const colors = ['#00FFD4', '#FF00FF', '#FF4488', '#44AAFF'];
    for (let i = 0; i < count; i++) {
      signs.push({
        x: Math.random() * w,
        y: h * (0.55 + Math.random() * 0.25),
        r: 10 + Math.random() * 15,
        color: colors[Math.floor(Math.random() * colors.length)],
        pulsePhase: Math.random() * Math.PI * 2,
        speed: 4,
      });
    }
    signs._w = w;
    state.neonSigns = signs;
  }

  for (const ns of state.neonSigns) {
    if (ns._drift == null) { ns._drift = 0; ns._lastTime = time; }
    const ndt = Math.min(time - ns._lastTime, 2.0);
    ns._lastTime = time;
    ns._drift += ndt * ns.speed * (sign === 0 ? 0.3 : sign);
    const wrapW = w + ns.r * 4;
    const nx = vert ? ns.x : ((ns.x + ns._drift) % wrapW + wrapW) % wrapW - ns.r * 2;
    const pulse = 0.5 + 0.5 * Math.sin(wallTime * 1.5 + ns.pulsePhase);
    ctx.globalAlpha = 0.06 * pulse;
    ctx.fillStyle = ns.color;
    ctx.beginPath();
    ctx.arc(nx, ns.y, ns.r, 0, Math.PI * 2);
    ctx.fill();
    // Bright core
    ctx.globalAlpha = 0.12 * pulse;
    ctx.beginPath();
    ctx.arc(nx, ns.y, ns.r * 0.3, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;

  // Rain streaks (back layer)
  if (!state.neonRain || state.neonRain._w !== w || state.neonRain._h !== h) {
    const drops = [];
    const backCount = Math.min(Math.floor(cappedArea(w, h) / 4000), 150);
    const frontCount = Math.min(15, Math.min(Math.floor(cappedArea(w, h) / 12000), 60));
    for (let i = 0; i < backCount + frontCount; i++) {
      const isBack = i < backCount;
      drops.push({
        x: Math.random() * w,
        y: Math.random() * h,
        length: isBack ? 6 + Math.random() * 10 : 10 + Math.random() * 16,
        fallSpeed: isBack ? 40 + Math.random() * 50 : 60 + Math.random() * 60,
        windSpeed: isBack ? 4 + Math.random() * 6 : 6 + Math.random() * 8,
        opacity: isBack ? 0.06 + Math.random() * 0.08 : 0.04 + Math.random() * 0.06,
        layer: isBack ? 'back' : 'front',
      });
    }
    drops._w = w;
    drops._h = h;
    state.neonRain = drops;
  }

  ctx.strokeStyle = '#8890B0';
  ctx.lineWidth = 0.5;
  for (const drop of state.neonRain) {
    if (drop.layer === 'front') continue;
    if (drop._driftY == null) { drop._driftY = 0; drop._driftX = 0; drop._lastTime = time; }
    const ddt = Math.min(time - drop._lastTime, 2.0);
    drop._lastTime = time;
    drop._driftY += ddt * drop.fallSpeed;
    drop._driftX += ddt * drop.windSpeed * (sign === 0 ? 0.5 : sign);
    const wrapW = w + 20;
    const dx = ((drop.x + drop._driftX) % wrapW + wrapW) % wrapW - 10;
    const dy = ((drop.y + drop._driftY) % (h + drop.length * 2) + h + drop.length * 2) % (h + drop.length * 2) - drop.length;
    ctx.globalAlpha = drop.opacity;
    ctx.beginPath();
    ctx.moveTo(dx, dy);
    ctx.lineTo(dx - 1, dy + drop.length);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
}

// ── 24. Rooftop Sunset ────────────────────────────────────

function rooftopSunset(ctx, w, h, time, state, direction, wallTime = time) {
  // Warm golden-hour gradient
  const grad = ctx.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, '#1A1020');
  grad.addColorStop(0.15, '#3A1830');
  grad.addColorStop(0.35, '#C06030');
  grad.addColorStop(0.55, '#E8A040');
  grad.addColorStop(0.75, '#F0C860');
  grad.addColorStop(1, '#FFE888');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  const sign = getDirSign(direction);
  const vert = isVertical(direction);

  // Building silhouettes — varied with water towers and antennae
  if (!state.rooftopBuildings || state.rooftopBuildings._w !== w || state.rooftopBuildings._h !== h) {
    const buildings = [];
    // Far skyline
    const farCount = Math.floor(w / 50);
    for (let i = 0; i < farCount; i++) {
      const bw = 30 + Math.random() * 30;
      const bh = h * (0.08 + Math.random() * 0.12);
      buildings.push({
        x: (w / farCount) * i + Math.random() * 8 - 4,
        width: bw, height: bh,
        detail: null,
        layer: 'far', speed: 4,
      });
    }
    // Near skyline with rooftop details
    const nearCount = Math.floor(w / 70);
    for (let i = 0; i < nearCount; i++) {
      const bw = 40 + Math.random() * 40;
      const bh = h * (0.15 + Math.random() * 0.2);
      let detail = null;
      const rnd = Math.random();
      if (rnd > 0.7) {
        // Water tower
        detail = { type: 'tower', w: 8, legH: 8, tankH: 10, xOff: Math.random() * (bw - 12) + 2 };
      } else if (rnd > 0.4) {
        // Antenna
        detail = { type: 'antenna', h: 15 + Math.random() * 20, xOff: Math.random() * (bw - 4) + 2 };
      }
      buildings.push({
        x: (w / nearCount) * i + Math.random() * 15 - 7,
        width: bw, height: bh,
        detail,
        layer: 'near', speed: 4,
      });
    }
    buildings._w = w;
    buildings._h = h;
    state.rooftopBuildings = buildings;
  }

  // Draw far buildings first
  for (const b of state.rooftopBuildings) {
    if (b.layer !== 'far') continue;
    if (b._drift == null) { b._drift = 0; b._lastTime = time; }
    const bdt = Math.min(time - b._lastTime, 2.0);
    b._lastTime = time;
    b._drift += bdt * b.speed * (sign === 0 ? 0.2 : sign);
    const wrapW = w + b.width + 20;
    const bx = vert ? b.x : ((b.x + b._drift) % wrapW + wrapW) % wrapW - b.width - 10;
    const by = h - b.height;
    ctx.fillStyle = '#1A0A14';
    ctx.fillRect(bx, by, b.width, b.height);
  }

  // Warm haze layer between far and near
  ctx.fillStyle = 'rgba(200,120,60,0.04)';
  ctx.fillRect(0, h * 0.7, w, h * 0.3);

  // Draw near buildings
  for (const b of state.rooftopBuildings) {
    if (b.layer !== 'near') continue;
    if (b._drift == null) { b._drift = 0; b._lastTime = time; }
    const bdt = Math.min(time - b._lastTime, 2.0);
    b._lastTime = time;
    b._drift += bdt * b.speed * (sign === 0 ? 0.3 : sign);
    const wrapW = w + b.width + 30;
    const bx = vert ? b.x : ((b.x + b._drift) % wrapW + wrapW) % wrapW - b.width - 15;
    const by = h - b.height;
    ctx.fillStyle = '#0E0608';
    ctx.fillRect(bx, by, b.width, b.height);
    if (b.detail) {
      if (b.detail.type === 'tower') {
        const d = b.detail;
        // Legs
        ctx.fillRect(bx + d.xOff, by - d.legH, 1.5, d.legH);
        ctx.fillRect(bx + d.xOff + d.w - 1.5, by - d.legH, 1.5, d.legH);
        // Tank
        ctx.fillRect(bx + d.xOff - 1, by - d.legH - d.tankH, d.w + 2, d.tankH);
      } else if (b.detail.type === 'antenna') {
        const d = b.detail;
        ctx.fillRect(bx + d.xOff, by - d.h, 1.5, d.h);
        // Blink light
        const blink = Math.sin(wallTime * 3 + bx) > 0.7 ? 0.6 : 0;
        if (blink > 0) {
          ctx.globalAlpha = blink;
          ctx.fillStyle = '#FF3030';
          ctx.beginPath();
          ctx.arc(bx + d.xOff + 0.75, by - d.h, 2, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = '#0E0608';
          ctx.globalAlpha = 1;
        }
      }
    }
  }

  // Birds — small V-shapes drifting across
  if (!state.birds || state.birds._w !== w) {
    const birds = [];
    const count = Math.min(8, Math.floor(w / 60));
    for (let i = 0; i < count; i++) {
      birds.push({
        x: Math.random() * w,
        y: h * (0.15 + Math.random() * 0.35),
        speed: 6 + Math.random() * 8,
        flapSpeed: 2 + Math.random() * 2,
        flapPhase: Math.random() * Math.PI * 2,
        size: 3 + Math.random() * 3,
        layer: i < 5 ? 'back' : 'front',
      });
    }
    birds._w = w;
    state.birds = birds;
  }

  ctx.strokeStyle = '#1A0A10';
  ctx.lineWidth = 1.2;
  for (const bird of state.birds) {
    if (bird.layer === 'front') continue;
    if (bird._drift == null) { bird._drift = 0; bird._lastTime = time; }
    const bdt = Math.min(time - bird._lastTime, 2.0);
    bird._lastTime = time;
    bird._drift += bdt * bird.speed * (sign === 0 ? 0.5 : sign);
    const wrapW = w + bird.size * 8;
    const bx = vert ? bird.x : ((bird.x + bird._drift) % wrapW + wrapW) % wrapW - bird.size * 4;
    const flap = Math.sin(wallTime * bird.flapSpeed + bird.flapPhase) * bird.size * 0.6;
    ctx.globalAlpha = 0.5;
    ctx.beginPath();
    ctx.moveTo(bx - bird.size, bird.y - flap);
    ctx.lineTo(bx, bird.y);
    ctx.lineTo(bx + bird.size, bird.y - flap);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;

  // Warm haze particles (back layer)
  if (!state.hazeParticles || state.hazeParticles._w !== w || state.hazeParticles._h !== h) {
    const particles = [];
    const backCount = Math.min(Math.floor(cappedArea(w, h) / 10000), 80);
    const frontCount = Math.min(10, Math.floor(cappedArea(w, h) / 22000));
    for (let i = 0; i < backCount + frontCount; i++) {
      particles.push({
        x: Math.random() * w,
        y: h * 0.4 + Math.random() * h * 0.5,
        r: 1 + Math.random() * 2,
        driftSpeed: 3 + Math.random() * 5,
        floatSpeed: 0.2 + Math.random() * 0.4,
        floatAmp: 3 + Math.random() * 6,
        floatPhase: Math.random() * Math.PI * 2,
        opacity: 0.08 + Math.random() * 0.12,
        layer: i < backCount ? 'back' : 'front',
      });
    }
    particles._w = w;
    particles._h = h;
    state.hazeParticles = particles;
  }

  ctx.fillStyle = '#F0C060';
  for (const p of state.hazeParticles) {
    if (p.layer === 'front') continue;
    if (p._drift == null) { p._drift = 0; p._lastTime = time; }
    const pdt = Math.min(time - p._lastTime, 2.0);
    p._lastTime = time;
    p._drift += pdt * p.driftSpeed * (sign === 0 ? 0.5 : sign);
    const float = Math.sin(wallTime * p.floatSpeed + p.floatPhase) * p.floatAmp;
    const wrapW = w + p.r * 4;
    const px = vert ? p.x + float : ((p.x + p._drift) % wrapW + wrapW) % wrapW - p.r * 2;
    const py = vert ? ((p.y + p._drift) % (h + p.r * 4) + h + p.r * 4) % (h + p.r * 4) - p.r * 2 : p.y + float;
    ctx.globalAlpha = p.opacity;
    ctx.beginPath();
    ctx.arc(px, py, p.r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

// ── 25. Downtown Night ────────────────────────────────────

function downtownNight(ctx, w, h, time, state, direction, wallTime = time) {
  // Deep blue night sky
  const grad = ctx.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, '#060818');
  grad.addColorStop(0.3, '#0A1028');
  grad.addColorStop(0.6, '#0E1430');
  grad.addColorStop(1, '#0A0E20');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  const sign = getDirSign(direction);
  const vert = isVertical(direction);

  // Stars
  if (!state.dtStars || state.dtStars._w !== w || state.dtStars._h !== h) {
    const stars = [];
    const count = Math.min(Math.floor((cappedArea(w, h) * 0.3) / 1200), 200);
    for (let i = 0; i < count; i++) {
      stars.push({
        x: Math.random() * w,
        y: Math.random() * h * 0.35,
        r: 0.3 + Math.random() * 0.6,
        opacity: 0.2 + Math.random() * 0.3,
        twinkleSpeed: 0.5 + Math.random() * 1.5,
        twinklePhase: Math.random() * Math.PI * 2,
      });
    }
    stars._w = w;
    stars._h = h;
    state.dtStars = stars;
  }

  ctx.fillStyle = '#c8d8ff';
  for (const star of state.dtStars) {
    const twinkle = 0.5 + 0.5 * Math.sin(wallTime * star.twinkleSpeed + star.twinklePhase);
    ctx.globalAlpha = star.opacity * twinkle;
    ctx.beginPath();
    ctx.arc(star.x, star.y, star.r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;

  // Moonlit clouds
  if (!state.dtClouds || state.dtClouds._w !== w || state.dtClouds._h !== h * 0.4) {
    state.dtClouds = generateClouds(w, h * 0.4, 5, 2);
    for (const cloud of state.dtClouds) {
      cloud.opacity = 0.06 + Math.random() * 0.08;
      cloud.speed = 0.03 + Math.random() * 0.06;
    }
    state.dtClouds._w = w;
    state.dtClouds._h = h * 0.4;
  }
  drawClouds(ctx, w, h * 0.4, time, state.dtClouds, '#2A3050', 1.0, direction, 'back');

  // Building silhouettes with flickering windows
  if (!state.dtBuildings || state.dtBuildings._w !== w || state.dtBuildings._h !== h) {
    const buildings = [];
    // Far
    const farCount = Math.floor(w / 50);
    for (let i = 0; i < farCount; i++) {
      const bw = 30 + Math.random() * 25;
      const bh = h * (0.1 + Math.random() * 0.15);
      const windows = [];
      const winCols = Math.floor(bw / 5);
      const winRows = Math.floor(bh / 7);
      for (let r = 0; r < winRows; r++) {
        for (let c = 0; c < winCols; c++) {
          if (Math.random() > 0.55) {
            windows.push({
              xOff: 2 + c * 4, yOff: 3 + r * 6,
              on: true, flickerPhase: Math.random() * Math.PI * 2,
              flickerSpeed: 0.02 + Math.random() * 0.05,
            });
          }
        }
      }
      buildings.push({ x: (w / farCount) * i + Math.random() * 8, width: bw, height: bh, windows, layer: 'far', speed: 4 });
    }
    // Near
    const nearCount = Math.floor(w / 70);
    for (let i = 0; i < nearCount; i++) {
      const bw = 40 + Math.random() * 35;
      const bh = h * (0.18 + Math.random() * 0.22);
      const windows = [];
      const winCols = Math.floor(bw / 6);
      const winRows = Math.floor(bh / 8);
      for (let r = 0; r < winRows; r++) {
        for (let c = 0; c < winCols; c++) {
          if (Math.random() > 0.4) {
            windows.push({
              xOff: 3 + c * 5, yOff: 4 + r * 7,
              on: true, flickerPhase: Math.random() * Math.PI * 2,
              flickerSpeed: 0.01 + Math.random() * 0.03,
            });
          }
        }
      }
      buildings.push({ x: (w / nearCount) * i + Math.random() * 12, width: bw, height: bh, windows, layer: 'near', speed: 4 });
    }
    buildings._w = w;
    buildings._h = h;
    state.dtBuildings = buildings;
  }

  for (const b of state.dtBuildings) {
    if (b._drift == null) { b._drift = 0; b._lastTime = time; }
    const bdt = Math.min(time - b._lastTime, 2.0);
    b._lastTime = time;
    b._drift += bdt * b.speed * (sign === 0 ? 0.2 : sign);
    const wrapW = w + b.width + 20;
    const bx = vert ? b.x : ((b.x + b._drift) % wrapW + wrapW) % wrapW - b.width - 10;
    const by = h - b.height;
    ctx.fillStyle = b.layer === 'far' ? '#0A0E1A' : '#060A14';
    ctx.fillRect(bx, by, b.width, b.height);
    // Windows — warm white, some flicker off/on over time
    for (const win of b.windows) {
      // Slow flicker: occasionally turn off
      const flick = Math.sin(wallTime * win.flickerSpeed + win.flickerPhase);
      const isOn = flick > -0.85; // mostly on, occasionally off
      if (isOn) {
        ctx.globalAlpha = (b.layer === 'far' ? 0.3 : 0.5) * (0.7 + 0.3 * Math.sin(wallTime * 0.5 + win.flickerPhase));
        ctx.fillStyle = '#FFE8C0';
        ctx.fillRect(bx + win.xOff, by + win.yOff, 2.5, 3.5);
      }
    }
    ctx.globalAlpha = 1;
  }

  // Ground-level car headlight streaks
  if (!state.headlights || state.headlights._w !== w) {
    const lights = [];
    const count = Math.min(6, Math.floor(w / 100));
    for (let i = 0; i < count; i++) {
      lights.push({
        x: Math.random() * w,
        y: h * (0.92 + Math.random() * 0.04),
        speed: 15 + Math.random() * 25,
        length: 20 + Math.random() * 30,
        color: Math.random() > 0.4 ? '#FFE8C0' : '#FF4444',
        opacity: 0.08 + Math.random() * 0.06,
        layer: i < 4 ? 'back' : 'front',
      });
    }
    lights._w = w;
    state.headlights = lights;
  }

  for (const light of state.headlights) {
    if (light.layer === 'front') continue;
    if (light._drift == null) { light._drift = 0; light._lastTime = time; }
    const ldt = Math.min(time - light._lastTime, 2.0);
    light._lastTime = time;
    const dir = light.color === '#FF4444' ? -1 : 1;
    light._drift += ldt * light.speed * dir;
    const wrapW = w + light.length * 2;
    const lx = ((light.x + light._drift) % wrapW + wrapW) % wrapW - light.length;
    ctx.globalAlpha = light.opacity;
    ctx.strokeStyle = light.color;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(lx, light.y);
    ctx.lineTo(lx + light.length, light.y);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;

  // Falling atmospheric dust — gentle vertical motion
  if (!state.dtDust || state.dtDust._w !== w || state.dtDust._h !== h) {
    const dust = [];
    const count = Math.min(Math.floor(cappedArea(w, h) / 15000), 60);
    for (let i = 0; i < count; i++) {
      dust.push({
        x: Math.random() * w,
        y: Math.random() * h * 0.8,
        r: 0.5 + Math.random() * 1.5,
        fallSpeed: 3 + Math.random() * 6,
        driftSpeed: 1 + Math.random() * 3,
        wobbleSpeed: 0.3 + Math.random() * 0.5,
        wobbleAmp: 2 + Math.random() * 4,
        wobblePhase: Math.random() * Math.PI * 2,
        opacity: 0.04 + Math.random() * 0.06,
      });
    }
    dust._w = w;
    dust._h = h;
    state.dtDust = dust;
  }

  ctx.fillStyle = '#8090C0';
  for (const d of state.dtDust) {
    if (d._driftX == null) { d._driftX = 0; d._driftY = 0; d._lastTime = time; }
    const ddt = Math.min(time - d._lastTime, 2.0);
    d._lastTime = time;
    d._driftY += ddt * d.fallSpeed;
    d._driftX += ddt * d.driftSpeed * (sign === 0 ? 0.3 : sign);
    const wobble = Math.sin(wallTime * d.wobbleSpeed + d.wobblePhase) * d.wobbleAmp;
    const wrapW = w + 20;
    const wrapH = h * 0.85 + 20;
    const dx = ((d.x + wobble + d._driftX) % wrapW + wrapW) % wrapW - 10;
    const dy = ((d.y + d._driftY) % wrapH + wrapH) % wrapH - 10;
    ctx.globalAlpha = d.opacity;
    ctx.beginPath();
    ctx.arc(dx, dy, d.r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

// ── 26. Harbour Front ─────────────────────────────────────

function harbourFront(ctx, w, h, time, state, direction, wallTime = time) {
  // Teal-slate gradient
  const grad = ctx.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, '#101820');
  grad.addColorStop(0.3, '#182830');
  grad.addColorStop(0.5, '#1A3038');
  grad.addColorStop(0.7, '#1A3038');
  grad.addColorStop(1, '#0E1820');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  const sign = getDirSign(direction);
  const vert = isVertical(direction);

  // Building + crane silhouettes
  if (!state.harbourBuildings || state.harbourBuildings._w !== w || state.harbourBuildings._h !== h) {
    const buildings = [];
    // Far skyline
    const farCount = Math.floor(w / 50);
    for (let i = 0; i < farCount; i++) {
      const bw = 28 + Math.random() * 25;
      const bh = h * (0.06 + Math.random() * 0.1);
      buildings.push({
        x: (w / farCount) * i + Math.random() * 8,
        width: bw, height: bh,
        detail: null,
        layer: 'far', speed: 4,
      });
    }
    // Near waterfront with cranes and warehouses
    const nearCount = Math.floor(w / 80);
    for (let i = 0; i < nearCount; i++) {
      const bw = 35 + Math.random() * 40;
      const bh = h * (0.08 + Math.random() * 0.12);
      let detail = null;
      if (Math.random() > 0.55) {
        // Crane
        detail = { type: 'crane', mast: 30 + Math.random() * 25, arm: 25 + Math.random() * 20, xOff: Math.random() * (bw - 6) + 3 };
      }
      buildings.push({
        x: (w / nearCount) * i + Math.random() * 15,
        width: bw, height: bh,
        detail,
        layer: 'near', speed: 4,
      });
    }
    buildings._w = w;
    buildings._h = h;
    state.harbourBuildings = buildings;
  }

  // Water line — where buildings meet water
  const waterY = h * 0.6;

  // Draw far buildings above waterline
  for (const b of state.harbourBuildings) {
    if (b.layer !== 'far') continue;
    if (b._drift == null) { b._drift = 0; b._lastTime = time; }
    const bdt = Math.min(time - b._lastTime, 2.0);
    b._lastTime = time;
    b._drift += bdt * b.speed * (sign === 0 ? 0.2 : sign);
    const wrapW = w + b.width + 20;
    const bx = vert ? b.x : ((b.x + b._drift) % wrapW + wrapW) % wrapW - b.width - 10;
    const by = waterY - b.height;
    ctx.fillStyle = '#0C1420';
    ctx.fillRect(bx, by, b.width, b.height);
  }

  // Draw near buildings + cranes
  for (const b of state.harbourBuildings) {
    if (b.layer !== 'near') continue;
    if (b._drift == null) { b._drift = 0; b._lastTime = time; }
    const bdt = Math.min(time - b._lastTime, 2.0);
    b._lastTime = time;
    b._drift += bdt * b.speed * (sign === 0 ? 0.3 : sign);
    const wrapW = w + b.width + 30;
    const bx = vert ? b.x : ((b.x + b._drift) % wrapW + wrapW) % wrapW - b.width - 15;
    const by = waterY - b.height;
    ctx.fillStyle = '#081018';
    ctx.fillRect(bx, by, b.width, b.height);
    if (b.detail && b.detail.type === 'crane') {
      const d = b.detail;
      // Mast
      ctx.fillRect(bx + d.xOff, by - d.mast, 2.5, d.mast);
      // Arm — horizontal from top of mast
      ctx.fillRect(bx + d.xOff - d.arm * 0.3, by - d.mast, d.arm, 2);
      // Cable
      ctx.strokeStyle = '#081018';
      ctx.lineWidth = 0.8;
      ctx.beginPath();
      ctx.moveTo(bx + d.xOff + d.arm * 0.6, by - d.mast + 2);
      ctx.lineTo(bx + d.xOff + d.arm * 0.6, by - d.mast + 14);
      ctx.stroke();
    }
  }

  // Water — rippled reflective surface below waterY
  ctx.fillStyle = '#0A1A22';
  ctx.fillRect(0, waterY, w, h - waterY);

  // Water reflections — rippled light streaks
  if (state._harbourRippleDrift == null) { state._harbourRippleDrift = 0; state._harbourRippleLastTime = time; }
  const rippleDt = Math.min(time - state._harbourRippleLastTime, 2.0);
  state._harbourRippleLastTime = time;
  state._harbourRippleDrift += rippleDt * 4 * (sign === 0 ? 0.3 : sign);

  ctx.strokeStyle = '#304850';
  ctx.lineWidth = 0.6;
  const rippleCount = Math.floor((h - waterY) / 8);
  for (let i = 0; i < rippleCount; i++) {
    const ry = waterY + 6 + i * 8;
    const wobble = 3 + i * 0.5;
    ctx.globalAlpha = 0.15 - i * 0.008;
    if (ctx.globalAlpha <= 0) break;
    ctx.beginPath();
    for (let x = 0; x <= w; x += 6) {
      const y = ry + Math.sin((x + state._harbourRippleDrift + i * 12) * 0.02 + time * 0.8) * wobble;
      if (x === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
  }
  ctx.globalAlpha = 1;

  // Reflected lights on water — warm dots that wobble
  if (!state.waterLights || state.waterLights._w !== w) {
    const lights = [];
    const count = Math.floor(w / 50);
    const colors = ['#FFE0A0', '#80C8D0', '#F0A060', '#A0D0E0'];
    for (let i = 0; i < count; i++) {
      lights.push({
        x: Math.random() * w,
        y: waterY + 10 + Math.random() * (h - waterY - 20),
        color: colors[Math.floor(Math.random() * colors.length)],
        size: 2 + Math.random() * 3,
        wobblePhase: Math.random() * Math.PI * 2,
        speed: 2 + Math.random() * 3,
        opacity: 0.06 + Math.random() * 0.08,
        layer: i < count - 3 ? 'back' : 'front',
      });
    }
    lights._w = w;
    state.waterLights = lights;
  }

  for (const light of state.waterLights) {
    if (light.layer === 'front') continue;
    if (light._drift == null) { light._drift = 0; light._lastTime = time; }
    const ldt = Math.min(time - light._lastTime, 2.0);
    light._lastTime = time;
    light._drift += ldt * light.speed * (sign === 0 ? 0.3 : sign);
    const wrapW = w + light.size * 4;
    const lx = vert ? light.x : ((light.x + light._drift) % wrapW + wrapW) % wrapW - light.size * 2;
    const wobY = Math.sin(wallTime * 1.2 + light.wobblePhase) * 2;
    ctx.globalAlpha = light.opacity;
    ctx.fillStyle = light.color;
    // Elongated horizontal reflection
    ctx.beginPath();
    ctx.ellipse(lx, light.y + wobY, light.size * 2.5, light.size * 0.5, 0, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;

  // Seagull particles (back layer)
  if (!state.seagulls || state.seagulls._w !== w) {
    const gulls = [];
    const backCount = Math.min(6, Math.floor(w / 80));
    const frontCount = Math.min(3, Math.floor(w / 150));
    for (let i = 0; i < backCount + frontCount; i++) {
      gulls.push({
        x: Math.random() * w,
        y: h * (0.1 + Math.random() * 0.35),
        speed: 5 + Math.random() * 8,
        flapSpeed: 2.5 + Math.random() * 2,
        flapPhase: Math.random() * Math.PI * 2,
        size: 3 + Math.random() * 3,
        layer: i < backCount ? 'back' : 'front',
      });
    }
    gulls._w = w;
    state.seagulls = gulls;
  }

  ctx.strokeStyle = '#1A2830';
  ctx.lineWidth = 1;
  for (const gull of state.seagulls) {
    if (gull.layer === 'front') continue;
    if (gull._drift == null) { gull._drift = 0; gull._lastTime = time; }
    const gdt = Math.min(time - gull._lastTime, 2.0);
    gull._lastTime = time;
    gull._drift += gdt * gull.speed * (sign === 0 ? 0.5 : sign);
    const wrapW = w + gull.size * 8;
    const gx = vert ? gull.x : ((gull.x + gull._drift) % wrapW + wrapW) % wrapW - gull.size * 4;
    const flap = Math.sin(wallTime * gull.flapSpeed + gull.flapPhase) * gull.size * 0.5;
    ctx.globalAlpha = 0.4;
    ctx.beginPath();
    ctx.moveTo(gx - gull.size, gull.y - flap);
    ctx.lineTo(gx, gull.y);
    ctx.lineTo(gx + gull.size, gull.y - flap);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
}

// ─── Shared cityscape wisp helper ──────────────────────────

function drawCityWisps(ctx, w, h, time, state, sign, key, color, wallTime = time) {
  if (!state[key] || state[key]._w !== w || state[key]._h !== h) {
    const wisps = [];
    const count = Math.min(15, Math.floor(cappedArea(w, h) / 20000));
    for (let i = 0; i < count; i++) {
      wisps.push({
        x: Math.random() * w,
        y: h * (0.1 + Math.random() * 0.7),
        r: 2 + Math.random() * 4,
        speed: 3 + Math.random() * 5,
        floatSpeed: 0.2 + Math.random() * 0.4,
        floatAmp: 4 + Math.random() * 8,
        floatPhase: Math.random() * Math.PI * 2,
        opacity: 0.03 + Math.random() * 0.04,
      });
    }
    wisps._w = w;
    wisps._h = h;
    state[key] = wisps;
  }

  ctx.fillStyle = color;
  for (const p of state[key]) {
    if (p._drift == null) { p._drift = 0; p._lastTime = time; }
    const pdt = Math.min(time - p._lastTime, 2.0);
    p._lastTime = time;
    p._drift += pdt * p.speed * (sign === 0 ? 0.5 : sign);
    const float = Math.sin(wallTime * p.floatSpeed + p.floatPhase) * p.floatAmp;
    const wrapW = w + p.r * 4;
    const px = ((p.x + p._drift) % wrapW + wrapW) % wrapW - p.r * 2;
    ctx.globalAlpha = p.opacity;
    ctx.beginPath();
    ctx.arc(px, p.y + float, p.r, 0, Math.PI * 2);
    ctx.fill();
  }
}

// ─── Foreground renderers for cityscape backgrounds ────────

// 23. Neon District — front rain streaks + wisps
function neonDistrict_fg(ctx, w, h, time, state, direction, wallTime = time) {
  ctx.clearRect(0, 0, w, h);

  const sign = getDirSign(direction);

  if (state.neonRain) {
    ctx.strokeStyle = '#A0A8C8';
    ctx.lineWidth = 0.8;
    for (const drop of state.neonRain) {
      if (drop.layer !== 'front') continue;
      if (drop._fgDriftY == null) { drop._fgDriftY = 0; drop._fgDriftX = 0; drop._fgLastTime = time; }
      const ddt = Math.min(time - drop._fgLastTime, 2.0);
      drop._fgLastTime = time;
      drop._fgDriftY += ddt * drop.fallSpeed;
      drop._fgDriftX += ddt * drop.windSpeed * 1.3 * (sign === 0 ? 0.5 : sign);
      const wrapW = w + 20;
      const dx = ((drop.x + drop._fgDriftX) % wrapW + wrapW) % wrapW - 10;
      const dy = ((drop.y + drop._fgDriftY) % (h + drop.length * 2) + h + drop.length * 2) % (h + drop.length * 2) - drop.length;
      ctx.globalAlpha = drop.opacity;
      ctx.beginPath();
      ctx.moveTo(dx, dy);
      ctx.lineTo(dx - 1.5, dy + drop.length);
      ctx.stroke();
    }
  }

  // Subtle wispy particles
  drawCityWisps(ctx, w, h, time, state, sign, '_neonWisps', '#6070A0', wallTime);
  ctx.globalAlpha = 1;
}

// 24. Rooftop Sunset — front birds + haze
function rooftopSunset_fg(ctx, w, h, time, state, direction, wallTime = time) {
  ctx.clearRect(0, 0, w, h);

  const sign = getDirSign(direction);
  const vert = isVertical(direction);

  // Front birds
  if (state.birds) {
    ctx.strokeStyle = '#1A0A10';
    ctx.lineWidth = 1.5;
    for (const bird of state.birds) {
      if (bird.layer !== 'front') continue;
      if (bird._fgDrift == null) { bird._fgDrift = 0; bird._fgLastTime = time; }
      const bdt = Math.min(time - bird._fgLastTime, 2.0);
      bird._fgLastTime = time;
      bird._fgDrift += bdt * bird.speed * 1.5 * (sign === 0 ? 0.5 : sign);
      const wrapW = w + bird.size * 8;
      const bx = vert ? bird.x : ((bird.x + bird._fgDrift) % wrapW + wrapW) % wrapW - bird.size * 4;
      const flap = Math.sin(wallTime * bird.flapSpeed + bird.flapPhase) * bird.size * 0.6;
      ctx.globalAlpha = 0.6;
      ctx.beginPath();
      ctx.moveTo(bx - bird.size * 1.2, bird.y - flap);
      ctx.lineTo(bx, bird.y);
      ctx.lineTo(bx + bird.size * 1.2, bird.y - flap);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
  }

  // Front haze
  if (state.hazeParticles) {
    ctx.fillStyle = '#F0C060';
    for (const p of state.hazeParticles) {
      if (p.layer !== 'front') continue;
      if (p._fgDrift == null) { p._fgDrift = 0; p._fgLastTime = time; }
      const pdt = Math.min(time - p._fgLastTime, 2.0);
      p._fgLastTime = time;
      p._fgDrift += pdt * p.driftSpeed * 1.5 * (sign === 0 ? 0.5 : sign);
      const float = Math.sin(wallTime * p.floatSpeed + p.floatPhase) * p.floatAmp;
      const wrapW = w + p.r * 4;
      const px = vert ? p.x + float : ((p.x + p._fgDrift) % wrapW + wrapW) % wrapW - p.r * 2;
      const py = vert ? ((p.y + p._fgDrift) % (h + p.r * 4) + h + p.r * 4) % (h + p.r * 4) - p.r * 2 : p.y + float;
      ctx.globalAlpha = p.opacity * 0.7;
      ctx.beginPath();
      ctx.arc(px, py, p.r * 1.3, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  // Subtle wisps
  drawCityWisps(ctx, w, h, time, state, sign, '_rooftopWisps', '#E0B060', wallTime);
  ctx.globalAlpha = 1;
}

// 25. Downtown Night — front headlight streaks + wisps
function downtownNight_fg(ctx, w, h, time, state, direction, wallTime = time) {
  ctx.clearRect(0, 0, w, h);

  const sign = getDirSign(direction);

  // Front clouds
  if (state.dtClouds) {
    drawClouds(ctx, w, h * 0.4, time, state.dtClouds, '#2A3050', 2.0, direction, 'front', 1.2, 0.4);
  }

  // Front headlights
  if (state.headlights) {
    for (const light of state.headlights) {
      if (light.layer !== 'front') continue;
      if (light._fgDrift == null) { light._fgDrift = 0; light._fgLastTime = time; }
      const ldt = Math.min(time - light._fgLastTime, 2.0);
      light._fgLastTime = time;
      const dir = light.color === '#FF4444' ? -1 : 1;
      light._fgDrift += ldt * light.speed * 1.3 * dir;
      const wrapW = w + light.length * 2;
      const lx = ((light.x + light._fgDrift) % wrapW + wrapW) % wrapW - light.length;
      ctx.globalAlpha = light.opacity * 0.8;
      ctx.strokeStyle = light.color;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(lx, light.y);
      ctx.lineTo(lx + light.length, light.y);
      ctx.stroke();
    }
  }

  // Subtle wisps
  drawCityWisps(ctx, w, h, time, state, sign, '_dtWisps', '#4050A0', wallTime);
  ctx.globalAlpha = 1;
}

// 26. Harbour Front — front seagulls + water lights
function harbourFront_fg(ctx, w, h, time, state, direction, wallTime = time) {
  ctx.clearRect(0, 0, w, h);

  const sign = getDirSign(direction);
  const vert = isVertical(direction);

  // Front seagulls
  if (state.seagulls) {
    ctx.strokeStyle = '#1A2830';
    ctx.lineWidth = 1.5;
    for (const gull of state.seagulls) {
      if (gull.layer !== 'front') continue;
      if (gull._fgDrift == null) { gull._fgDrift = 0; gull._fgLastTime = time; }
      const gdt = Math.min(time - gull._fgLastTime, 2.0);
      gull._fgLastTime = time;
      gull._fgDrift += gdt * gull.speed * 1.5 * (sign === 0 ? 0.5 : sign);
      const wrapW = w + gull.size * 8;
      const gx = vert ? gull.x : ((gull.x + gull._fgDrift) % wrapW + wrapW) % wrapW - gull.size * 4;
      const flap = Math.sin(wallTime * gull.flapSpeed + gull.flapPhase) * gull.size * 0.6;
      ctx.globalAlpha = 0.5;
      ctx.beginPath();
      ctx.moveTo(gx - gull.size * 1.3, gull.y - flap);
      ctx.lineTo(gx, gull.y);
      ctx.lineTo(gx + gull.size * 1.3, gull.y - flap);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
  }

  // Front water lights
  if (state.waterLights) {
    for (const light of state.waterLights) {
      if (light.layer !== 'front') continue;
      if (light._fgDrift == null) { light._fgDrift = 0; light._fgLastTime = time; }
      const ldt = Math.min(time - light._fgLastTime, 2.0);
      light._fgLastTime = time;
      light._fgDrift += ldt * light.speed * 1.5 * (sign === 0 ? 0.3 : sign);
      const wrapW = w + light.size * 4;
      const lx = vert ? light.x : ((light.x + light._fgDrift) % wrapW + wrapW) % wrapW - light.size * 2;
      const wobY = Math.sin(wallTime * 1.2 + light.wobblePhase) * 2;
      ctx.globalAlpha = light.opacity * 0.8;
      ctx.fillStyle = light.color;
      ctx.beginPath();
      ctx.ellipse(lx, light.y + wobY, light.size * 3, light.size * 0.6, 0, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  // Subtle wisps
  drawCityWisps(ctx, w, h, time, state, sign, '_harbourWisps', '#406070', wallTime);
  ctx.globalAlpha = 1;
}

// ── 27. Miami Vice ───────────────────────────────────────────

function miamiVice(ctx, w, h, time, state, direction, wallTime = time) {
  // Vibrant sky — bold horizontal bands inspired by Miami Vice / retrowave art
  const grad = ctx.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, '#2E0854');    // deep purple top
  grad.addColorStop(0.2, '#6B1D7B');  // rich magenta
  grad.addColorStop(0.38, '#E8368F'); // hot pink
  grad.addColorStop(0.52, '#FF6B9D'); // bright pink
  grad.addColorStop(0.64, '#FFAA5C'); // warm orange
  grad.addColorStop(0.74, '#FFE156'); // vibrant yellow
  grad.addColorStop(0.82, '#FFD0E0'); // pale pink
  grad.addColorStop(0.88, '#4DE8D0'); // cyan
  grad.addColorStop(1, '#1A9E8F');    // teal
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  const sign = getDirSign(direction);
  const vert = isVertical(direction);
  const HORIZON = 0.88;

  // Large sun disc sitting on the horizon
  const sunR = Math.min(w, h) * 0.22;
  const sunX = w * 0.5;
  const sunY = h * HORIZON;
  const sunGrad = ctx.createRadialGradient(sunX, sunY, 0, sunX, sunY, sunR);
  sunGrad.addColorStop(0, '#FFE88A');
  sunGrad.addColorStop(0.5, '#FFCC55');
  sunGrad.addColorStop(0.8, '#FFAA44');
  sunGrad.addColorStop(0.95, '#FF8844');
  sunGrad.addColorStop(1, 'rgba(255, 136, 68, 0)');
  ctx.fillStyle = sunGrad;
  ctx.beginPath();
  ctx.arc(sunX, sunY, sunR, 0, Math.PI * 2);
  ctx.fill();

  // Horizontal stripe bands across the sun (retrowave effect)
  ctx.save();
  ctx.beginPath();
  ctx.arc(sunX, sunY, sunR, 0, Math.PI * 2);
  ctx.clip();
  ctx.fillStyle = 'rgba(255, 100, 150, 0.25)';
  const stripeCount = 8;
  const stripeH = sunR * 0.06;
  for (let i = 0; i < stripeCount; i++) {
    const yOff = sunY + sunR * 0.1 + i * (sunR * 0.12);
    ctx.fillRect(sunX - sunR, yOff, sunR * 2, stripeH);
  }
  ctx.restore();

  // Building silhouettes
  if (!state.miamiBuildings || state.miamiBuildings._w !== w || state.miamiBuildings._h !== h) {
    const buildings = [];
    const farCount = Math.floor(w / 50);
    for (let i = 0; i < farCount; i++) {
      const bw = 28 + Math.random() * 25;
      const bh = h * (0.06 + Math.random() * 0.12);
      buildings.push({
        x: (w / farCount) * i + Math.random() * 8 - 4,
        width: bw, height: bh,
        layer: 'far', speed: 4,
      });
    }
    const nearCount = Math.floor(w / 70);
    for (let i = 0; i < nearCount; i++) {
      const bw = 38 + Math.random() * 35;
      const bh = h * (0.1 + Math.random() * 0.15);
      buildings.push({
        x: (w / nearCount) * i + Math.random() * 12 - 6,
        width: bw, height: bh,
        layer: 'near', speed: 4,
      });
    }
    buildings._w = w;
    buildings._h = h;
    state.miamiBuildings = buildings;
  }

  const PALM_COLOR = '#1A0540';

  for (const b of state.miamiBuildings) {
    if (b._drift == null) { b._drift = 0; b._lastTime = time; }
    const bdt = Math.min(time - b._lastTime, 2.0);
    b._lastTime = time;
    b._drift += bdt * b.speed * (sign === 0 ? 0.3 : sign);
    const wrapW = w + b.width + 20;
    const bx = vert ? b.x : ((b.x + b._drift) % wrapW + wrapW) % wrapW - b.width - 10;
    const by = h * HORIZON - b.height;
    ctx.fillStyle = PALM_COLOR;
    ctx.fillRect(bx, by, b.width, b.height + h * (1 - HORIZON));
  }

  // Dark ground below horizon
  ctx.fillStyle = PALM_COLOR;
  ctx.fillRect(0, h * HORIZON, w, h * (1 - HORIZON));

  // Palm tree silhouettes — only in gaps between buildings
  if (!state.miamiPalms || state.miamiPalms._w !== w || state.miamiPalms._h !== h) {
    const palms = [];
    // Find gaps between buildings
    const allBuildings = state.miamiBuildings || [];
    const occupied = allBuildings.map(b => ({ left: b.x, right: b.x + b.width }));
    const count = Math.floor(w / 100) + 3;
    for (let i = 0; i < count; i++) {
      const trunkH = h * (0.18 + Math.random() * 0.14);
      const lean = (Math.random() - 0.5) * 25;
      const frondCount = 6 + Math.floor(Math.random() * 3);
      const fronds = [];
      for (let f = 0; f < frondCount; f++) {
        const angle = (Math.PI * 2 / frondCount) * f + (Math.random() - 0.5) * 0.4;
        const length = 22 + Math.random() * 18;
        const droopFactor = 0.25 + Math.random() * 0.35;
        const width = 5 + Math.random() * 4;
        fronds.push({ angle, length, droopFactor, width, swayPhase: Math.random() * Math.PI * 2 });
      }
      // Find an x position that doesn't overlap any building
      let px = Math.random() * w * 1.3 - w * 0.15;
      let attempts = 0;
      while (attempts < 20 && occupied.some(b => px > b.left - 10 && px < b.right + 10)) {
        px = Math.random() * w * 1.3 - w * 0.15;
        attempts++;
      }
      if (attempts >= 20) continue; // skip if no gap found
      palms.push({
        x: px,
        trunkH, lean, fronds,
        trunkWidth: 3 + Math.random() * 2,
        speed: 4,
      });
    }
    palms._w = w;
    palms._h = h;
    state.miamiPalms = palms;
  }

  ctx.fillStyle = PALM_COLOR;
  for (const palm of state.miamiPalms) {
    if (palm._drift == null) { palm._drift = 0; palm._lastTime = time; }
    const pdt = Math.min(time - palm._lastTime, 2.0);
    palm._lastTime = time;
    palm._drift += pdt * palm.speed * (sign === 0 ? 0.2 : sign);
    const wrapW = w + 120;
    const px = vert ? palm.x : ((palm.x + palm._drift) % wrapW + wrapW) % wrapW - 60;

    const baseY = h * (HORIZON + 0.01);
    const topX = px + palm.lean;
    const topY = baseY - palm.trunkH;
    const tw = palm.trunkWidth;

    // Filled trunk — tapered quad from base to top
    ctx.beginPath();
    ctx.moveTo(px - tw * 1.2, baseY);
    ctx.quadraticCurveTo(px - tw * 0.6 + palm.lean * 0.3, baseY - palm.trunkH * 0.5, topX - tw * 0.4, topY);
    ctx.lineTo(topX + tw * 0.4, topY);
    ctx.quadraticCurveTo(px + tw * 0.6 + palm.lean * 0.3, baseY - palm.trunkH * 0.5, px + tw * 1.2, baseY);
    ctx.closePath();
    ctx.fill();

    // Filled frond leaves — teardrop/leaf shapes
    for (const frond of palm.fronds) {
      const sway = Math.sin(wallTime * 0.5 + frond.swayPhase) * 2;
      const a = frond.angle + sway * 0.015;
      const len = frond.length;
      const droop = frond.droopFactor;
      const fw = frond.width;

      // Control points for the frond curve
      const midX = topX + Math.cos(a) * len * 0.5 + sway;
      const midY = topY + Math.sin(a) * len * 0.4;
      const endX = topX + Math.cos(a) * len + sway * 1.5;
      const endY = topY + Math.sin(a) * len * 0.5 + droop * len;

      // Draw filled leaf shape
      ctx.beginPath();
      ctx.moveTo(topX, topY);
      ctx.quadraticCurveTo(
        midX - Math.sin(a) * fw,
        midY + Math.cos(a) * fw,
        endX, endY
      );
      ctx.quadraticCurveTo(
        midX + Math.sin(a) * fw,
        midY - Math.cos(a) * fw,
        topX, topY
      );
      ctx.closePath();
      ctx.fill();
    }
  }
}

// Miami Vice foreground — wisps only (palms are all in background)
function miamiVice_fg(ctx, w, h, time, state, direction, wallTime = time) {
  ctx.clearRect(0, 0, w, h);
  const sign = getDirSign(direction);
  drawCityWisps(ctx, w, h, time, state, sign, '_miamiWisps', '#80305A', wallTime);
  ctx.globalAlpha = 1;
}

// ── 27. Clean Corporate ──────────────────────────────────

function cleanCorporate(ctx, w, h, time, state, direction, wallTime = time) {
  // Dark navy-charcoal gradient — professional, not playful
  const grad = ctx.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, '#141C28');
  grad.addColorStop(0.5, '#1A2438');
  grad.addColorStop(1, '#1E2840');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  // Subtle animated grid — very faint horizontal and vertical lines
  const gridSize = 40;
  const gridDrift = time * 0.5;
  ctx.strokeStyle = 'rgba(100,140,180,0.12)';
  ctx.lineWidth = 0.5;
  for (let x = (gridDrift % gridSize) - gridSize; x <= w + gridSize; x += gridSize) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, h);
    ctx.stroke();
  }
  for (let y = (gridDrift * 0.3 % gridSize) - gridSize; y <= h + gridSize; y += gridSize) {
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(w, y);
    ctx.stroke();
  }

  // Grid intersection dots — subtle pulse at crosspoints
  if (!state.corpDots || state.corpDots._w !== w || state.corpDots._h !== h) {
    const dots = [];
    for (let x = 0; x <= w + gridSize; x += gridSize) {
      for (let y = 0; y <= h + gridSize; y += gridSize) {
        if (Math.random() > 0.3) {
          dots.push({
            x, y,
            pulsePhase: Math.random() * Math.PI * 2,
            pulseSpeed: 0.2 + Math.random() * 0.3,
            opacity: 0.08 + Math.random() * 0.08,
          });
        }
      }
    }
    dots._w = w;
    dots._h = h;
    state.corpDots = dots;
  }

  for (const dot of state.corpDots) {
    const pulse = 0.5 + 0.5 * Math.sin(wallTime * dot.pulseSpeed + dot.pulsePhase);
    ctx.globalAlpha = dot.opacity * pulse;
    ctx.fillStyle = '#6090C0';
    ctx.beginPath();
    ctx.arc(dot.x, dot.y, 1.5, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;

  // Floating particles — very subtle, slow, professional
  if (!state.corpParticles || state.corpParticles._w !== w || state.corpParticles._h !== h) {
    const particles = [];
    const count = Math.min(Math.floor(cappedArea(w, h) / 20000), 25);
    for (let i = 0; i < count; i++) {
      particles.push({
        x: Math.random() * w,
        y: Math.random() * h,
        r: 0.5 + Math.random() * 0.8,
        floatSpeedX: 0.1 + Math.random() * 0.2,
        floatSpeedY: 0.08 + Math.random() * 0.15,
        floatPhaseX: Math.random() * Math.PI * 2,
        floatPhaseY: Math.random() * Math.PI * 2,
        floatAmpX: 8 + Math.random() * 15,
        floatAmpY: 5 + Math.random() * 10,
        opacity: 0.08 + Math.random() * 0.1,
        layer: i < count * 0.7 ? 'back' : 'front',
      });
    }
    particles._w = w;
    particles._h = h;
    state.corpParticles = particles;
  }

  ctx.fillStyle = '#80B0D0';
  for (const p of state.corpParticles) {
    if (p.layer === 'front') continue;
    const mx = p.x + Math.sin(wallTime * p.floatSpeedX + p.floatPhaseX) * p.floatAmpX;
    const my = p.y + Math.sin(wallTime * p.floatSpeedY + p.floatPhaseY) * p.floatAmpY;
    ctx.globalAlpha = p.opacity;
    ctx.beginPath();
    ctx.arc(mx, my, p.r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;

  // Floating geometric shapes — slow drifting outlines
  if (!state.corpShapes || state.corpShapes._w !== w || state.corpShapes._h !== h) {
    const shapes = [];
    const count = 8 + Math.floor(Math.random() * 4);
    for (let i = 0; i < count; i++) {
      shapes.push({
        x: Math.random() * w,
        y: Math.random() * h,
        size: 15 + Math.random() * 40,
        type: ['circle', 'roundedRect', 'hexagon', 'diamond'][Math.floor(Math.random() * 4)],
        rotation: Math.random() * Math.PI * 2,
        rotSpeed: (Math.random() - 0.5) * 0.15,
        floatSpeedX: 0.05 + Math.random() * 0.1,
        floatSpeedY: 0.04 + Math.random() * 0.08,
        floatPhaseX: Math.random() * Math.PI * 2,
        floatPhaseY: Math.random() * Math.PI * 2,
        floatAmpX: 10 + Math.random() * 20,
        floatAmpY: 8 + Math.random() * 15,
        opacity: 0.04 + Math.random() * 0.06,
      });
    }
    shapes._w = w;
    shapes._h = h;
    state.corpShapes = shapes;
  }

  for (const s of state.corpShapes) {
    const mx = s.x + Math.sin(wallTime * s.floatSpeedX + s.floatPhaseX) * s.floatAmpX;
    const my = s.y + Math.sin(wallTime * s.floatSpeedY + s.floatPhaseY) * s.floatAmpY;
    const rot = s.rotation + time * s.rotSpeed;
    ctx.globalAlpha = s.opacity;
    ctx.strokeStyle = '#6090C0';
    ctx.lineWidth = 1;
    ctx.save();
    ctx.translate(mx, my);
    ctx.rotate(rot);
    ctx.beginPath();
    if (s.type === 'circle') {
      ctx.arc(0, 0, s.size * 0.5, 0, Math.PI * 2);
    } else if (s.type === 'roundedRect') {
      const hs = s.size * 0.4;
      ctx.roundRect(-hs, -hs, hs * 2, hs * 2, hs * 0.2);
    } else if (s.type === 'hexagon') {
      for (let i = 0; i < 6; i++) {
        const a = (Math.PI * 2 / 6) * i - Math.PI / 6;
        const r = s.size * 0.45;
        if (i === 0) ctx.moveTo(Math.cos(a) * r, Math.sin(a) * r);
        else ctx.lineTo(Math.cos(a) * r, Math.sin(a) * r);
      }
      ctx.closePath();
    } else {
      const hs = s.size * 0.45;
      ctx.moveTo(0, -hs);
      ctx.lineTo(hs, 0);
      ctx.lineTo(0, hs);
      ctx.lineTo(-hs, 0);
      ctx.closePath();
    }
    ctx.stroke();
    ctx.restore();
  }
  ctx.globalAlpha = 1;

  // Gradient wave — slow animated diagonal band
  const waveX = w * 0.5 + Math.sin(wallTime * 0.08) * w * 0.4;
  const waveGrad = ctx.createLinearGradient(waveX - w * 0.3, 0, waveX + w * 0.3, h);
  waveGrad.addColorStop(0, 'rgba(60,100,160,0)');
  waveGrad.addColorStop(0.5, 'rgba(60,100,160,0.03)');
  waveGrad.addColorStop(1, 'rgba(60,100,160,0)');
  ctx.fillStyle = waveGrad;
  ctx.fillRect(0, 0, w, h);
}

function cleanCorporate_fg(ctx, w, h, time, state, _direction, wallTime = time) {
  ctx.clearRect(0, 0, w, h);
  if (!state.corpParticles) return;
  ctx.fillStyle = '#80B0D0';
  for (const p of state.corpParticles) {
    if (p.layer !== 'front') continue;
    const mx = p.x + Math.sin(wallTime * p.floatSpeedX * 0.7 + p.floatPhaseX) * p.floatAmpX * 1.3;
    const my = p.y + Math.sin(wallTime * p.floatSpeedY * 0.7 + p.floatPhaseY) * p.floatAmpY * 1.3;
    ctx.globalAlpha = p.opacity * 0.5;
    ctx.beginPath();
    ctx.arc(mx, my, p.r * 1.3, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

// ── 28. Clean Corporate Light ─────────────────────────────

function cleanCorporateLight(ctx, w, h, time, state, _direction, wallTime = time) {
  // Light cool grey-blue gradient
  const grad = ctx.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, '#E8EDF4');
  grad.addColorStop(0.5, '#DFE5EE');
  grad.addColorStop(1, '#D4DCE8');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  // Subtle animated grid
  const gridSize = 40;
  const gridDrift = time * 0.4;
  ctx.strokeStyle = 'rgba(160,180,210,0.12)';
  ctx.lineWidth = 0.5;
  for (let x = (gridDrift % gridSize) - gridSize; x <= w + gridSize; x += gridSize) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, h);
    ctx.stroke();
  }
  for (let y = (gridDrift * 0.3 % gridSize) - gridSize; y <= h + gridSize; y += gridSize) {
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(w, y);
    ctx.stroke();
  }

  // Grid intersection dots
  if (!state.corpLightDots || state.corpLightDots._w !== w || state.corpLightDots._h !== h) {
    const dots = [];
    for (let x = 0; x <= w + gridSize; x += gridSize) {
      for (let y = 0; y <= h + gridSize; y += gridSize) {
        if (Math.random() > 0.3) {
          dots.push({
            x, y,
            pulsePhase: Math.random() * Math.PI * 2,
            pulseSpeed: 0.2 + Math.random() * 0.3,
            opacity: 0.06 + Math.random() * 0.06,
          });
        }
      }
    }
    dots._w = w;
    dots._h = h;
    state.corpLightDots = dots;
  }

  for (const dot of state.corpLightDots) {
    const pulse = 0.5 + 0.5 * Math.sin(wallTime * dot.pulseSpeed + dot.pulsePhase);
    ctx.globalAlpha = dot.opacity * pulse;
    ctx.fillStyle = '#8098C0';
    ctx.beginPath();
    ctx.arc(dot.x, dot.y, 1.5, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;

  // Floating particles
  if (!state.corpLightParticles || state.corpLightParticles._w !== w || state.corpLightParticles._h !== h) {
    const particles = [];
    const count = Math.min(Math.floor(cappedArea(w, h) / 20000), 25);
    for (let i = 0; i < count; i++) {
      particles.push({
        x: Math.random() * w,
        y: Math.random() * h,
        r: 0.5 + Math.random() * 0.8,
        floatSpeedX: 0.1 + Math.random() * 0.2,
        floatSpeedY: 0.08 + Math.random() * 0.15,
        floatPhaseX: Math.random() * Math.PI * 2,
        floatPhaseY: Math.random() * Math.PI * 2,
        floatAmpX: 8 + Math.random() * 15,
        floatAmpY: 5 + Math.random() * 10,
        opacity: 0.06 + Math.random() * 0.08,
        layer: i < count * 0.7 ? 'back' : 'front',
      });
    }
    particles._w = w;
    particles._h = h;
    state.corpLightParticles = particles;
  }

  ctx.fillStyle = '#6080B0';
  for (const p of state.corpLightParticles) {
    if (p.layer === 'front') continue;
    const mx = p.x + Math.sin(wallTime * p.floatSpeedX + p.floatPhaseX) * p.floatAmpX;
    const my = p.y + Math.sin(wallTime * p.floatSpeedY + p.floatPhaseY) * p.floatAmpY;
    ctx.globalAlpha = p.opacity;
    ctx.beginPath();
    ctx.arc(mx, my, p.r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;

  // Floating geometric shapes — same as dark but in darker stroke
  if (!state.corpLightShapes || state.corpLightShapes._w !== w || state.corpLightShapes._h !== h) {
    const shapes = [];
    const count = 8 + Math.floor(Math.random() * 4);
    for (let i = 0; i < count; i++) {
      shapes.push({
        x: Math.random() * w,
        y: Math.random() * h,
        size: 15 + Math.random() * 40,
        type: ['circle', 'roundedRect', 'hexagon', 'diamond'][Math.floor(Math.random() * 4)],
        rotation: Math.random() * Math.PI * 2,
        rotSpeed: (Math.random() - 0.5) * 0.15,
        floatSpeedX: 0.05 + Math.random() * 0.1,
        floatSpeedY: 0.04 + Math.random() * 0.08,
        floatPhaseX: Math.random() * Math.PI * 2,
        floatPhaseY: Math.random() * Math.PI * 2,
        floatAmpX: 10 + Math.random() * 20,
        floatAmpY: 8 + Math.random() * 15,
        opacity: 0.06 + Math.random() * 0.08,
      });
    }
    shapes._w = w;
    shapes._h = h;
    state.corpLightShapes = shapes;
  }

  for (const s of state.corpLightShapes) {
    const mx = s.x + Math.sin(wallTime * s.floatSpeedX + s.floatPhaseX) * s.floatAmpX;
    const my = s.y + Math.sin(wallTime * s.floatSpeedY + s.floatPhaseY) * s.floatAmpY;
    const rot = s.rotation + time * s.rotSpeed;
    ctx.globalAlpha = s.opacity;
    ctx.strokeStyle = '#8098C0';
    ctx.lineWidth = 1;
    ctx.save();
    ctx.translate(mx, my);
    ctx.rotate(rot);
    ctx.beginPath();
    if (s.type === 'circle') {
      ctx.arc(0, 0, s.size * 0.5, 0, Math.PI * 2);
    } else if (s.type === 'roundedRect') {
      const hs = s.size * 0.4;
      ctx.roundRect(-hs, -hs, hs * 2, hs * 2, hs * 0.2);
    } else if (s.type === 'hexagon') {
      for (let i = 0; i < 6; i++) {
        const a = (Math.PI * 2 / 6) * i - Math.PI / 6;
        const r = s.size * 0.45;
        if (i === 0) ctx.moveTo(Math.cos(a) * r, Math.sin(a) * r);
        else ctx.lineTo(Math.cos(a) * r, Math.sin(a) * r);
      }
      ctx.closePath();
    } else {
      const hs = s.size * 0.45;
      ctx.moveTo(0, -hs);
      ctx.lineTo(hs, 0);
      ctx.lineTo(0, hs);
      ctx.lineTo(-hs, 0);
      ctx.closePath();
    }
    ctx.stroke();
    ctx.restore();
  }
  ctx.globalAlpha = 1;

  // Gradient wave
  const waveX = w * 0.5 + Math.sin(wallTime * 0.08) * w * 0.4;
  const waveGrad = ctx.createLinearGradient(waveX - w * 0.3, 0, waveX + w * 0.3, h);
  waveGrad.addColorStop(0, 'rgba(100,130,180,0)');
  waveGrad.addColorStop(0.5, 'rgba(100,130,180,0.04)');
  waveGrad.addColorStop(1, 'rgba(100,130,180,0)');
  ctx.fillStyle = waveGrad;
  ctx.fillRect(0, 0, w, h);
}

function cleanCorporateLight_fg(ctx, w, h, time, state, _direction, wallTime = time) {
  ctx.clearRect(0, 0, w, h);
  if (!state.corpLightParticles) return;
  ctx.fillStyle = '#6080B0';
  for (const p of state.corpLightParticles) {
    if (p.layer !== 'front') continue;
    const mx = p.x + Math.sin(wallTime * p.floatSpeedX * 0.7 + p.floatPhaseX) * p.floatAmpX * 1.3;
    const my = p.y + Math.sin(wallTime * p.floatSpeedY * 0.7 + p.floatPhaseY) * p.floatAmpY * 1.3;
    ctx.globalAlpha = p.opacity * 0.5;
    ctx.beginPath();
    ctx.arc(mx, my, p.r * 1.3, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

// ── 29. Warm Interior ────────────────────────────────────

function warmInterior(ctx, w, h, time, state, direction, wallTime = time) {
  // Deep warm gradient — dark wood/bar tones
  const grad = ctx.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, '#1A1210');
  grad.addColorStop(0.4, '#201814');
  grad.addColorStop(0.7, '#281E18');
  grad.addColorStop(1, '#20180F');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  // Warm light pools — amber bokeh circles at various depths
  if (!state.warmLights || state.warmLights._w !== w || state.warmLights._h !== h) {
    const lights = [];
    const count = Math.min(Math.floor(cappedArea(w, h) / 8000), 40);
    const warmColors = ['#FFD080', '#FFBA60', '#FFE0A0', '#FFC870', '#FFB050'];
    for (let i = 0; i < count; i++) {
      lights.push({
        x: Math.random() * w,
        y: Math.random() * h,
        r: 8 + Math.random() * 25,
        color: warmColors[Math.floor(Math.random() * warmColors.length)],
        opacity: 0.03 + Math.random() * 0.05,
        floatSpeedX: 0.05 + Math.random() * 0.1,
        floatSpeedY: 0.04 + Math.random() * 0.08,
        floatPhaseX: Math.random() * Math.PI * 2,
        floatPhaseY: Math.random() * Math.PI * 2,
        floatAmpX: 3 + Math.random() * 8,
        floatAmpY: 2 + Math.random() * 5,
        pulseSpeed: 0.15 + Math.random() * 0.25,
        pulsePhase: Math.random() * Math.PI * 2,
        layer: i < count * 0.7 ? 'back' : 'front',
      });
    }
    lights._w = w;
    lights._h = h;
    state.warmLights = lights;
  }

  // Draw back bokeh lights
  for (const light of state.warmLights) {
    if (light.layer === 'front') continue;
    const mx = light.x + Math.sin(wallTime * light.floatSpeedX + light.floatPhaseX) * light.floatAmpX;
    const my = light.y + Math.sin(wallTime * light.floatSpeedY + light.floatPhaseY) * light.floatAmpY;
    const pulse = 0.6 + 0.4 * Math.sin(wallTime * light.pulseSpeed + light.pulsePhase);
    ctx.globalAlpha = light.opacity * pulse;
    ctx.fillStyle = light.color;
    ctx.beginPath();
    ctx.arc(mx, my, light.r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;

  // Candle-flicker light sources — 3-4 bright warm points
  if (!state.candles || state.candles._w !== w) {
    const candles = [];
    const count = 3 + Math.floor(Math.random() * 2);
    for (let i = 0; i < count; i++) {
      candles.push({
        x: w * (0.15 + (0.7 / (count - 1)) * i) + (Math.random() - 0.5) * w * 0.05,
        y: h * (0.5 + Math.random() * 0.3),
        flickerPhase1: Math.random() * Math.PI * 2,
        flickerPhase2: Math.random() * Math.PI * 2,
      });
    }
    candles._w = w;
    state.candles = candles;
  }

  for (const candle of state.candles) {
    const flicker = 0.6 + 0.2 * Math.sin(wallTime * 6 + candle.flickerPhase1) + 0.1 * Math.sin(wallTime * 11 + candle.flickerPhase2);
    // Outer glow
    const r = Math.min(w, h) * 0.15;
    const glow = ctx.createRadialGradient(candle.x, candle.y, 0, candle.x, candle.y, r);
    glow.addColorStop(0, `rgba(255,200,100,${0.08 * flicker})`);
    glow.addColorStop(0.4, `rgba(255,180,80,${0.03 * flicker})`);
    glow.addColorStop(1, 'rgba(255,160,60,0)');
    ctx.fillStyle = glow;
    ctx.fillRect(candle.x - r, candle.y - r, r * 2, r * 2);
    // Bright core
    ctx.globalAlpha = 0.15 * flicker;
    ctx.fillStyle = '#FFE0A0';
    ctx.beginPath();
    ctx.arc(candle.x, candle.y, 3, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;

  // Subtle warm dust motes
  if (!state.warmDust || state.warmDust._w !== w || state.warmDust._h !== h) {
    const motes = [];
    const count = Math.min(Math.floor(cappedArea(w, h) / 18000), 20);
    for (let i = 0; i < count; i++) {
      motes.push({
        x: Math.random() * w,
        y: Math.random() * h,
        r: 0.4 + Math.random() * 0.6,
        floatSpeedX: 0.1 + Math.random() * 0.15,
        floatSpeedY: 0.08 + Math.random() * 0.12,
        floatPhaseX: Math.random() * Math.PI * 2,
        floatPhaseY: Math.random() * Math.PI * 2,
        floatAmpX: 5 + Math.random() * 10,
        floatAmpY: 4 + Math.random() * 8,
        opacity: 0.04 + Math.random() * 0.06,
      });
    }
    motes._w = w;
    motes._h = h;
    state.warmDust = motes;
  }

  ctx.fillStyle = '#D0A060';
  for (const m of state.warmDust) {
    const mx = m.x + Math.sin(wallTime * m.floatSpeedX + m.floatPhaseX) * m.floatAmpX;
    const my = m.y + Math.sin(wallTime * m.floatSpeedY + m.floatPhaseY) * m.floatAmpY;
    ctx.globalAlpha = m.opacity;
    ctx.beginPath();
    ctx.arc(mx, my, m.r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

function warmInterior_fg(ctx, w, h, time, state, _direction, wallTime = time) {
  ctx.clearRect(0, 0, w, h);
  if (!state.warmLights) return;
  // Front bokeh — larger, softer
  for (const light of state.warmLights) {
    if (light.layer !== 'front') continue;
    const mx = light.x + Math.sin(wallTime * light.floatSpeedX * 0.6 + light.floatPhaseX) * light.floatAmpX * 1.5;
    const my = light.y + Math.sin(wallTime * light.floatSpeedY * 0.6 + light.floatPhaseY) * light.floatAmpY * 1.5;
    const pulse = 0.6 + 0.4 * Math.sin(wallTime * light.pulseSpeed + light.pulsePhase);
    ctx.globalAlpha = light.opacity * pulse * 0.4;
    ctx.fillStyle = light.color;
    ctx.beginPath();
    ctx.arc(mx, my, light.r * 1.5, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

// ── Top-down backgrounds (Lane Dash only) ────────────────
//
// Designed for a top-down viewer scrolling vertically. No horizon, no
// perspective. Tile seamlessly in Y so the scroll loops at any speed.
// Kept understated so brand assets (player, obstacles, collectibles)
// remain the visual centre.

function softDrift(ctx, w, h, time, state, direction, wallTime = time) {
  // Soft pale gradient — slate-blue top fading to a quieter slate
  // bottom. Reads as "calm surface" rather than any specific place.
  if (!state._softGrad || state._softGrad._h !== h) {
    const g = ctx.createLinearGradient(0, 0, 0, h);
    g.addColorStop(0, '#E8EEF5');
    g.addColorStop(1, '#CFD8E3');
    state._softGrad = g;
    state._softGrad._h = h;
  }
  ctx.fillStyle = state._softGrad;
  ctx.fillRect(0, 0, w, h);

  // Two parallax depth layers of soft round dots drifting downward.
  // Far dots: small, faint, slow. Near dots: larger, slightly stronger,
  // faster. Gives a clear sense of motion without dominating the play
  // area visually.
  if (!state.softDots || state.softDots._w !== w || state.softDots._h !== h) {
    const area = w * h;
    const count = Math.min(60, Math.max(24, Math.floor(area / 9000)));
    const dots = [];
    for (let i = 0; i < count; i++) {
      const depth = Math.random() < 0.55 ? 0 : 1;
      dots.push({
        x: Math.random() * w,
        y: Math.random() * h,
        r: depth === 0 ? 1.2 + Math.random() * 1.6 : 2.4 + Math.random() * 3.0,
        depth,
        alpha: depth === 0 ? 0.10 + Math.random() * 0.06 : 0.16 + Math.random() * 0.08,
      });
    }
    dots._w = w;
    dots._h = h;
    state.softDots = dots;
  }

  // Single road-surface speed (no parallax). The runner passes
  // multiplier = speed × 60, so `time × 1.0` matches lane scroll exactly.
  const sign = getDirSign(direction) || 1;
  const off = time * 1.0 * sign;
  for (const d of state.softDots) {
    const y = ((d.y + off) % h + h) % h;
    ctx.fillStyle = `rgba(255,255,255,${d.alpha})`;
    ctx.beginPath();
    ctx.arc(d.x, y, d.r, 0, Math.PI * 2);
    ctx.fill();
  }
}

function fairwayStrip(ctx, w, h, time, state, direction, wallTime = time) {
  // Pale spring-green base
  if (!state._fwGrad || state._fwGrad._h !== h) {
    const g = ctx.createLinearGradient(0, 0, 0, h);
    g.addColorStop(0, '#A6D77A');
    g.addColorStop(1, '#7CC25A');
    state._fwGrad = g;
    state._fwGrad._h = h;
  }
  ctx.fillStyle = state._fwGrad;
  ctx.fillRect(0, 0, w, h);

  // Mower-stripe bands. All elements on this surface scroll at
  // exactly lane speed (coeff 1.0) — no parallax.
  const sign = getDirSign(direction) || 1;
  const bandH = Math.max(60, h / 6);
  const offset = ((time * 1.0 * sign) % (bandH * 2) + bandH * 2) % (bandH * 2);
  for (let i = -2; i < Math.ceil(h / bandH) + 2; i++) {
    const y = i * bandH + offset;
    const lighter = (i % 2 === 0);
    ctx.fillStyle = lighter ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 60, 20, 0.07)';
    ctx.fillRect(0, y, w, bandH);
  }

  // Sparse subtle blade flecks — sells the texture as turf without
  // looking busy.
  if (!state.fwFlecks || state.fwFlecks._w !== w || state.fwFlecks._h !== h) {
    const area = w * h;
    const count = Math.min(160, Math.max(60, Math.floor(area / 4000)));
    const flecks = [];
    for (let i = 0; i < count; i++) {
      flecks.push({
        x: Math.random() * w,
        y: Math.random() * h,
        len: 2 + Math.random() * 3,
        tone: Math.random() < 0.5 ? 'rgba(255,255,255,0.18)' : 'rgba(0,50,18,0.18)',
      });
    }
    flecks._w = w;
    flecks._h = h;
    state.fwFlecks = flecks;
  }
  const fleckOff = time * 1.0 * sign;
  for (const f of state.fwFlecks) {
    const y = ((f.y + fleckOff) % h + h) % h;
    ctx.fillStyle = f.tone;
    ctx.fillRect(f.x, y, 1, f.len);
  }
}

function waterChannel(ctx, w, h, time, state, direction, wallTime = time) {
  // Calm blue-teal water gradient
  if (!state._waterGrad || state._waterGrad._h !== h) {
    const g = ctx.createLinearGradient(0, 0, 0, h);
    g.addColorStop(0, '#9FD3DC');
    g.addColorStop(1, '#5FA8C2');
    state._waterGrad = g;
    state._waterGrad._h = h;
  }
  ctx.fillStyle = state._waterGrad;
  ctx.fillRect(0, 0, w, h);

  // Soft ripple bands scrolling downward — wide, low-opacity curves
  // that read as water surface texture, not waves.
  const sign = getDirSign(direction) || 1;
  if (!state.ripples || state.ripples._w !== w || state.ripples._h !== h) {
    const ripples = [];
    const count = 6;
    for (let i = 0; i < count; i++) {
      ripples.push({
        baseY: (i / count) * h,
        amp: 6 + Math.random() * 10,
        freq: 0.012 + Math.random() * 0.01,
        phase: Math.random() * Math.PI * 2,
        alpha: 0.06 + Math.random() * 0.05,
      });
    }
    ripples._w = w;
    ripples._h = h;
    state.ripples = ripples;
  }
  const off = time * 1.0 * sign;
  ctx.lineWidth = 1.2;
  for (const r of state.ripples) {
    const y = ((r.baseY + off) % h + h) % h;
    ctx.strokeStyle = `rgba(255,255,255,${r.alpha})`;
    ctx.beginPath();
    for (let x = 0; x <= w; x += 8) {
      const wy = y + Math.sin(x * r.freq + r.phase + time * 0.08) * r.amp;
      if (x === 0) ctx.moveTo(x, wy); else ctx.lineTo(x, wy);
    }
    ctx.stroke();
  }

  // Pale glints drifting down — sun reflections.
  if (!state.glints || state.glints._w !== w || state.glints._h !== h) {
    const area = w * h;
    const count = Math.min(28, Math.max(10, Math.floor(area / 22000)));
    const glints = [];
    for (let i = 0; i < count; i++) {
      glints.push({
        x: Math.random() * w,
        y: Math.random() * h,
        r: 1.5 + Math.random() * 2.2,
        alpha: 0.18 + Math.random() * 0.10,
      });
    }
    glints._w = w;
    glints._h = h;
    state.glints = glints;
  }
  const glintOff = time * 1.0 * sign;
  for (const g of state.glints) {
    const y = ((g.y + glintOff) % h + h) % h;
    ctx.fillStyle = `rgba(255,255,255,${g.alpha})`;
    ctx.beginPath();
    ctx.arc(g.x, y, g.r, 0, Math.PI * 2);
    ctx.fill();
  }
}

function skyCloudsTopDown(ctx, w, h, time, state, direction, wallTime = time) {
  // Match the original Sky & Clouds gradient exactly.
  ctx.fillStyle = cachedVGradient(ctx, h, state, 'bg', [
    [0, '#FFFFFF'], [0.4, '#EEF6FC'], [0.7, '#DDEFFF'], [1, '#C3E3FF'],
  ]);
  ctx.fillRect(0, 0, w, h);

  // Generate the same Kenney-style clouds the original uses, but lay
  // them out across a tall virtual strip (2× canvas) so we can scroll
  // them vertically with seamless wrap.
  if (!state.tdClouds || state.tdClouds._w !== w || state.tdClouds._h !== h) {
    const stripH = h * 2;
    state.tdClouds = generateKenneyClouds(w, stripH, 18, 0.02, 0.98);
    state.tdClouds._w = w;
    state.tdClouds._h = h;
    state.tdClouds._strip = stripH;
  }

  const sign = getDirSign(direction) || 1;
  const stripH = state.tdClouds._strip;
  const drift = time * 1.0 * sign;
  for (const c of state.tdClouds) {
    // Wrap each cloud across the strip so they loop seamlessly.
    const yWrapped = ((c.y + drift) % stripH + stripH) % stripH - c.baseH;
    // Skip clouds outside the visible band (small perf win).
    if (yWrapped > h + 40 || yWrapped + c.baseH < -40) continue;

    // Shadow pass (same tone the original uses on the back layer).
    ctx.globalAlpha = 0.12;
    ctx.fillStyle = '#C8DFF0';
    _drawCloudShape(ctx, c.x, yWrapped + c.baseH * 0.15, c.baseW, c.baseH, c.bumps);

    // Cloud body.
    ctx.globalAlpha = 1;
    ctx.fillStyle = '#FFFFFF';
    _drawCloudShape(ctx, c.x, yWrapped, c.baseW, c.baseH, c.bumps);
  }
}

// ── Sky Journey ──────────────────────────────────────────────
// Vertical-only background that doesn't tile. `time` is treated as altitude
// (it's the cameraY delta in Sky Bounce, scroll offset in Lane Dash). Bands:
//   altitude 0 – 300:   ground visible at viewport bottom, sky above
//   altitude 300 – 3000: pure cloud band
//   altitude 3000 – 8000: clouds thin, sky darkens (dusk → twilight)
//   altitude 8000+:     deep space with stars
// Cloud/star positions are seeded once at fixed world coordinates so the
// player "flies past" them — they don't reshuffle as you climb.
function skyJourney(ctx, w, h, time, state, _direction) {
  const altBottom = time;
  const altTop = time + h;

  // Colour gradient stops by altitude. RGB tuples interpolated linearly.
  // Matches Sky & Clouds' palette at the start (white top, soft blue bottom),
  // stays white through the cloud band, then deepens into sky/space above.
  const STOPS = [
    [0,    [195, 227, 255]], // #C3E3FF — matches skyCloudsTopDown bottom stop
    [200,  [221, 239, 255]], // #DDEFFF — matches skyCloudsTopDown 70% stop
    [400,  [238, 246, 252]], // #EEF6FC — matches skyCloudsTopDown 40% stop
    [700,  [255, 255, 255]], // #FFFFFF — top of skyCloudsTopDown, "in the clouds"
    [2500, [255, 255, 255]], // hold white through the cloud band
    [3500, [180, 214, 240]], // clear sky above the clouds
    [4500, [124, 184, 232]], // deeper daytime blue
    [6000, [58,  111, 165]], // higher atmosphere
    [7500, [26,  51,  112]], // dusk
    [9000, [10,  20,  40]],  // twilight / edge of space
    [11000,[0,   0,   16]],  // deep space
  ];
  const colourAt = (alt) => {
    if (alt <= STOPS[0][0]) return STOPS[0][1];
    if (alt >= STOPS[STOPS.length - 1][0]) return STOPS[STOPS.length - 1][1];
    for (let i = 0; i < STOPS.length - 1; i++) {
      const a = STOPS[i], b = STOPS[i + 1];
      if (alt >= a[0] && alt < b[0]) {
        const t = (alt - a[0]) / (b[0] - a[0]);
        return [
          a[1][0] + (b[1][0] - a[1][0]) * t,
          a[1][1] + (b[1][1] - a[1][1]) * t,
          a[1][2] + (b[1][2] - a[1][2]) * t,
        ];
      }
    }
    return STOPS[0][1];
  };
  const rgb = (c) => `rgb(${Math.round(c[0])},${Math.round(c[1])},${Math.round(c[2])})`;

  // Vertical gradient across the viewport's altitude span.
  const grad = ctx.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, rgb(colourAt(altTop)));    // top of viewport = highest altitude
  grad.addColorStop(1, rgb(colourAt(altBottom))); // bottom of viewport = lowest altitude
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  // worldY → screenY helper (screen y=0 at top, y=h at bottom)
  const screenYFor = (worldY) => h - (worldY - altBottom);

  // ── Clouds — Kenney style, same look as Sky & Clouds bgs ──
  // generateKenneyClouds lays clouds across a virtual strip with grid
  // spacing + shadow pass. We treat the strip's Y axis as world altitude
  // offset, so cloud.y == worldY - CLOUD_ALT_MIN. Generated once.
  const CLOUD_ALT_MIN = 300;
  const CLOUD_ALT_MAX = 4500;
  const CLOUD_STRIP = CLOUD_ALT_MAX - CLOUD_ALT_MIN;
  if (!state.kenneyClouds || state.kenneyClouds._w !== w) {
    // Higher count because the strip is much taller than a normal viewport.
    state.kenneyClouds = generateKenneyClouds(w, CLOUD_STRIP, 28, 0, 1);
    state.kenneyClouds._w = w;
  }
  for (const c of state.kenneyClouds) {
    const worldY = CLOUD_ALT_MIN + c.y;
    const screenY = screenYFor(worldY);
    if (screenY < -c.baseH - 20 || screenY > h + c.baseH + 20) continue;
    // Fade out in the upper transition band so space looks clear.
    let opacity = 1;
    if (worldY > 3500) opacity = Math.max(0, 1 - (worldY - 3500) / 1000);
    if (opacity <= 0.02) continue;

    // Shadow pass (same tone Sky & Clouds uses).
    ctx.globalAlpha = 0.12 * opacity;
    ctx.fillStyle = '#C8DFF0';
    _drawCloudShape(ctx, c.x, screenY + c.baseH * 0.15, c.baseW, c.baseH, c.bumps);

    // Cloud body.
    ctx.globalAlpha = opacity;
    ctx.fillStyle = '#FFFFFF';
    _drawCloudShape(ctx, c.x, screenY, c.baseW, c.baseH, c.bumps);
  }
  ctx.globalAlpha = 1;

  // ── Stars (loop infinitely above STAR_ALT_MIN) ────────────
  // Stars are seeded once with worldY in [0, STAR_BAND), then for each
  // viewport we figure out which band(s) overlap and offset accordingly.
  // STAR_BAND > viewport height, so at most two bands ever overlap.
  const STAR_ALT_MIN = 6000;
  const STAR_BAND = 5000;
  if (altTop > STAR_ALT_MIN) {
    if (!state.stars || state.stars._w !== w) {
      const stars = [];
      const count = 180;
      let seed = 0x57A25;
      const rnd = () => { seed = (seed * 1664525 + 1013904223) | 0; return ((seed >>> 0) / 0x100000000); };
      for (let i = 0; i < count; i++) {
        stars.push({
          x: rnd() * w,
          bandY: rnd() * STAR_BAND, // position within one band
          size: 0.5 + rnd() * 1.6,
          tw: rnd() * Math.PI * 2,
        });
      }
      stars._w = w;
      state.stars = stars;
    }
    // Which band offsets overlap the visible altitude range?
    const visBottom = Math.max(altBottom, STAR_ALT_MIN);
    const bandStart = Math.floor((visBottom - STAR_ALT_MIN) / STAR_BAND);
    const bandEnd   = Math.floor((altTop - STAR_ALT_MIN) / STAR_BAND);
    for (let band = bandStart; band <= bandEnd; band++) {
      const bandOffset = STAR_ALT_MIN + band * STAR_BAND;
      for (const s of state.stars) {
        const worldY = bandOffset + s.bandY;
        const screenY = screenYFor(worldY);
        if (screenY < -4 || screenY > h + 4) continue;
        // Fade in across the first 1500 altitude above STAR_ALT_MIN so stars
        // don't pop on at the threshold. Above that, fully visible forever.
        const fadeIn = Math.min(1, Math.max(0, (worldY - STAR_ALT_MIN) / 1500));
        const twinkle = 0.55 + 0.45 * Math.sin(time * 0.002 + s.tw);
        ctx.globalAlpha = fadeIn * twinkle;
        ctx.fillStyle = '#FFFFFF';
        ctx.beginPath();
        ctx.arc(s.x, screenY, s.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    ctx.globalAlpha = 1;
  }
}

// ── Magma Rise (vertical streams against the void) ───────────
// Deep-void background with vertical magma streams that snake gently
// top-to-bottom. Each stream's path is a function of WORLD Y only — no
// time-driven wave — so when the camera moves, you simply see different
// parts of the same fixed stream. No jittery shimmer.
function magmaRise(ctx, w, h, time, state, _direction) {
  // Deep void gradient
  const grad = ctx.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, '#04000a');
  grad.addColorStop(0.3, '#08001a');
  grad.addColorStop(0.5, '#0a0020');
  grad.addColorStop(0.7, '#0c0018');
  grad.addColorStop(1, '#06000f');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  // Slow ambient pulse on the void (opacity only — no movement)
  const pulse = 0.025 * Math.sin(time * 0.0008);
  ctx.fillStyle = `rgba(80,20,160,${Math.max(0, pulse)})`;
  ctx.fillRect(0, 0, w, h);

  const altBottom = time;

  // Seed streams once. Each stream has a fixed baseX (horizontal anchor) and
  // wave parameters that depend only on worldY → wave is locked to the world.
  if (!state.magmaStreams || state.magmaStreams._w !== w) {
    const palette = [
      { r: 255, g: 120, b: 40  }, // hot orange
      { r: 120, g: 40,  b: 255 }, // deep purple
      { r: 255, g: 50,  b: 120 }, // hot pink
      { r: 40,  g: 180, b: 255 }, // cyan accent
      { r: 255, g: 200, b: 60  }, // amber
    ];
    const streams = [];
    const count = 5;
    let seed = 0x4A60A;
    const rnd = () => { seed = (seed * 1664525 + 1013904223) | 0; return ((seed >>> 0) / 0x100000000); };
    for (let i = 0; i < count; i++) {
      // Distribute baseX across the canvas so streams don't clump.
      const baseX = ((i + 0.5) / count) * w + (rnd() - 0.5) * (w / count) * 0.5;
      streams.push({
        baseX,
        thickness: 6 + rnd() * 6,
        color: palette[i % palette.length],
        waveFreq1: 0.0015 + rnd() * 0.0012, // wavelength in world Y units
        waveFreq2: 0.004  + rnd() * 0.002,
        waveAmp1: 26 + rnd() * 22,
        waveAmp2: 6  + rnd() * 8,
        phase: rnd() * Math.PI * 2,
        // Slow, gentle pulse (alpha only, no position movement)
        pulseSpeed: 0.4 + rnd() * 0.3,
        pulsePhase: rnd() * Math.PI * 2,
        // Tiny lateral sway — each stream drifts a few px in a slow sine.
        // Independent speed/phase per stream so they don't move in sync.
        swaySpeed: 0.0006 + rnd() * 0.0005,
        swayPhase: rnd() * Math.PI * 2,
        swayAmp: 8 + rnd() * 8, // px peak-to-peak roughly
      });
    }
    streams._w = w;
    state.magmaStreams = streams;
  }

  const step = 8; // vertical sample step (px)
  for (const s of state.magmaStreams) {
    const { r: cr, g: cg, b: cb } = s.color;
    // Brightness pulse — gentle, decoupled from position so the stream
    // never jitters horizontally. Time is in seconds-ish (scroll offset).
    const pulse = 0.7 + 0.3 * Math.sin(time * 0.001 * s.pulseSpeed + s.pulsePhase);

    // Wave shape is anchored to WORLD Y so the stream is locked in space —
    // camera just reveals different vertical slices. A small time-based
    // sway adds gentle life without making the streams shimmy.
    const sway = Math.sin(time * s.swaySpeed + s.swayPhase) * s.swayAmp;
    const xAt = (worldY) => {
      const wave1 = Math.sin(worldY * s.waveFreq1 + s.phase) * s.waveAmp1;
      const wave2 = Math.sin(worldY * s.waveFreq2 + s.phase * 1.7) * s.waveAmp2;
      return s.baseX + wave1 + wave2 + sway;
    };

    // Outer glow
    ctx.globalAlpha = 0.18 * pulse;
    ctx.fillStyle = `rgb(${cr},${cg},${cb})`;
    ctx.beginPath();
    for (let screenY = -step; screenY <= h + step; screenY += step) {
      const worldY = altBottom + (h - screenY);
      const cx = xAt(worldY);
      const left = cx - s.thickness * 2.5;
      if (screenY === -step) ctx.moveTo(left, screenY); else ctx.lineTo(left, screenY);
    }
    for (let screenY = h + step; screenY >= -step; screenY -= step) {
      const worldY = altBottom + (h - screenY);
      const cx = xAt(worldY);
      ctx.lineTo(cx + s.thickness * 2.5, screenY);
    }
    ctx.closePath();
    ctx.fill();

    // Core fill
    ctx.globalAlpha = 0.7 * pulse;
    ctx.beginPath();
    for (let screenY = -step; screenY <= h + step; screenY += step) {
      const worldY = altBottom + (h - screenY);
      const cx = xAt(worldY);
      const left = cx - s.thickness * 0.7;
      if (screenY === -step) ctx.moveTo(left, screenY); else ctx.lineTo(left, screenY);
    }
    for (let screenY = h + step; screenY >= -step; screenY -= step) {
      const worldY = altBottom + (h - screenY);
      const cx = xAt(worldY);
      ctx.lineTo(cx + s.thickness * 0.7, screenY);
    }
    ctx.closePath();
    ctx.fill();

    // Hot center line
    ctx.globalAlpha = Math.min(1, pulse);
    ctx.strokeStyle = `rgb(${Math.min(255, cr + 70)},${Math.min(255, cg + 70)},${Math.min(255, cb + 70)})`;
    ctx.lineWidth = 2;
    ctx.beginPath();
    for (let screenY = -step; screenY <= h + step; screenY += step) {
      const worldY = altBottom + (h - screenY);
      const cx = xAt(worldY);
      if (screenY === -step) ctx.moveTo(cx, screenY); else ctx.lineTo(cx, screenY);
    }
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
}

function tileFlow(ctx, w, h, time, state, direction, wallTime = time) {
  // Cool pale grey base — neutral B2B-friendly
  if (!state._tileGrad || state._tileGrad._h !== h) {
    const g = ctx.createLinearGradient(0, 0, 0, h);
    g.addColorStop(0, '#EEF1F5');
    g.addColorStop(1, '#DDE3EB');
    state._tileGrad = g;
    state._tileGrad._h = h;
  }
  ctx.fillStyle = state._tileGrad;
  ctx.fillRect(0, 0, w, h);

  // Square tile grid scrolling downward. Stroke-only so the gradient
  // shows through. Subtle highlight on each tile's top-left edge for a
  // soft bevel effect.
  const sign = getDirSign(direction) || 1;
  const tileSize = 56;
  // Coefficient 1.0 = match lane divider speed exactly.
  const offset = ((time * 1.0 * sign) % tileSize + tileSize) % tileSize;
  ctx.strokeStyle = 'rgba(120, 140, 170, 0.22)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  // Vertical lines
  for (let x = 0; x <= w + tileSize; x += tileSize) {
    ctx.moveTo(x, 0);
    ctx.lineTo(x, h);
  }
  // Horizontal lines — these are the ones that scroll
  for (let y = -tileSize + offset; y < h + tileSize; y += tileSize) {
    ctx.moveTo(0, y);
    ctx.lineTo(w, y);
  }
  ctx.stroke();

  // Subtle inner highlight on a few tiles — gives the grid life
  // without dominating.
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.45)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (let y = -tileSize + offset; y < h + tileSize; y += tileSize) {
    for (let x = 0; x <= w; x += tileSize) {
      // Only highlight every 3rd tile to keep it subtle
      const cellRow = Math.floor((y + offset) / tileSize);
      const cellCol = Math.floor(x / tileSize);
      if ((cellRow + cellCol) % 3 !== 0) continue;
      ctx.moveTo(x + 1, y + tileSize - 1);
      ctx.lineTo(x + 1, y + 1);
      ctx.lineTo(x + tileSize - 1, y + 1);
    }
  }
  ctx.stroke();
}

// ─── Registry ─────────────────────────────────────────────

const renderers = {
  skyClouds,
  sunsetGlow,
  nightStars,
  neonGrid,
  oceanWaves,
  rollingHills,
  fairway,
  sunsetHorizon,
  mountainRange,
  snowfall,
  lavaFlow,
  forestCanopy,
  coralDepths,
  candySky,
  bambooGrove,
  terracottaDesert,
  pixelRain,
  goldenPalace,
  neonDistrict,
  rooftopSunset,
  downtownNight,
  harbourFront,
  miamiVice,
  cleanCorporate,
  cleanCorporateLight,
  warmInterior,
  softDrift,
  fairwayStrip,
  waterChannel,
  tileFlow,
  skyCloudsTopDown,
  skyJourney,
  magmaRise,
};

/** Foreground overlay renderers — keyed by the SAME rendererKey as the background */
export const foregroundRenderers = {
  skyClouds: skyClouds_fg,
  sunsetGlow: sunsetGlow_fg,
  nightStars: nightStars_fg,
  neonGrid: neonGrid_fg,
  oceanWaves: oceanWaves_fg,
  rollingHills: rollingHills_fg,
  fairway: fairway_fg,
  mountainRange: mountainRange_fg,
  sunsetHorizon: sunsetHorizon_fg,
  snowfall: snowfall_fg,
  lavaFlow: lavaFlow_fg,
  forestCanopy: forestCanopy_fg,
  coralDepths: coralDepths_fg,
  candySky: candySky_fg,
  bambooGrove: bambooGrove_fg,
  terracottaDesert: terracottaDesert_fg,
  pixelRain: pixelRain_fg,
  goldenPalace: goldenPalace_fg,
  neonDistrict: neonDistrict_fg,
  rooftopSunset: rooftopSunset_fg,
  downtownNight: downtownNight_fg,
  harbourFront: harbourFront_fg,
  miamiVice: miamiVice_fg,
  cleanCorporate: cleanCorporate_fg,
  cleanCorporateLight: cleanCorporateLight_fg,
  warmInterior: warmInterior_fg,
};

/** Check if a procedural background has a foreground overlay */
/**
 * Dominant (top) color for each procedural background — used to set the
 * mobile browser chrome (meta theme-color) so it blends seamlessly with the
 * game canvas instead of showing the default page color.
 */
export const proceduralDominantColors = {
  skyClouds:          '#C3E3FF',
  sunsetGlow:         '#020111',
  nightStars:         '#0a0820',
  neonGrid:           '#08080c',
  oceanWaves:         '#003040',
  rollingHills:       '#C3E3FF',
  fairway:            '#7FC7FF',
  sunsetHorizon:      '#1B1033',
  mountainRange:      '#0F1B3D',
  snowfall:           '#3a4a6a',
  lavaFlow:           '#04000a',
  forestCanopy:       '#b8d8c8',
  coralDepths:        '#002030',
  candySky:           '#FFB8D0',
  bambooGrove:        '#1A3018',
  terracottaDesert:   '#2A1810',
  pixelRain:          '#080810',
  goldenPalace:       '#1A1008',
  neonDistrict:       '#06060E',
  rooftopSunset:      '#1A1020',
  downtownNight:      '#060818',
  harbourFront:       '#101820',
  miamiVice:          '#2E0854',
  cleanCorporate:     '#141C28',
  cleanCorporateLight: '#E8EDF4',
  warmInterior:       '#1A1210',
  softDrift:          '#E8EEF5',
  fairwayStrip:       '#A6D77A',
  waterChannel:       '#9FD3DC',
  tileFlow:           '#EEF1F5',
  skyCloudsTopDown:   '#7CB8E8',
  skyJourney:         '#C3E3FF',
  magmaRise:          '#04000a',
};

/** Get the dominant color for a procedural background URL or key */
export function getProceduralDominantColor(urlOrKey) {
  const key = urlOrKey?.startsWith('procedural:') ? getProceduralKey(urlOrKey) : urlOrKey;
  return proceduralDominantColors[key] || null;
}

export function hasForegroundLayer(key) {
  return key in foregroundRenderers;
}

/** Get foreground renderer by key */
export function getForegroundRenderer(key) {
  return foregroundRenderers[key] || null;
}

export default renderers;
